import { AlertTypeEnum } from '@/models/Alert';
import { KlassStatusEnum } from '@/models/Klass';
import { ContentGivenEnum, PresenceEnum } from '@/models/ScheduledLessonReport';
import { UserTypeEnum } from '@/models/User';
import {
  GameErrorCodes,
  GroundToken,
  InteractiveToken,
} from '@cna-br/astro-code';
import TranslateInterface, {
  Sheet,
  TranslateBehaviorAssessmentForm,
  TranslateCommentAssessmentForm,
} from './translateInterface';
import { GenerateDifficultyEnum } from '@/models/astro.ai/generate/Activity';
import { StageTypeEnum } from '@/models/Hackathon';
import { StudentTypeEnum } from '@/enums/StudentTypeEnum';
import {
  BusinessModelEnum,
  CourseFormatEnum,
  CourseDurationMinuteEnum,
} from '@/models/Course';
import { ProgressStatusEnum } from '@/enums/ProgressStatusEnum';
import { CourseLineCategory, CourseLineSubCategory } from '@/models/CourseLine';
import { PromptCategoryEnum } from '@/models/PromptInstruction';
import { KlassPeriodEnum } from '@/utils/klasses/klassPeriods';

const userStatus = {
  REGISTRED: 'Registrado',
  ACTIVE_ENROLLMENT: 'Matrícula Ativa',
  INACTIVE: 'Inativo',
  WAITING_ENROLLMENT: 'Aguardando Matrícula',
};

const klassStatus: Record<KlassStatusEnum, string> = {
  OPEN: 'Aberta',
  IN_PROGRESS: 'Em progresso',
  CONCLUDED: 'Concluída',
  CANCELED: 'Cancelada',
};

const presenceStatus: Record<PresenceEnum, string> = {
  ATTENDED: 'Presente',
  MISSED: 'Falta',
  NONE: 'Sem Registro',
  RESCHEDULED: 'Reagendado',
};

const sheet: Sheet = {
  title: 'Title',
  comment: 'Comentário',
  assessment: 'Avaliação',
  assessments: {
    noInfo: 'Nenhuma informação adicionada sobre',
    loadMore: 'Carregar mais',
    moreDetails: 'Mais detalhes',
  },
  occurrence: 'Ocorrência',
};

const behaviorAssessmentForm: TranslateBehaviorAssessmentForm = {
  successCreatedMessage: 'Avaliação criada com sucesso',
  successUpdatedMessage: 'Avaliação editada com sucesso',
  labels: {
    energy: 'Energia',
    maturity: 'Maturidade',
    speed: 'Velocidade',
    attention: 'Atenção',
    organization: 'Organização',
    initiative: 'Interesse/Iniciativa',
    socialization: 'Socialização',
    apprenticeship: 'Aprendizado',
  },
};

const commentAssessmentForm: TranslateCommentAssessmentForm = {
  successCreatedMessage: 'Comentário criado com sucesso',
  successUpdatedMessage: 'Comentário editado com sucesso',
  commentInput: {
    label: {
      studentLabel: 'Adicionar comentário',
      klassLabel: 'Adicionar observação',
    },
    placeholder: {
      studentCommentAbout:
        'Escreva um comentário sobre o aluno. As informações serão somente para uso interno e não serão visualizadas pelos alunos ou pelos responsáveis',
      klassCommentAbout:
        'Escreva uma observação sobre a turma {{name}}, as informações adicionadas serão para uso interno e não serão visualizadas pelos alunos ou pelos responsáveis',
    },
  },
  klassInput: {
    placeholder: 'Selecione uma turma',
  },
};

const occurrenceAssessmentForm = {
  successCreatedMessage: 'Ocorrência criada com sucesso',
  successUpdatedMessage: 'Ocorrência editada com sucesso',
  descriptionTextArea: {
    label: {
      student: 'Adicionar ocorrência',
    },
    placeholder: {
      student:
        'Escreva uma observação sobre a ocorrência com o aluno. As informações serão somente para uso interno e não serão visualizadas pelos alunos ou pelos responsáveis',
    },
  },
  notificableCheckbox: {
    label: {
      student: 'Avisar os pais (gerar notificação para secretaria)',
    },
    info: {
      student:
        'Se marcada essa opção, a ocorrência aparecerá no quadro de notificações, caso precise entrar em contato com os responsáveis pelo aluno',
    },
  },
};

const courseFormats = {
  [CourseFormatEnum.IN_PERSON]: 'Presencial',
  [CourseFormatEnum.ONLINE]: 'Online',
  [CourseFormatEnum.SCHOOL]: 'Na escola',
  [CourseFormatEnum.EXTRA_COURSE]: 'Curso Extra',
  [CourseFormatEnum.SELF_PACED]: 'Autodidata',
};

const businessModels = {
  [BusinessModelEnum.CNA_STANDARD]: 'Padrão CNA',
  [BusinessModelEnum.IN_SCHOOL]: 'Na Escola',
  [BusinessModelEnum.SELF_PACED]: 'Autodidata',
};

const courseType = {
  CTRL_KIDS: 'Ctrl+Kids',
  CTRL_TEENS: 'Ctrl+Teens',
  CTRL_YOUNG: 'Ctrl+Young',
  CTRL_QUICK: 'Ctrl+Quick',
  CTRL_PRO: 'Ctrl+Pro',
};

const courseLineCategory: Record<CourseLineCategory, string> = {
  [CourseLineCategory.LANGUAGE]: 'Idioma',
  [CourseLineCategory.TECHNOLOGY]: 'Tecnologia',
  [CourseLineCategory.SCHOOL_REINFORCEMENT]: 'Reforço Escolar',
};

const courseLineSubCategory: Record<CourseLineSubCategory, string> = {
  [CourseLineSubCategory.ENGLISH]: 'Inglês',
  [CourseLineSubCategory.SPANISH]: 'Espanhol',
  [CourseLineSubCategory.CTRL_PLAY]: 'Ctrl+Play',
};

const weekday = {
  MONDAY: 'Segunda-feira',
  TUESDAY: 'Terça-feira',
  WEDNESDAY: 'Quarta-feira',
  THURSDAY: 'Quinta-feira',
  FRIDAY: 'Sexta-feira',
  SATURDAY: 'Sábado',
  SUNDAY: 'Domingo',
};

const studentType: {
  [key in StudentTypeEnum]: string;
} = {
  [StudentTypeEnum.REGULAR]: 'Regular',
  [StudentTypeEnum.SCHOOL]: 'Escola',
  [StudentTypeEnum.CONECTA_INTERNAL]: 'Conecta interno',
  [StudentTypeEnum.CONECTA_EXTERNAL]: 'Conecta externo',
};

const pt_BR: TranslateInterface = {
  questionTypes: {
    TEXT: 'Multimídia',
    QUIZ: 'Quiz',
    CODE: 'Código',
    TRUE_OR_FALSE: 'True or False',
    DND_ORDENATION: 'Ordenação',
    DND_POSITIONAL: 'Combinação',
    CODE_EDITOR: 'Editor de código',
    BLOCK: 'Código em Blocos',
    WRITING: 'Escrita',
    FILL_IN_THE_BLANK: 'Fill in the blanks',
    FLASHCARD: 'Flashcard',
  },
  activityBlockTypes: {
    REGULAR: 'Regular',
    MANUAL_GRADE: 'Correção Manual',
    OPTIONAL: 'Opcional',
    CONSOLIDATION: 'Consolidação',
  },
  examTypes: {
    TEST: 'Prova',
    SIMULATED: 'Simulado',
  },
  termsOfUse: {
    title: 'TERMOS DE USO E SERVIÇOS',
    toAccept: 'Eu aceito',
    accepted: 'Termo aceito',
    readMore: 'Ler documento completo',
  },
  astrobot: {
    title: 'AstroBot',
    placeholder:
      'Digite uma pergunta sobre a aula, peça exemplos, ou dicas para solucionar uma atividade...',
    thinking: 'Pensando',
    maximize: 'Expandir',
    minimize: 'Minimizar',
    tokensLimit:
      'Seus créditos acabaram, volte amanhã para poder interagir mais',
    help: 'Está com dúvida? Eu posso ajudar',
    close: 'Fechar',
    send: 'Enviar mensagem',
    firstMessage:
      'Está com dúvidas? Estou aqui para te ajudar a tirar dúvidas sobre robótica e programação!',
    error: {
      message:
        'Desculpe, ocorreu um erro ao processar sua mensagem: \n``` \n{{error}} \n``` \nPor favor, tente novamente ou contate o suporte se o problema persistir.',
    },
    quickPrompts: {
      actions: {
        summarize: 'Resuma esta aula',
        activities: 'Tem atividades?',
        keyPoints: 'Quais os pontos chaves?',
      },
    },
  },
  blockCodeRuntime: {
    blocks_one: 'Bloco',
    blocks_other: 'Blocos',
  },
  errors: {
    serverError:
      'Ocorreu um erro, tente novamente mais tarde ou contate o suporte',
    networkError:
      'Ops, parece que você está offline. Verifique se a sua conexão com a internet está estável.',
    errorPage: {
      backButton: 'Voltar',
      panelButton: 'Painel',
      reloadButton: 'Recarregar',
      activityNotFound: {
        title: 'Ops! Essa atividade não existe.',
        subtitle:
          'A atividade que você está procurando não foi encontrada, confira se a url está correta',
      },
      badRequest: {
        title: 'Ops! Algo não saiu como esperado',
        subtitle:
          'Vamos corrigir o problema, tente acessar novamente mais tarde.',
      },
      internError: {
        title: 'Ops! Algo deu errado.',
        subtitle:
          'Vamos corrigir o problema, tente acessar novamente mais tarde.',
      },
      notFound: {
        title: 'Ops! Essa página não existe.',
        subtitle:
          'A página que você está procurando não foi encontrada, confira se a url está correta.',
      },
      offline: {
        title: 'Ops, parece que você está offline.',
        subtitle: 'Verifique se a sua conexão com a internet está estável.',
      },
      materialNotFound: {
        title: 'Ops! Esse material não existe.',
        subtitle:
          'O material que você está procurando não foi encontrado, confira se a url está correta',
      },
      forbidden: {
        title: 'Ops! Você não tem permissão para acessar essa página.',
        subtitle:
          'Você não tem permissão para acessar essa página, entre em contato com o administrador do sistema.',
      },
      lessonForbidden: {
        title: 'Ops! Você não tem permissão para acessar essa lição.',
        subtitle:
          'Certifique-se que você está matriculado neste curso ou entre em contato com um administrador',
      },
      reactException: {
        title: 'Ops! É raro mas acontece!',
        subtitle:
          'Tente recarregar a página, caso o erro persista, iremos resolver o mais rápido possível!',
      },
    },
    errorPrefix: 'Corrija os seguintes erros: ',
  },
  common: {
    all: 'Todos',
    resultsRange: 'Resultados: {{first}}-{{last}} de {{total}}',
    embedPreview: {
      visitProject: 'Visitar projeto',
    },
    dndPlaceholder: 'Arraste e solte itens para esta lista',
    objectives: 'Objetivos',
    next: 'Próximo',
    forbiddenAction: 'Você não tem permissão para realizar essa ação',
    previous: 'Anterior',
    title: 'Título',
    content: 'Conteúdo',
    acceptPermited:
      'Tipo de arquivo não permitido. Aceitamos apenas {{accept}}',
    fileSizeLimit:
      'O arquivo excede o limite de tamanho. O limite é de {{limit}}',
    maxFilesLimitReached_one: 'Limite de {{count}} arquivo atingido',
    maxFilesLimitReached_other: 'Limite de {{count}} arquivos atingido',
    morning: 'Manhã',
    afternoon: 'Tarde',
    evening: 'Noite',
    periodOfTheDay: 'Periodo do dia',
    course: 'Curso',
    lessons: 'Aulas',
    newest: 'mais recentes',
    oldest: 'menos recentes',
    saved: 'Salvo',
    delete: 'Excluir',
    emptyMessage: 'Sem Resultados',

    clear: 'Limpar',
    difficulty: {
      [GenerateDifficultyEnum.ADVANCED]: 'Avançado',
      [GenerateDifficultyEnum.BEGINNER]: 'Iniciante',
      [GenerateDifficultyEnum.INTERMEDIATE]: 'Intermediário',
    },
    dateRangeInput: {
      selectDate: 'selecione as datas',
      cancelSelect: 'Cancelar seleção',
    },
    complexDateInput: {
      placeholder: 'Selecione',
      close: 'Fechar',
      divider: 'ou selecione no calendário',
      clear: 'Limpar data',
      directions: {
        past: 'Últimos',
        next: 'Próximos',
        this: 'Este/Esta',
      },
      granularities: {
        day: 'Dia(s)',
        week: 'Semana(s)',
        month: 'Mês(es)',
        year: 'Ano(s)',
      },
      granularitiesThis: {
        day: 'Hoje',
        week: 'Esta Semana',
        month: 'Este Mês',
        year: 'Este Ano',
      },
      directionAriaLabel: 'Direção do período',
      amountAriaLabel: 'Quantidade',
      granularityAriaLabel: 'Unidade de tempo',
    },
    timeInput: {
      placeholder: 'Selecionar horário',
      clear: 'Limpar horário',
      hours: 'Horas',
      minutes: 'Minutos',
    },
    scheduledLessonCard: {
      time: '{{time}}h',
      endsAt: 'Término: {{date}}',
      students_one: '{{count}} aluno',
      students_other: '{{count}} alunos',
      attendances_one: '{{count}} chamada',
      attendances_other: '{{count}} chamadas',
      exams_one: '{{count}} prova',
      exams_other: '{{count}} provas',
      requiresAttention: 'Requer atenção',
    },
    copied: 'Copiado',
    copy: 'Copiar',
    collapse: 'Colapsar',
    expand: 'Expandir',
    moreOptions: 'Mais opções',
    preview: 'Pré-visualizar',
    tool: 'Ferramentas',
    userNotFound: 'Usuário não encontrado',
    lessonUnpublished: 'Aula não publicada',
    courses: 'Cursos',
    you: 'Você',
    add: 'Adicionar',
    madeBy: '© Feito pela',
    withLove: 'com',
    inBrazil: 'no 🇧🇷',
    userType: {
      [UserTypeEnum.PARENT]: 'Pai',
      [UserTypeEnum.SUPER_ADMIN]: 'Super Administrador',
      [UserTypeEnum.UNIT_ADMIN]: 'Administrador',
      [UserTypeEnum.TEACHER]: 'Professor',
      [UserTypeEnum.STUDENT]: 'Aluno',
      [UserTypeEnum.CONTENT_CREATOR]: 'Conteudista',
    },
    studentAge: {
      sixOrLess: '6 ou menos',
      sevenToNine: '7 a 9',
      tenToThirteen: '10 a 13',
      fourteenToSeveteen: '14 a 17',
    },
    scheduledLessonReportStatus: {
      ACTIVE: 'Relatório ativo',
      CANCELED: 'Relatório cancelado',
      TRANSFERED: 'Relatório transferido',
      NOT_ENROLLED: 'Não ativo nesta aula',
    },
    filter: {
      admin: 'Administrador',
      teacher: 'Professor',
      clearButton: 'Limpar',
      okButton: 'OK',
    },
    months: {
      january: 'Janeiro',
      february: 'Fevereiro',
      march: 'Março',
      april: 'Abril',
      may: 'Maio',
      june: 'Junho',
      july: 'Julho',
      august: 'Agosto',
      september: 'Setembro',
      october: 'Outubro',
      november: 'Novembro',
      december: 'Dezembro',
    },
    day: 'Dia',
    confirm: 'Confirmar',
    cancel: 'Cancelar',
    save: 'Salvar',

    never: 'Nunca',
    generalInformation: 'Informações gerais',
    alterPassword: 'Alterar senha',
    rewards: 'Recompensas',
    lastLogin: 'Último acesso',
    goodMorning: 'Bom dia',
    goodAfternoon: 'Boa tarde',
    goodEvening: 'Boa noite',
    teacherPrefix: 'Prof.',
    form: {
      required: 'O campo {{field}} é requerido',
      empty: 'O campo {{field}} não pode ser vazio',
      invalid: 'O campo {{field}} é inválido',
      startDate: {
        title: 'Data de início',
      },
      endDate: {
        title: 'Data de fim',
      },
      reason: {
        title: 'Motivo',
        placeholder: 'Digite um motivo para as férias',
      },
      recess: {
        title: 'Datas de recesso',
        validation: 'Não é possível remover todas as datas',
      },
      title: {
        title: 'Título',
      },
      subtitle: {
        title: 'Subtítulo',
      },
      language: {
        title: 'Linguagem',
      },
      content: {
        title: 'Conteúdo',
      },
      newPassword: { title: 'Nova senha' },
      reNewPassword: { title: 'Confirme a nova senha' },
    },

    completed: 'Concluído',
    lessonTitle: 'Título da aula',
    edit: 'Editar',
    notEditable: 'Não editável',

    input: {
      unit: {
        label: 'Unidade',
        placeholder: 'Selecione uma unidade',
      },
      teacher: {
        label: 'Professor',
        placeholder: 'Selecione um professor',
      },
    },
    students: 'Alunos',
    info: 'Informações',
    sheet: 'Ficha',
    noResults: 'Nenhum resultado encontrado',
    students_one: '{{count}} aluno',
    students_other: '{{count}} alunos',
    increasingTime: 'Horário crescente',
    descendingTime: 'Horário decrescente',

    accessibility: {
      yourClassShip: 'A nave da sua classe',
      weekdayKlassCell: 'Célula da classe',
      klassPopup: 'Popup da classe',
      todayLessons: 'Aulas de hoje',
      notificationsPage: 'Página de notificações',
      vacationNotice: 'Aviso de férias',
    },

    onVacation: 'Em férias',
    dates: 'Datas',
    notifications: 'Notificações',
    toAdd: 'Adicionar',
    warnings: 'Avisos',
    noInfo: 'Nenhuma informação adicionada sobre {{fullName}}',
    loadMore: 'Carregar mais',
    remove: 'Remover',
    noDates: 'Sem datas',
    optional: 'Opcional',

    lessonTabs: {
      details: 'Informações',
      material: 'Material do Aluno',
      teacherMaterial: 'Material do Professor',
      activity: 'Atividade',
      challenge: 'Desafio',
      resource: 'Recursos',
    },

    newResource: 'Novo recurso',
    fileInstructions: 'Arraste ou clique pra adicionar um arquivo',
    finish: 'Finalizar',
    unknown: 'Desconhecido',
    file: 'Arquivo',
    revert: 'Reverter',
    deleted: 'Deletado',
    activity: 'Atividade',
    link: 'Link',
    open: 'Abrir',
    download: 'Baixar',
    activities: 'Atividades',
    start: 'Iniciar',
    template: 'Gabarito',
    grade: 'Nota',
    notFoundFile: 'Nenhum arquivo fornecido.',
    resources: 'Recursos',
    seeAnswers: 'Ver respostas',
    see: 'Ver',
    mandatory: 'Obrigatório',
    unit: 'Unidade',
    teacher: 'Professor',
    user: 'Usuário',
    courseType: 'Tipo do curso',
    klass: 'Turma',
    role: 'Cargo',

    select: 'Selecione',
    otherFilters: 'Outros filtros',
    loading: 'Carregando',
    modality: 'Modalidade',
    startSoon: 'Início próximo',
    endSoon: 'Término próximo',
    hasActiveLessons: 'Possui aulas ativas',
    klassType: 'Tipo de turma',
    first: 'Início',
    termination: 'Término',
    status: 'Status',
    actions: 'Ações',
    ascending: 'Crescente',
    descending: 'Decrescente',
    research: 'Pesquisar',
    doNotAnticipateLesson: 'Não é possível essa aula',
    classStartsSoon: 'Aula começa em breve',
    lesson: 'Aula {{order}}',
    refresh: 'Atualizar',
    hideSection: 'Ocultar seção',
    today: 'Hoje',
    pending: 'Pendentes',
    dayPeriod: {
      am: 'AM',
      pm: 'PM',
    },
    occurrenceDescription: '{{name}}: "{{description}}"',
    name: 'Nome',
    email: 'E-mail',
    enrollmentType: 'Tipo de matrícula',
    newInstructions: 'Novas instruções',
    instructions: 'Instruções',
    allSectionsAdd: 'Todas as seções já foram adicionadas',
    noInstructions: 'Sem instruções',
    homework: 'Tarefa de casa',
    matrix: 'Matriz',
    availableSoon: 'Estará disponível em breve',
  },
  formValidation: {
    user: {
      username: {
        pattern: 'Apenas letras, números e . - _ são aceitos para usuário',
        minLength: 'Usuário deve ter no mínimo 5 caracteres',
        usernameExists: 'Esse nome de usuário já existe',
        startsLetters: 'Usuário só pode começar com letras',
        required: 'Usuário é obrigatório',
      },
    },
    general: {
      url: {
        pattern: 'Insira uma url válida',
      },
    },
  },
  imageInput: {
    chooseImage: 'Escolha uma imagem',
    delete: 'Excluir',
  },
  imageZoom: {
    expand: 'Ampliar imagem',
    close: 'Fechar imagem ampliada',
  },
  login: {
    sendEmail: {
      forgotPassword: 'Esqueci minha senha',
      weRecoverPassword: 'Vamos recuperar a sua senha',
      back: 'Voltar',
      emailLabel: 'E-mail',
      emailSent: 'E-mail enviado com sucesso',
      emailPlaceholder: 'Digite seu e-mail',
      usernamePlaceholder: 'Digite o username',
      submit: 'Enviar',
      checkEmail:
        'Cheque seu e-mail com as instruções para finalizar a redefinição de sua senha',
      specifyUser: 'Especifique usuário para alterar a senha',
      userTypeRequired: 'Tipo de usuário não informado',
      usernameRequired: 'Username é obrigatório',
    },
    resetPassword: {
      alreadyRegister: 'Já possui conta?',
      confirmNewPassword: 'Confirme a nova senha',
      confirmRedefine: 'Redefinir senha',
      login: 'Faça login aqui',
      newPassword: 'Nova senha',
      redefinePassword: 'Redefina sua senha',
      typeNewPassword: 'Digite sua nova senha',
      differentPasswordError: 'As senhas inseridas não coincidem',
      modal: {
        canLogin: 'Você já pode fazer o seu login utilizando a senha nova',
        success: 'Senha redefinida com sucesso!',
      },
    },
    motivationalMessage: 'Vamos começar a aprender? 🚀',
    loginAccount: 'Entre com a sua conta',
    login: 'Login',
    loginPlaceholder: 'Digite seu e-mail ou usuário',
    password: 'Senha',
    passwordPlaceholder: 'Digite sua senha',
    keepConnected: 'Mantenha-me conectado',
    signIn: 'Entrar',
    forgotPassword: 'Esqueceu sua Senha?',
  },

  newAccount: {
    questionText: 'Novo por aqui?',
    linkText: 'Crie sua conta grátis',
  },

  tableHeader: {
    search: 'Pesquisar',
    filter: 'Filtrar',
    increasing: 'Crescente',
    decreasing: 'Decrescente',
  },

  modalAddStudent: {
    addStudentPhrase: 'Quais alunos gostaria de adicionar?',
    confirm: 'Confirmar',
    cancel: 'Cancelar',
  },

  confirmModal: {
    defaultTitle: 'Tem certeza?',
    confirmDestructive: 'Excluir',
    confirmDefault: 'Confirmar',
    cancel: 'Cancelar',
  },

  modalChangePassword: {
    passwordChangeMessage:
      'Tem certeza que deseja gerar uma nova senha para este aluno?',
    confirm: 'Confirmar',
    cancel: 'Cancelar',

    warning: 'Atenção!',
    lostChangesWarning:
      'Você possui alterações não salvas que serão perdidas se sair da página',
    confirmLeave: 'Tem certeza de que deseja sair?',
    leave: 'Sair',
    stay: 'Permanecer',

    passwordChangeSuccess: 'Nova senha gerada com sucesso!',
    emailSent: 'A nova senha foi enviada ao e-mail do aluno e do responsável',
    close: 'Fechar',
  },
  modalGenerateMatrix: {
    title: 'Instruções',
    subtitle:
      'Abaixo, você pode passar mais instruções para a geração da matriz',
    generate: 'Gerar matriz',
    hasValue: 'A matriz atual será substituída',
    form: {
      materialType: {
        label: 'Formato do Material:',
        options: {
          APRESENTATION: 'Slides',
          TEXT: 'Texto',
        },
      },
      instructions: {
        label: 'Instruções',
        placeholder: 'Digite informações relevantes sobre a matriz do curso',
      },
    },
  },
  modalGenerateObjective: {
    title: 'Gerar Objetivos',
    subtitle:
      'Defina o tópico e propósito para gerar objetivos de aprendizagem personalizados',
    generate: 'Gerar Objetivos',
    hasValue: 'Os objetivos atuais serão substituídos',
    form: {
      topic: {
        label: 'Tópico',
        placeholder: 'Em uma linha, sobre o que se trata esse curso?',
      },
      purpose: {
        label: 'Propósito',
        placeholder: 'O que gostaria que fosse ensinado neste curso?',
      },
    },
  },
  modalGenerateQuestion: {
    title: 'Selecione um tipo de questão e os parâmetros',
    generate: 'Gerar',
    form: {
      numberOfAlternatives: 'Quantidade de alternativas',
      difficulty: { label: 'Dificuldade', placeholder: 'Selecione' },
      topic: { label: 'Tópico', placeholder: 'Digite um tópico' },
    },
  },
  modalWarning: {
    modalUpdateWinner: {
      attention: 'Atenção!',
      warning: 'O projeto abaixo já está nesta classificação',
      proceed: 'Deseja substituir pelo projeto atual?',
      confirm: 'Substituir',
      cancel: 'Cancelar',
    },
    modalUpdateCourse: {
      attention: 'Atenção!',
      warning:
        'Ao atualizar a versão do curso, a turma passará a ter 1h30min de duração. Se a duração das aulas era maior antes, favor levar em consideração a duração nos contratos assinados e que os responsáveis estejam cientes. Todos os alunos serão afetados e seus progressos serão resetados.',
      proceed: 'Deseja atualizar para a nova versão do curso?',
      confirm: 'Atualizar',
      cancel: 'Cancelar',
    },
    modalAnswerKey: {
      attention: 'Atenção!',
      warning: 'Se você abrir o gabarito, não poderá refazer mais a atividade',
      proceed: 'Deseja ver o gabarito agora?',
      confirm: 'Gabarito',
      cancel: 'Cancelar',
    },
    gerateChargesModal: {
      attention: 'Atenção!',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      proceed: 'Tem certeza que deseja continuar?',
      warning:
        'Ao confirmar que o aluno esteve presente na aula <strong>será gerada uma cobrança</strong> para a unidade de origem',
    },
    modalLeaveActivityPage: {
      attention: 'Atenção!',
      warning: 'Se você sair da atividade o seu progresso não será salvo',
      proceed: 'Tem certeza que deseja sair?',
      confirm: 'Sair',
      cancel: 'Ficar',
    },

    modalSaveChanges: {
      attention: 'Tem certeza que deseja alterar?',
      warning: 'Essa ação irá gravar as informações',
      proceed: '',
      confirm: 'Confirmar',
      cancel: 'Cancelar',
    },

    modalDiscardChanges: {
      attention: 'Tem certeza que deseja cancelar as alterações?',
      warning: 'Esta ação não irá salvar as informações',
      proceed: '',
      confirm: 'Confirmar',
      cancel: 'Cancelar',
    },

    modalSaveHomework: {
      attention: 'Atenção!',
      warning: 'A tarefa será publicada para todos os alunos',
      proceed: 'Tem certeza que deseja publicá-la?',
      confirm: 'Publicar',
      cancel: 'Cancelar',
    },

    modalDeleteChapter: {
      attention: 'Atenção!',
      warning: 'Tem certeza que deseja deletar esse capítulo?',
      proceed: 'Essa ação não poderá ser desfeita',
      confirm: 'Confirmar',
      cancel: 'Cancelar',
    },

    lessonReportModal: {
      attention: 'Tem certeza que deseja marcar a aula como concluída?',
      warning: '',
      proceed: 'Você não poderá desfazer esta ação',
      confirm: 'Confirmar',
      cancel: 'Cancelar',
    },

    modalNoComment: {
      attention: 'Nenhum comentário foi adicionado para o aluno',
      warning: 'Tem certeza que deseja prosseguir?',
      proceed: '',
      confirm: 'Confirmar',
      cancel: 'Cancelar',
    },

    modalChangeKlass: {
      attention: 'As mudanças feitas para essa turmas não foram salvas',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      warning:
        'Selecionar uma nova turma ou sair da página fará com que você perca suas alterações',
      proceed: 'Tem certeza que deseja prosseguir?',
    },

    modalRemoveVacationNotice: {
      attention: 'Tem certeza que deseja remover esse aviso de férias?',
      warning: 'Você não poderá desfazer esta ação',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      proceed: '',
    },

    modalPublishLesson: {
      attention: 'Atenção!',
      warning:
        'Ao publicar esta aula para uso geral, ela estará imediatamente disponível para uso em cursos e aulas extras',
      proceed: 'Esta ação não poderá ser revertida. Deseja continuar?',
      confirm: 'Publicar',
      cancel: 'Cancelar',
    },
  },

  modalDisable: {
    disable: 'Desativar',
    cancel: 'Cancelar',
    delete: 'Excluir',
    confirm: 'Confirmar',
    modalDisableActivityElement: {
      warning: 'Tem certeza que deseja desativar esta questão?',
      lostChangesWarning:
        'Esta ação não excluirá as informações da questão para os alunos',
    },

    modalDeleteCourse: {
      warning: 'Tem certeza que deseja descartar este curso?',
      lostChangesWarning:
        'Descartar o curso é uma ação que não pode ser desfeitas. Todas as alterações salvas e não salvas serão perdidas.',
    },

    modalDisableHackaton: {
      warning: 'Tem certeza que deseja descartar este hackathon?',
      lostChangesWarning:
        'Descartar o hackathon é uma ação que não pode ser desfeitas.',
    },
    modalDisableHackatonStage: {
      warning: 'Tem certeza que deseja descartar este periodo do hackathon?',
      lostChangesWarning:
        'Descartar o periodo do hackathon é uma ação que não pode ser desfeitas.',
    },

    modalDisableChallenge: {
      warning: 'Tem certeza que deseja desativar este desafio?',
      lostChangesWarning:
        'Esta ação não excluirá o progresso ou informações dos alunos',
    },

    modalDisableUnit: {
      warning: 'Tem certeza que deseja desativar esta unidade?',
      lostChangesWarning: 'Esta ação irá preservar as informações',
    },

    modalDisableKlass: {
      warning: 'Tem certeza que deseja desativar esta turma?',
      lostChangesWarning:
        'Esta ação não fará com que os alunos percam seu progresso',
    },

    modalDisableLesson: {
      warning: 'Tem certeza que deseja excluir esta aula?',
      lostChangesWarning:
        'Esta ação não excluirá o progresso ou informações dos alunos',
    },
    modalDeleteLesson: {
      warning: 'Tem certeza que deseja excluir esta aula?',
      lostChangesWarning:
        'Esta ação excluirá todas as alterações feitas nesta lição',
    },

    modalDisableTeacher: {
      warning: 'Tem certeza que deseja desativar este professor?',
      lostChangesWarning: 'Esta ação irá preservar as informações',
    },

    modalDisableActivity: {
      warning: 'Tem certeza que deseja excluir esta atividade?',
      lostChangesWarning:
        'Esta ação não excluirá o progresso ou informações dos alunos',
    },

    modalRemoveBook: {
      warning: 'Tem certeza que deseja excluir o material?',
      lostChangesWarning:
        'Esta ação não poderá ser desfeita e todas as informações serão perdidas',
    },
    modalDisableTopic: {
      warning: 'Tem certeza que deseja excluir este tópico?',
      lostChangesWarning: 'Esta ação pode afetar o progresso dos alunos',
    },
    modalDisableDifficulty: {
      warning: 'Tem certeza que deseja excluir esta dificuldade?',
      lostChangesWarning: 'Esta ação pode afetar o progresso dos alunos',
    },

    modalStudentAbsence: {
      warning: 'Tem certeza que deseja remover a presença desse aluno?',
      lostChangesWarning:
        'Essa ação também removerá todo o relatório da aula, incluindo notas e comentários, caso já tenham sido preenchidos.',
    },

    modalDisableScheduledLesson: {
      warning: 'Tem certeza que deseja desativar esta aula?',
      lostChangesWarning: 'Essa ação afeterá o calendário de aulas',
    },
    orderLessons: {
      lostChangesWarning:
        'Os alunos já receberão esta atualização e poderão ver que a ordem foi alterada.',
      warning: 'Tem certeza que deseja fazer esta alteração?',
    },
    deleteProject: {
      warning: 'Você tem certeza que deseja deletar o projeto?',
      lostChangesWarning: 'Essa ação não pode ser desfeita',
    },
    deleteResource: {
      warning: 'Você tem certeza que deseja deletar esse recurso?',
      lostChangesWarning: 'Essa ação não pode ser desfeita',
    },
    deleteResourceActivity: {
      warning: 'Tem certeza que deseja excluir esta atividade?',
      lostChangesWarning:
        'Esta ação não excluirá o progresso ou informações dos alunos',
    },

    deleteSectionInstructions: {
      warning: 'Tem certeza que deseja excluir essa instrução?',
      lostChangesWarning: 'Essa ação não pode ser desfeita',
    },

    deleteChapter: {
      warning: 'Tem certeza que deseja excluir este capítulo?',
      lostChangesWarning: 'Essa ação não pode ser desfeita',
    },

    deleteLessonBlock: {
      warning: 'Tem certeza que deseja excluir este bloco de aulas?',
      lostChangesWarning:
        'Existem aulas nesse bloco que serão perdidas e não poderão ser recuperadas após o salvamento.',
    },
  },

  avatar: {
    profile: 'Perfil',
  },

  countdown: {
    materialButton: 'Material',
    classroomButton: 'Sala de Aula',
    nextClass: 'PRÓXIMA AULA',
    days: 'dias',
    hours: 'horas',
    minutes: 'minutos',
    seconds: 'segundos',
    accessLesson: 'Acessar Lição',
    watchVideo: 'Ver Vídeo',
    instructionUrlText:
      'Essa aula tem um video de como instalar e/ou usar o programa. Assista antes da sua aula no botão abaixo:',
    materialText:
      'Antes da sua aula começar você já pode acessar o material e ler sobre o assunto no botão abaixo:',
    lessonText:
      'Antes da sua aula começar você já pode ver o conteúdo dela clicando no botão abaixo:',
    accessClassroom: 'Sala de Aula',
    classroomTooltip:
      'A Sala de Aula fica disponível 30 minutos antes da aula começar',
  },

  sideMenu: {
    home: 'Início',
    classroom: 'Minhas Aulas',
    feedback: 'Avaliações',
    courses: 'Cursos',
    extraCourses: 'Cursos Extras',
    achievements: 'Conquistas',
    notifications: 'Notificações',
    expand: 'Expandir',
    collapse: 'Minimizar',
    howWorks: 'Como Funciona',
    admin: 'Admin',
    calendar: 'Calendário',
    config: 'Configurações',
    leave: 'Sair',
    teacher: 'Professor',
    projects: 'Projetos',
  },
  courses: {
    status: {
      EDITING: 'Editando',
      VERSIONING: 'Versionando',
      BETA: 'Beta',
      PUBLISHED: 'Publicado',
      NEW: 'Rascunho',
      DISABLED: 'Desativado',
    },
    legalInformation: 'Informações legais',
  },
  coursePathForm: {
    codeWillOverride:
      'Você está no modo de edição limitada. As alterações feitas irão sobrescrever a versão anterior assim que esta versão for publicada',
    isbnCode: 'Insira o código ISBN ou outras informações legais',
    saveSuccessfuly: 'Alterações salvas com sucesso',
  },
  klassPage: {
    nextKlass: 'Próxima aula',
    myLessons: 'Minhas Aulas',
    myLessonsMessage:
      'Aqui você encontra todas as aulas disponíveis no seu módulo',
    myLessonsCompleted: 'Não há aulas a se fazer!',
    errorMessage: {
      lessonListPhrase: 'Não foi possível carregar a lista de aulas',
      nextLessonPhrase: 'Não foi possível carregar a próxima aula',
    },
  },
  lesson: {
    availableSoon: 'Esta lição estará disponível em breve!',
    availableExplain:
      'Esta lição ainda não está disponível. Você poderá acessá-la uma semana antes da data ou se seu professor alterar a ordem das lições',
    backLessons: 'Ver lições',
    teacher: {
      activity: {
        answerError: 'Ocorreu um erro ao salvar a resposta',
        errorTextSubTitle: 'Houve um erro ao abrir a atividade',
        errorTextTitle: 'Atividade não encontrada',
        klass: {
          errorTextTitle: 'Turma não encontrada',
          errorTextSubTitle: 'Houve um erro ao buscar a turma',
          submittions: 'Envios',
          noStudents: 'Essa turma não tem alunos',
        },
      },
    },
    manage: {
      selectKlass: 'Selecione uma turma',
      bankLesson: {
        title: 'Banco de Aulas',
        details: 'Adicionar uma aula já criada do banco de aulas',
      },
      addLesson: {
        cantAdd:
          'Não é possível adicionar aula entre duas aulas que já passaram',
        title: 'Adicionar Aula',
        details: 'Adicionar uma aula livre ou regular no curso.',
      },
      editLesson: {
        title: 'Editar Aulas',
        details: 'Alterar o conteúdo ou ordem das aulas do curso.',
      },
      invalidLessonsMessage:
        'Existem lições inválidas nesta turma. Por favor, notifique o suporte.',
    },
    access: {
      accessVideo: 'Acessar vídeo',
      accessProgram: 'Acessar programa',
      accessAndInstallation: 'Acesso e Instalação',
      hasVideoInstallationText:
        'Essa aula tem um video de como instalar e/ou usar o programa.',
      clickToWatchVideoInstalationText:
        'Clique nos botões abaixo para assistir o vídeo de como usar o programa e para acessá-lo.',
    },
    attention: 'Atenção!',
    closeActivity: {
      buttonCancel: 'Ficar',
      buttonConfirm: 'Sair',
      warning: 'Se você sair da atividade seu progresso não será salvo',
      confirmMessage: 'Tem certeza de que deseja sair?',
    },
    activityProgress: 'Atividade',
    controlButton: {
      bookNext: 'CONTINUAR',
      bookPrev: 'VOLTAR',
      activityNext: 'PRÓXIMO',
      activityPrev: 'ANTERIOR',
      finish: 'FINALIZAR',
      unexpectedError:
        'Ocorreu um erro inesperado. Tente recarregar a página ou contate o time de suporte.',
    },
    activity: {
      false: 'F',
      true: 'V',
      titleHeader: 'Atividades',
      feedback: 'Gabarito',
      score: 'Nota',
      notDone: 'Não fez',
      redo: 'Refazer',
      start: 'Iniciar',
      view: 'Visualizar',
      card: 'Atividade',
      homework: 'Tarefa de casa',
      givenByTeacher: 'dado pelo professor',
      noQuestions: 'Nenhuma questão nesta atividade',
      noQuestionsSubtitle:
        'Esta atividade não tem questões, adicione questões ou remova a atividade',
      activityNext: 'PRÓXIMO',
      activityPrev: 'ANTERIOR',
      notFoundTitle: 'Atividade não encontrada',
      notFoundSubtitle: 'Um erro ocorreu ao buscar a atividade',
      result: {
        finished: 'Finalizado',
        resultText: 'Resultado',
        congratulationsText: 'Parabéns, você finalizou a atividade! 🎉',
        questions: 'Questões',
        correct: 'Certas',
        percentage: 'Percentual',
        review: 'Revisar',
        detailReview: 'Revisar as perguntas e suas respostas',
        remake: 'Refazer',
        detailRemake: 'Tentar novamente a atividade',
        leave: 'Sair',
      },
      text: {
        instruction: 'Escreva sua resposta no campo abaixo',
        correction: 'Correção do Professor',
        grade: 'Nota',
      },
      task: {
        instruction: 'Faça upload do arquivo no campo abaixo',
        upload: 'Clique aqui e selecione um arquivo para enviar',
      },
      quiz: {
        statement: 'Escolha a opção correta',

        correctAnswer: 'Resposta correta',
      },
      trueOrFalse: {
        correctAnswer: 'Resposta correta',
        question: 'Verdadeiro ou Falso?',
      },
      dragAndDropOrdenation: {
        instruction: 'Organize os itens na ordem correta',
        errorReordenation: 'Erro ao tentar reordenada os items',
        wrongAnswer: 'Ordem correta',
      },

      dragAndDropPositional: {
        instruction:
          'Organize os itens à direita para corresponder à ordem correta à esquerda',
        errorReordenation: 'Erro ao tentar reordenar os items',
      },
      video: {},
    },
  },

  feedbackPage: {
    pageTitle: 'Avaliação das Aulas',
    all: 'geral',
    byCourse: 'por curso',
    byModule: 'por módulo',
    byKlass: 'por turma',
    byLesson: 'por aula',
    context: {
      students: 'Alunos',
      teachers: 'Professores',
    },
    header: {
      average: 'Média',
      lessonRating: 'Conteúdo da aula',
      teacherRating: 'Explicação do professor',
      notFound: 'Nenhuma avaliação encontrada',
      closeFeedback: 'Fechar avaliações',
      loadFeedback: 'Carregar avaliações',
      oldest: 'mais antigas',
      all: 'Todos',
      newest: 'mais recentes',
      positive: 'Positivo 😍',
      neutral: 'Neutro 😐',
      negative: 'Negativo 😖',
    },
    filter: {
      courseLine: 'Linha do Curso',
      module: 'Módulo',
      selectCourse: 'Selecione um curso primeiro',
      units: 'Unidades',
      rating: 'Avaliação',
      teacher: 'Professor',
      klass: 'Turma',
    },
    tag: {
      feedbackSingular: 'avaliação',
      feedbackPlural: 'avaliações',
    },
    noLessonsFound: 'Nenhuma aula encontrada',
  },

  accountPage: {
    soundSettings: 'Configurações de som',
    alertSound: 'Som das notificações',
    accountTitle: 'Minhas configurações',
    accountSubheading: 'Aqui você pode alterar as informações da sua conta ',
    accountInfoTitle: 'Dados da Conta',
    accountPasswordTitle: 'Senha da Conta',
    personalInfo: 'Informações Pessoais',
    accountInfo: 'Informações da Conta',
    accountSystemTitle: 'Definições do Sistema',
    theme: 'Tema',
    systemInfo: 'Configurações do Sistema',
    personalInfoTitle: 'Dados Pessoais',
    alterTimezone: 'Fuso-horário',
    fieldName: 'Nome',
    fieldLastname: 'Sobrenome',
    fieldEmail: 'E-mail',
    fieldUsername: 'Username',
    fieldPhoneNumber: 'Telefone',
    fieldDateOfBirth: 'Data de nascimento',
    fieldAddress: 'Cidade, Estado',
    fieldState: 'Estado',
    fieldCity: 'Cidade',
    selectCity: 'Digite a cidade',
    currentPassword: 'Senha Atual',
    saveErrorDifferentPassword: 'As senhas inseridas não coincidem',
    fieldNewPassword: 'Nova senha',
    fieldConfirmPassword: 'Confirme a nova senha',
    saveButton: 'Salvar',
    editButton: 'Editar',
    cancelButton: 'Cancelar',
    saveSuccess: 'Salvo com sucesso',
    saveError:
      'Ocorreu um erro, certifique-se de que a senha atual está correta',
    visualSettings: 'Configurações Visuais',
    changeConsent: 'Alterar informações de consentimento',
  },
  game: {
    edit: {
      title: 'Editar mapa',
      resize: { rows: 'Linhas', cols: 'Colunas' },
      allEnemiesShouldMove: 'Todos os inimigos devem se mover',
    },
    layers: {
      ground: 'Camada de chão',
      interactive: 'Camada interativa',
    },
    selectBlocks: {
      ground: {
        title: 'Blocos',
        description:
          'Clique em um estilo de bloco e depois clique no mapa onde deseja inseri-lo',
      },
      interactive: {
        title: 'Itens',
        description:
          'Itens que interagem com o aluno, clique para adicionar ou remover do mapa',
      },
    },
    validators: {
      [GameErrorCodes.DUPLICATE_SPRITE]:
        'Não é possível adicionar dois items com mesmo sprite',
      [GameErrorCodes.EXISTS_INTERACTIVE_ITEM_IN_COORD]:
        'Já existe um item interativo nessa coordenada',
      [GameErrorCodes.INVALID_COLLECTIBLE_POSITION]:
        'O item coletável não pode estar em um bloco de lava',
      [GameErrorCodes.INVALID_ENEMY_POSITION]:
        'O inimigo não pode estar em um bloco de limite',
      [GameErrorCodes.INVALID_MIN_MATRIX_DIMENSIONS]:
        'A matriz deve ter no mínimo 3x3',
      [GameErrorCodes.INVALID_MAX_MATRIX_DIMENSIONS]:
        'A matriz deve ter no máximo 13x13',
      [GameErrorCodes.INVALID_GROUND_POSITION]:
        'Posição inválida para o bloco de chão, o personagem está nessa posição',
      [GameErrorCodes.GROUND_SHOULD_CONTAIN_ALL_INTERACTIVE_ITEMS]:
        'O chão deve conter todos os itens interativos',
      [GameErrorCodes.PLAYER_SIDE_ENEMY]:
        'Um inimigo que se movimenta não pode estar ao lado do personagem',
      [GameErrorCodes.INVALID_INITIAL_POSITION_PLAYER]:
        'O personagem só pode iniciar em bloco de grama',
      [GameErrorCodes.REGULAR_MATRIX]:
        'A matriz deve ser regular, todas as linhas devem ter o mesmo tamanho',
      [GameErrorCodes.WORKSPACE_UNDEFINED]:
        'O espaço de trabalho não foi definido',
    },
    tokens: {
      names: {
        [GroundToken.FINAL]: 'Pódio',
        [GroundToken.REGULAR]: 'Grama',
        [GroundToken.DANGER]: 'Lava',
        [GroundToken.LIMIT]: 'Limites',
        [InteractiveToken.PLAYER]: 'Personagem',
        [InteractiveToken.COLLECTIBLE]: 'Coletável',
        [InteractiveToken.ENEMY]: 'Inimigo',
        [InteractiveToken.DIRECTION]: 'Indicador de direção do player',
      },
      describes: {
        [GroundToken.DANGER]:
          'Caminho proíbido (o aluno não pode passar por aqui)',
        [GroundToken.REGULAR]: 'Caminho livre (o aluno pode passar por aqui)',
        [GroundToken.FINAL]: 'Ponto de chegada (o aluno deve chegar até aqui)',
        [GroundToken.LIMIT]: 'Bloco de chão que indica o limite do mapa',
        [InteractiveToken.PLAYER]: 'Defina onde o personagem deverá começar',
        [InteractiveToken.COLLECTIBLE]:
          'Adicione ou remova no mapa um item que o aluno deverá coletar',
        [InteractiveToken.ENEMY]:
          'Adicione ou remova um inimigo que o aluno deverá evitar',
        [InteractiveToken.DIRECTION]: 'Indicador de direção do player',
      },
    },
  },
  klass: {
    selectKlass: 'Selecione uma turma',
    lessons: {
      inBank: 'Banco de Aulas',
      readOnly: 'Não editável',
      success: 'Alterações salvas com sucesso',
      cantOrder: 'Não é possível ordenar essa aula',
      errorTextTitle: 'Ocorreu um erro ao carregar as aulas',
      errorTextSubTitle: 'Entre em contato com o suporte',
      finish: 'Finalizar',
      cancel: 'Cancelar',
      view: 'Visualizar',
      title: 'Editar Curso da Turma',
      details: 'Informações',
      activity: 'Atividade',
      studentBook: 'Material do Aluno',
      teacherBook: 'Material do Professor',
      challenge: 'Desafio',
      modal: {
        cancel: {
          success: 'Mudanças descartadas com sucesso',
        },
      },
      lessonDetail: {
        errorTool: 'Link inválido',
        deleteSuccess: 'Aula deletada com sucesso',
        ImageUploadSuccess: 'A imagem foi carregada com sucesso:',
        ImageUploadError_1: 'Atenção: o upload da imagem',
        ImageUploadError_2: ', falhou! Tente novamente!',
        normal: 'Normal',
        free: 'Livre',
        lessonTitle: 'Título da Aula',
        toolUrl: 'Adicione um link de acesso ao programa',
        instructionsUrl: 'Adicione um link de video sobre como usar o programa',
        addLink: 'Adicionar Link',
        save: 'Salvar',
        exclude: 'Excluir Aula',
        freeExplain:
          'Quando o conteúdo da aula é fora do currículo, aumentando a quantidade de aulas total do curso. Recomendado para eventos ou feriados.',
        normalExplain:
          'Quando precisa substituir uma ferramenta ou passar mais tempo para terminar um projeto. Você pode desativar outra aula para manter o total de 18 aulas.',
      },
      alert: {
        saved: 'Salvo',
      },
    },
  },
  detachedLessonEditPage: {
    publishForSelectedUnits: 'Publicar para unidades selecionadas',
    publishForGeneralUse: 'Publicar para uso geral',
  },
  modalBank: {
    manage: 'Gerenciar',
    disabledButton: 'Indisponível',
    inUseLesson: 'aula já presente no curso',
    inUseActivity: 'presente no curso',
    private: 'Privado',
    public: 'Público',
    edit: 'Editar',
    openModal: {
      activities: 'Banco de Atividades',
      questions: 'Banco de Questões',
      lessons: 'Banco de Aulas',
      all: 'Banco de Conteúdo',
    },
    tooltip: {
      outsideBank: 'Fora do banco',
      insideBank: 'Dentro do banco',
    },
    expansibleContent: {
      lesson: {
        description: 'Descrição',
        links: 'Links',
      },
      activity: {
        name: 'Nome',
        viewActivities: 'Visualizar questões',
      },
    },
    selectTypeButton: {
      activities: 'Atividades',
      questions: 'Questões',
      lessons: 'Aulas',
      all: 'Todos',
    },
    list: {
      empty: {
        lessons: 'Nenhuma aula encontrada',
        activities: 'Nenhuma atividade encontrada',
        questions: 'Nenhuma questão encontrada',
        all: 'Nenhum item encontrado',
      },
    },
    section: {
      types: 'Tipos',
      filters: 'Filtros',
    },
    filters: {
      author: 'Autor',
      unit: 'Unidade',
      tool: 'Ferramenta',
      my: 'minha autoria',
      course: 'meu curso',
      search: 'Pesquisar',
    },
    authorFilter: {
      pedagogical: 'Pedagógico',
      my: 'Meu',
      teacher: 'Professor',
      all: 'Todos',
    },
    useButton: {
      activities: 'Utilizar Atividade',
      questions: 'Utilizar Questão',
      lessons: 'Utilizar Aula',
      all: 'Utilizar Conteúdo',
      addingActivity: 'Adicionando atividade...',
    },
  },
  studentHistoric: {
    title: 'Histórico do Aluno',
    enrollTransfer: 'Matrícula transferida da turma',
    enrollCanceled: 'Matrícula cancelada na turma',
    enrolled: 'Matrícula na turma',
    noData: 'Sem informações sobre este aluno',
    fullScreen: 'tela cheia',
  },

  adminPage: {
    title: 'Admin',
    teacherTitle: 'Professor',
    noTeachers: 'Nenhum professor encontrado nessa unidade',
    noAdminScreen:
      'A tela do admin não está disponível para dispositivos móveis',
    useComputer: 'Favor utilizar um computador',
    editCourseRules: {
      cantModify: 'Não permitido na edição limitada',
      activityWarning:
        'Você está na edição limitada de cursos e, portanto, alterações como: editar respostas ou criar/excluir questões, não são permitidas',
    },
    createCourse: 'Criar Curso',
    addStudent: 'Adicionar Aluno',
    createTeacher: 'Criar Professor',
    createClass: 'Criar Turma',
    students: 'Alunos',
    teachers: 'Professores',
    courses: 'Cursos',
    topics: 'Tópicos',
    prompts: 'Prompts',
    trial: 'Aula Experimental',
    classes: 'Turmas',
    hackathons: 'Hackathons',
    paths: 'Atalhos',
    units: 'Unidades',
    calendars: 'Calendários',
    recesses: 'Recessos',
    team: 'Equipe',
    lesson: {
      modal: {
        beta: {
          title: 'Atenção!',
          warning:
            'O curso será disponibilizado para os professores visualizarem e fazerem sugestões.',
          question: 'Tem certeza de que deseja finalizar a edição?',
          cancel: 'Cancelar',
          confirm: 'Finalizar',
          success: 'O curso foi adicionado para beta com sucesso',
        },
        cancel: {
          title: 'Tem certeza que deseja descartar esta edição?',
          warning:
            'Cancelar todas as alterações é uma ação que não pode ser desfeita. Todas as alterações salvas e não salvas serão perdidas.',
          question: 'Tem certeza de que deseja cancelar todas as alterações?',
          cancel: 'Voltar',
          confirm: 'Descartar',
          success: 'Edição cancelada com sucesso!',
        },
        publishEdit: {
          title: 'Atenção!',
          warning:
            'As alterações serão publicadas para todos os alunos imediatamente. Revise bem as alterações antes de publicá-las',
          question: 'Deseja publicar as alterações?',
          cancel: 'Cancelar',
          confirm: 'Publicar',
          success: 'Curso publicado com sucesso',
        },
        publishBeta: {
          title: 'Atenção!',
          warning:
            'O curso será disponibilizado para novas turmas. Revise e certifique-se que está tudo correto antes de publicar as mudanças.',
          question: 'Tem certeza de que deseja publicar as mudanças?',
          cancel: 'Cancelar',
          confirm: 'Publicar',
          success: 'Alterações publicadas com sucesso',
        },
        enableEdit: {
          title: 'Atenção!',
          warning:
            'Ao habilitar a edição o curso não será mais disponível para professores visualizarem e voltará a ser um rascunho',
          question: 'Tem certeza de que deseja habilitar a edição?',
          cancel: 'Cancelar',
          confirm: 'Habilitar',
        },
      },
      successPublish: 'Curso atualizado com sucesso',
      editingTitle: 'Edição de Curso',
      draftTitle: 'Curso em Rascunho',
      versioningTitle: 'Nova Versão do Curso',
      see: 'Visualizar',
      editLessons: 'Editar Aulas',
      finish: 'Finalizar',
      publish: 'Publicar',
      discard: 'Descartar',
      editingWarning:
        'Você está na edição limitada de cursos apenas para correções ou alterações pequenas. As alterações publicadas serão exibidas para alunos que já estão matriculados nos cursos. Para fazer mais alterações, selecione a opção "Nova Versão".',
      versioningWarning:
        'A nova versão do curso não será exibida para alunos que já estão matriculados no curso. Somente alunos novos verão as alterações',
      newLesson: 'Nova Aula',
      successDeleteMessage: 'Aula deletada com sucesso',
      successOrderMessage: 'Aulas reordenadas com sucesso',
      errorOrderMessage: 'Ocorreu um erro ao reordenar as aulas',
      errorDeleteMessage: 'Ocorreu um erro ao deletar a aula',
      successCreateMessage: 'Aula criada com sucesso',
      errorCreateMessage: 'Ocorreu um erro ao criar a aula',
      modalDeleteConfirm: 'Tem certeza que deseja excluir esta aula?',
      modalWarning: 'Esta ação não poderá ser desfeita',
      errorNotValidExtension: 'O arquivo deve ser do tipo .zip',

      lessonTabs: {
        details: 'Informações',
        material: 'Material do Aluno',
        teacherMaterial: 'Material do Professor',
        activity: 'Atividade',
        challenge: 'Desafio',
        resource: 'Recursos',
      },
      lessonDetails: {
        title: 'Titulo da Aula:',
        description: 'Descrição da Aula:',
        videoInstallMessage:
          'Se essa aula tem um vídeo sobre como instalar/acessar o programa, insira abaixo:',
        videoId: 'Url do Vídeo:',
        bannerUpload: 'Selecione uma imagem para a lição',
        deleteLesson: 'Excluir Aula',
        saveLesson: 'Salvar',
        programLinkMessage:
          'Caso o programa tenha um endereço de acesso, insira-o abaixo:',
        programUrl: 'Endereço de acesso ao programa:',
        programLinkExample: 'Exemplo: https://www.google.com.br/',
        motivation: 'Mensagem Motivacional:',
        success: 'Aula salva com sucesso',
        error: 'Ocorreu um erro ao tentar salvar a aula',
      },

      addMaterial: {
        materialTitle: 'Título do Material',
        teacherMaterialTitle: 'Título do Material do Professor',
        waitComplete: 'Os capítulos estão sendo formatados. Por favor aguarde.',
        addMaterial: 'Adicionar Material',
        tryAgain: 'Tentar novamente',
        slide: 'em Slides',
        text: 'em Texto',
        materialType: 'Formato do Material',
        verifyBook: 'Verifique a integridade do arquivo ou contate o suporte',
        takingTooLong:
          'Está demorando muito para formatar os capítulos. Por favor aguarde ou tente novamente.',
        selectMaterial: 'Selecione um arquivo para enviar.',
        errorMessageUploadZip:
          'Atenção: o upload do material falhou! Tente novamente!',
        successDeleteMaterial: 'O Livro foi deletado com sucesso',
        errorDeleteMaterial: 'Ocorreu um erro ao deletar o livro',
        successUploadZip: 'O material foi carregado com sucesso !',
        errorSaveZip: 'Ocorreu um erro ao salvar o material',
        addButton: 'Fazer upload',
        modalConfirm: 'Tem certeza que deseja excluir este livro?',
        modalWarning: 'Esta ação não poderá ser desfeita',
        back: 'Voltar',
      },
      alert: {
        saved: 'Salvo',
      },
    },
    manageUnit: {
      units: 'Unidades',
      title: 'Gerenciamento de Unidades',
      addUnit: 'ADICIONAR UNIDADE',
      totalUnit: 'TOTAL DE UNIDADES',
      successAddUnitMessage: 'Unidade adicionada com sucesso',
      errorAddUnitMessage: 'Ocorreu um erro ao adicionar a unidade',
      successEditUnitMessage: 'Unidade editada com sucesso',
      errorEditUnitMessage: 'Ocorreu um erro ao editar a unidade',
      name: 'Nome',
      timezone: 'Fuso-horário',
      nameErrorMessage: 'Digite o nome da unidade',
      address: 'Endereço',
      neighbourhood: 'Bairro',
      addressErrorMessage: 'Digite um endereço',
      neighbourhoodErrorMessage: 'Digite o bairro',
      addressNumber: 'Número',
      addressNumberErrorMessage: 'Digite o número do endereço da unidade',
      addressComplement: 'Complemento',
      addressReference: 'Referência',
      city: 'Cidade',
      cityErrorMessage: 'Digite a cidade da unidade',
      state: 'Estado',
      stateErrorMessage: 'Selecione o estado da unidade',
      zipcode: 'CEP',
      phone: 'Telefone',
      cnpj: 'CNPJ',
      corporateName: 'Razão social',
      emailErrorMessage: 'Digite o email da unidade',
      add: 'Adicionar',
      save: 'Salvar',
      cancel: 'Cancelar',
      status: 'Status',
      typeStatus: {
        active: 'Ativo',
        inactive: 'Inativo',
      },
    },
    listUnits: {
      errorTextSubTitle: 'Contate o suporte',
      errorTextTitle: 'Erro ao listar as unidades',
      recess: 'Recesso',
      infoTooltip: 'Informações gerais',
      search: 'Pesquisar',
      clearFilters: 'Limpar filtros',
      searchNotFound: 'Busca não encontrada',
      clearOrder: 'Limpar Ordens',
      clearAll: 'Limpar Ordens e Filtros',
      name: 'Nome do Unidade',
      city: 'Cidade',
      state: 'Estado',
      actions: 'Ações',
      editTooltip: 'Editar',
      deleteTooltip: 'Deletar Unidade',
      lastUpdate: 'Última atualização',
      modalConfirmMessage: 'Tem certeza de que quer deletar essa unidade?',
      modalWarningMessage: 'Essa ação não poderá ser desfeita.',
      successDeleteUnitMessage: 'Unidade deletada com sucesso',
      errorDeleteUnitMessage: 'Ocorreu um erro ao deletar a unidade',
      teachers: 'Professores',
      students: 'Estudantes',
      classes: 'Turmas',
      churn: 'Churn',
      presence: 'Presença',
      status: 'Status',
      units: 'Unidades',
    },
  },

  suggestionsPage: {
    suggestions: 'Sugestões',
    seeAll: 'Ver tudo',
    subheading:
      'Aqui você pode sugerir ou votar em novas funcionalidades no portal, e também acompanhar o adamento',
    updates: 'Novidades',
  },

  teacherCalendar: {
    lesson: 'Aula',
    nextLesson: 'Próxima aula:',
    teacherNoKlass: 'Esse professor ainda não possui nenhuma classe',
    endDate: 'Término:',
    students: 'Alunos:',
    noMoreLessons:
      'Esta turma não possui mais nenhuma aula, atualize o status da turma para Inativa',
  },

  teacherPage: {
    title: 'Professor',
    classes: 'Turmas',
    scheduledKlasses: 'Aulas',
    students: 'Alunos',
    reports: 'Relatórios',
  },

  teacherComments: {
    successAdd: 'Comentário adicionado com sucesso',
    errorAdd: 'Houve um erro ao adicionar o comentário',
    errorUpvote: 'Houve um erro ao votar no comentário',
    successDelete: 'Comentário deletado com sucesso',
    errorDelete: 'Erro ao deletar o comentário',
    at: 'às',
    comment: 'Comentário',
    comments: 'Comentários',
    reply: 'Responder',
    submit: 'Enviar',
    response: 'Resposta',
    responses: 'Respostas',
  },

  manageTrialLesson: {
    trialLessons: 'Aulas Experimentais',
    title: 'Gerenciamento de Aula Experimental',
    add: 'Adicionar Horário',
    totalSeats: 'Total de Vagas',
    seatsPerTeacher: 'Vagas por Professor',
    deleteTooltip:
      'Essa aula já tem alunos matriculados, altere a matrícula primeiro',
    noSchedule: 'Esta unidade não possui horários disponíveis',
    filledSeats: 'Vagas Preenchidas',
    deleteSuccess: 'Horário deletado com sucesso',
    deleteError: 'Houve um problema ao deletar o horário',
    attendance: 'Percentual Presença',
    addLesson: {
      canotChangeTeacher: 'Você já adicionou aulas para este professor',
      teacher: 'Professor',
      dates: 'Datas',
      seats: 'Assentos',
      units: 'Unidade',
      notTrialLesson: 'Não ha aulas experimentais agendadas',
      successMessage: 'Aulas experimentais criadas com sucesso',
    },
  },

  manageCourse: {
    courses: 'Cursos - Admin',
    title: 'Gerenciamento de Cursos',
    teachersQuantity: 'TOTAL DE PROFESSORES',
    studentsPerTeacher: 'ALUNOS POR PROFESSOR',
    weeklyAccesses: 'ACESSOS NESSA SEMANA',
    add: 'ADICIONAR CURSO',
    coursesQuantity: 'TOTAL DE CURSOS',
    klassesTotal: 'TOTAL DE TURMAS',
    studentsTotal: 'TOTAL DE ALUNOS',
    addCourse: {
      name: 'Nome do curso',
      slug: 'Nome resumido do curso',
      abbreviation: 'Sigla do curso',
      type: 'Tipo do curso',
      category: 'Categoria',
      f2f: 'Presencial',
      online: 'Online',
      isActive: 'Este curso está ativo?',
      dragger: 'Selecione uma imagem para o curso',
      addButton: 'Adicionar',
      duration: 'Duração do Curso',
      saveButton: 'Salvar',
      addSuccess: 'Curso criado com sucesso!',
      addError: 'Ocorreu um erro ao criar o curso',
      editSuccess: 'Alterações salvas com sucesso',
      editError: 'Ocorreu um erro ao tentar salvar as alterações',
      ImageUploadSuccess: 'A imagem foi carregada com sucesso:',
      ImageUploadError_1: 'Atenção: o upload da imagem',
      ImageUploadError_2: ', falhou! Tente novamente!',
      ImageTypeError: 'A imagem do banner pode ser um destes tipos de arquivo:',
      slugPopover:
        'O nome resumido é necessário para criar a URL(link) do curso! Exemplo Ctrl Kids 2 = ctrl-kids-2',
      errorMessageName: 'Insira o nome do curso!',
      errorMessageSlug: 'Insira o nome resumido do curso!',
      errorMessageAbbreviation: 'Insira a sigla do curso!',
      errorMessageType: 'Selecione o tipo do curso!',
      errorMessageDuration: 'Insira a duração do curso!',
      cancelButton: 'Cancelar',
      Quick: 'RÁPIDO',
      typeSelect: 'Selecione o tipo do curso',
    },
    editCourse: {
      format: 'Formato',
      abbreviation: 'Sigla do curso',
      duration: 'Duração',
      courseLine: 'Linha do Curso',
      courseModule: 'Módulo',
      isActive: 'Status',
      name: 'Nome do curso',
      type: 'Tipo do curso',
      errorMessageAbbreviation: 'Insira a sigla do curso!',
      errorMessageName: 'Insira o nome do Curso',
      errorMessageDuration: 'Insira a duração do curso!',
      saveButton: 'Salvar',
      cancelButton: 'Cancelar',
      dragger: 'Selecione uma imagem para o curso',
      published: 'Publicado',
      disabled: 'Desabilitado',
      editing: 'Editando',
      versioning: 'Versionando',
      editError: 'Ocorreu um erro ao editar o curso',
      editSuccess: 'Alterações salvas com sucesso',
      ImageUploadError_1: 'Atenção: o upload da imagem',
      ImageUploadError_2: ', falhou! Tente novamente!',
      ImageUploadSuccess: 'A imagem foi carregada com sucesso:',
      active: 'Ativo',
      inactive: 'Inativo',
      Quick: 'RÁPIDO',
      editButton: 'Editar',
      info: 'Informações Gerais',
      saveCreated: 'Curso criado com sucesso',
      saveEdited: 'Curso editado com sucesso',
      wanings: {
        emptyField: 'Preencha esse campo',
        noChanges: 'Nenhuma alteração identificada',
      },
      errors: {
        name: {
          maxLength: 'O nome não deve ultrapassar 200 caracteres',
        },
        abbreviation: {
          maxLength: 'A sigla não deve ultrapassar 7 caracteres',
          exists: 'Essa sigla já existe',
        },
      },
    },
    dataCourseTable: {
      editTooltip: 'Editar Curso',
      deleteTooltip: 'Deletar Curso',
      modalConfirm: 'Tem certeza que deseja excluir este curso?',
      modalWarning:
        'Todo o progresso do curso será perdido e esta ação não pode ser desfeita',
      deleteSuccess: 'Curso deletado com sucesso',
      deleteFail: 'Ocorreu um erro ao tentar deletar o curso',
      branchTooltip: 'Nova Versão',
      infoTooltip: 'Info',
      viewTooltip: 'Ver Curso',
      Quick: 'RÁPIDO',
      statisticNotFound: 'N/A',
      searchNotFound: 'Busca não encontrada',
      noCourses: 'Não há cursos cadastrados',
      noCoursePath:
        'Não é possível versionar esse curso. Por favor contate o suporte',
      school: 'Escola',
    },
    listCourses: {
      search: 'Pesquisar',
      clearFilters: 'Limpar filtros',
      clearOrder: 'Limpar Ordens',
      clearAll: 'Limpar Ordens e Filtros',
      name: 'Curso',
      type: 'Tipo',
      students: 'Alunos',
      status: 'Status',
      actions: 'Ações',
      category: 'Categoria',
      churn: 'Churn',
      presence: 'Presença',
      version: 'Versão',
    },
  },
  manageStudent: {
    students: 'Alunos',
    age_other: '{{count}} anos',
    age_one: '{{count}} ano',
    title: 'Gerenciamento de Alunos',
    accesses: 'TOTAL DE ACESSOS',
    studentWarning:
      'Atenção! Não é possível fazer edições em alunos pelo Portal, somente pela Loja',
    weeklyAccesses: 'ACESSOS NESSA SEMANA',
    quantity: 'TOTAL DE ALUNOS',
    add: 'ADICIONAR ALUNO',
    back: 'voltar',
    status: userStatus,
    listUsers: {
      search: 'Pesquisar',
      clearFilters: 'Limpar filtros',
      clearOrder: 'Limpar Ordens',
      clearAll: 'Limpar Ordens e Filtros',
      name: 'Nome',
      email: 'E-mail',
      unit: 'Unidade',
      klass: 'Turma',
      phoneNumber: 'Telefone',
      role: 'Cargo',
      lastAccess: 'Último Acesso',
      status: 'Status',
      actions: 'Ações',
    },
    dataUserTable: {
      passwordTooltip: 'Alterar Senha',
      infoTooltip: 'Informações Gerais',
      never: 'Nunca',
      searchNotFound: 'Busca não encontrada',
      noStudents: 'Não há alunos cadastrados',
      rewardsTooltip: 'Recompensas',
      pedagogicalSheetTooltip: 'Ficha Pedagógica',
      historicTooltip: 'Histórico do Aluno',
      vacationNoticeTooltip: 'Aviso de férias',
    },
    addUser: {
      info: 'Informações Gerais',
      edit: 'Editar',
      unit: 'Unidade',
      name: 'Nome',
      lastName: 'Sobrenome',
      email: 'E-mail',
      username: 'Usuário',
      selectCity: 'Digite a cidade',
      usernameRule: 'Somente letras, ponto, hifen e Underline',
      password: 'Senha',
      passwordConfirm: 'Confirme a senha',
      errorMessagePasswordMatch: 'As senhas inseridas não coincidem',
      birth: 'Aniversário',
      state: 'Estado',
      city: 'Cidade',
      bio: 'Bio',
      klass: 'Turma',
      phoneNumber: 'Telefone',
      role: 'Cargo',
      status: 'Status',
      Registred: 'Registrado',
      Active_Enrollment: 'Matricula Ativa',
      Inactive: 'Inativo',
      Waiting_Enrollment: 'Aguardando Matricula',
      errorMessageUnit: 'Selecione a Unidade',
      errorMessageName: 'Insira o nome do estudante',
      errorMessageLastName: 'Insira o sobrenome do estudante',
      errorMessageEmail: 'Insira o e-mail do estudante',
      errorMessageUsername: 'Insira o usuário do estudante',
      errorMessagePassword: 'Insira a senha do estudante',
      errorMessagePasswordConfirm: 'Confirme a senha do estudante',
      errorMessageBirth: 'Insira a data de nascimento do estudante',
      errorMessageState: 'Insira o estado do estudante',
      errorMessageCity: 'Insira a cidade do estudante',
      errorMessageKlass: 'Insira uma turma para o estudante',
      addButton: 'Adicionar',
      successCreateUser: 'Estudante criado com sucesso',
      errorCreateUser: 'Ocorreu um erro ao criar o estudante',
      successEditUser: 'Alterações salvas com sucesso',
      errorEditUser: 'Ocorreu um erro ao salvar as alterações',
      saveButton: 'Salvar',
      cancelButton: 'Cancelar',
      addUserRuleOne:
        'apenas letras, números e . - _ são aceitos para username ou e-mail',
      addUserRuleTwo: 'preencha todos os campos',
      addUserRuleThree: 'certifique-se que os campos de senha coincidem',
    },
  },

  manageTeam: {
    team: 'Time',
    teacherWarning:
      'Atenção! Não é possível criar professores ou fazer algumas edições pelo Portal, somente pela Loja',
    title: 'Gerenciamento de Equipe',
    course: {
      label: 'Cursos',
      placeholder: 'Selecione um curso',
    },
    accesses: 'TOTAL DE ACESSOS',
    weeklyAccesses: 'ACESSOS NESSA SEMANA',
    quantity: 'TOTAL DE MEMBROS',
    add: 'ADICIONAR EQUIPE',
    back: 'voltar',
    SUPER_ADMIN: 'Super Administrador',
    UNIT_ADMIN: 'Administrador',
    CONTENT_CREATOR: 'Conteudista',
    TEACHER: 'Professor',
    fromUnit: 'De outra unidade',
    newUser: 'Novo usuário',
    errorUserAlreadyHasUnit: 'Usuário já está nessa unidade',
    selectUser: 'Selecione o usuário',
    selectUnit: 'Seleciona a unidade',
    unitLabel:
      'O usuário manterá a(s) unidade(s) antiga, além da nova selecionada abaixo:',
    status: userStatus,
    listUsers: {
      search: 'Pesquisar',
      clearFilters: 'Limpar filtros',
      clearOrder: 'Limpar Ordens',
      clearAll: 'Limpar Ordens e Filtros',
      name: 'Nome',
      email: 'E-mail',
      unit: 'Unidade',
      klass: 'Turma',
      phoneNumber: 'Telefone',
      role: 'Cargo',
      lastAccess: 'Último Acesso',
      status: 'Status',
      actions: 'Ações',
    },
    dataUserTable: {
      passwordTooltip: 'alterar senha',
      infoTooltip: 'informações gerais',
      never: 'Nunca',
      searchNotFound: 'Busca não encontrada',
    },
    addUser: {
      info: 'Informações Gerais',
      edit: 'Editar',
      selectCity: 'Digite a cidade',
      selectRole: 'Selecione o cargo',
      unit: 'Unidade',
      name: 'Nome',
      lastName: 'Sobrenome',
      email: 'E-mail',
      username: 'Usuário',
      usernameRule: 'Somente letras, ponto, underline e números',
      password: 'Senha',
      passwordConfirm: 'Confirme a senha',
      errorMessagePasswordMatch: 'As senhas inseridas não coincidem',
      birth: 'Nascimento',
      state: 'Estado',
      city: 'Cidade',
      bio: 'Bio',
      klass: 'Turma',
      phoneNumber: 'Telefone',
      role: 'Cargo',
      status: 'Status',
      Registred: 'Registrado',
      Active_Enrollment: 'Matricula Ativa',
      Inactive: 'Inativo',
      Waiting_Enrollment: 'Aguardando Matricula',
      errorMessageUnit: 'Selecione a Unidade',
      errorMessageName: 'Insira o nome do membro da equipe',
      errorMessageLastName: 'Insira o sobrenome do membro da equipe',
      errorMessageEmail: 'Insira o e-mail do membro da equipe',
      errorMessageUserType: 'Selecione o tipo de usuário',
      errorMessageUsername: 'Insira o usuário do membro da equipe',
      errorMessagePassword: 'Insira uma senha',
      errorMessagePasswordConfirm: 'Confirme a senha',
      errorMessageBirth: 'Insira a data de nascimento do membro da equipe',
      errorMessageState: 'Insira o estado do membro da equipe',
      errorMessageCity: 'Insira a cidade do membro da equipe',
      errorMessageKlass: 'Insira uma turma para o membro da equipe',
      addButton: 'Adicionar',
      successCreateUser: 'Funcionário criado com sucesso',
      errorCreateUser: 'Ocorreu um erro ao criar o membro da equipe',
      successEditUser: 'Alterações salvas com sucesso',
      errorEditUser: 'Ocorreu um erro ao salvar as alterações',
      saveButton: 'Salvar',
      cancelButton: 'Cancelar',
      addUserRuleOne:
        'Apenas letras, números e . - _ são aceitos para username',
      addUserRuleTwo: 'preencha todos os campos',
      addUserRuleThree: 'certifique-se que os campos de senha coincidem',
    },
  },

  manageTeacher: {
    teachers: 'Professores',
    title: 'Gerenciamento de Professores',
    quantity: 'TOTAL DE PROFESSORES',
    accesses: 'TOTAL DE ACESSOS',
    weeklyAccesses: 'ACESSOS NESSA SEMANA',
    add: 'ADICIONAR PROFESSOR',
    back: 'voltar',
    status: userStatus,
    listUsers: {
      search: 'Pesquisar',
      clearFilters: 'Limpar filtros',
      clearOrder: 'Limpar Ordens',
      clearAll: 'Limpar Ordens e Filtros',
      name: 'Nome',
      email: 'E-mail',
      unit: 'Unidade',
      klass: 'Turma',
      phoneNumber: 'Telefone',
      role: 'Cargo',
      lastAccess: 'Último Acesso',
      status: 'Status',
      actions: 'Ações',
      students: 'Alunos',
      presence: 'Presença',
      churn: 'Churn',
    },
    dataUserTable: {
      noTeachers: 'Não há professores cadastrados',
      searchNotFound: 'Busca não encontrada',
      passwordTooltip: 'Alterar Senha',
      editTooltip: 'Editar Professor',
      deleteTooltip: 'Deletar Professor',
      never: 'Nunca',
      IntoTooltip: 'Informações gerais',
      studentsTooltip: 'Alunos de turmas em progresso',
      lessonsTooltip: 'Aulas',
      noProfileFound: 'Perfil do professor não encontrado',
    },
    addUser: {
      info: 'Informações Gerais',
      edit: 'Editar',
      unit: 'Unidade',
      name: 'Nome',
      lessons: 'Aulas',
      filters: {
        allLessons: 'todas as aulas',
        completedLessons: 'aulas concluídas',
        plannedLessons: 'aulas programadas',
      },
      noContent: {
        allLessons: 'Nenhuma aula para esse professor',
        completedLessons: 'Nenhuma aula concluída por esse professor',
        plannedLessons: 'Nenhuma aula programada para esse professor',
      },
      lastName: 'Sobrenome',
      email: 'E-mail',
      username: 'Usuário',
      usernameRule: 'Somente letras, ponto, hifen e Underline',
      password: 'Senha',
      passwordConfirm: 'Confirme a senha',
      birth: 'Nascimento',
      state: 'Estado',
      city: 'Cidade',
      selectCity: 'Digite a cidade',
      bio: 'Bio',
      klass: 'Turma',
      phoneNumber: 'Telefone',
      role: 'Cargo',
      status: 'Status',
      Registred: 'Ativo',
      Active_Enrollment: 'Matricula Ativa',
      Inactive: 'Inativo',
      Waiting_Enrollment: 'Aguardando Matricula',
      errorMessageUnit: 'Selecione a Unidade',
      errorMessageName: 'Insira o nome do professor',
      errorMessageLastName: 'Insira o sobrenome do professor',
      errorMessageEmail: 'Insira o e-mail do professor',
      errorMessageUsername: 'Insira o usuário do professor',
      errorMessagePassword: 'Insira a senha do professor',
      errorMessagePasswordConfirm: 'Confirme a senha do professor',
      errorMessagePasswordMatch: 'As senhas inseridas não coincidem',
      errorMessageBirth: 'Insira a data de nascimento do professor',
      errorMessageState: 'Insira o estado do professor',
      errorMessageCity: 'Insira a cidade do professor',
      errorMessageKlass: 'Insira uma turma para o professor',
      addButton: 'Adicionar',
      successCreateUser: 'Professor criado com sucesso',
      errorCreateUser: 'Ocorreu um erro ao criar o professor',
      successEditUser: 'Alterações salvas com sucesso',
      errorEditUser: 'Ocorreu um erro ao salvar as alterações',
      saveButton: 'Salvar',
      cancelButton: 'Cancelar',
      addUserRuleOne:
        'apenas letras, números e . - _ são aceitos para username ou e-mail',
      addUserRuleTwo: 'preencha todos os campos',
      addUserRuleThree: 'certifique-se que os campos de senha coincidem',
      typeStatus: {
        active: 'Ativo',
        inactive: 'Inativo',
      },
    },
  },
  manageHackathon: {
    add: 'Adicionar hackathon',
    title: 'Gerenciamento de Hackathons',
    hackathonStages: 'Estágios',
    info: 'Informações',
    noHackathons: 'Nenhum hackathon encontrado',
    titles: {
      hackathon: 'Hackathon',
      theme: 'Tema',
      content: 'Conteúdo',
    },
  },
  manageKlasses: {
    totalStudents: 'TOTAL DE ALUNOS',
    status: klassStatus,
    klasses: 'Turmas',
    title: 'Gerenciamento de Turmas',
    klassesPerTeacher: 'ALUNOS POR TURMA',
    klassesQuantity: 'TOTAL DE TURMAS',
    studentsPerTeacher: 'ALUNOS POR PROFESSOR',
    weeklyAccesses: 'ACESSOS NESSA SEMANA',
    add: 'ADICIONAR TURMA',
    updateCourse: {
      title: 'Atualizar para nova versão do curso',
      success: 'Turma atualizada com sucesso',
    },
    addKlass: {
      selectDayTimes: 'Selecione primeiro o dia da semana e horário da turma',
      info: 'Informações Gerais',
      warning: 'Alterações na turma só podem ser feitas pela Loja',
      edit: 'Editar',
      course: 'Curso',
      classRoomUrl: 'Link para a aula online',
      f2f: 'Presencial',
      online: 'Online',
      startDate: 'Início',
      endDate: 'Fim',
      klassStatus,
      duration: 'Duração',
      status: 'Status',
      klassDateAndTime: 'Dia e Horários',
      recurrence: 'Recorrência',
      recessCalendar: 'Calendario',
      teacher: 'Professor',
      students: 'Alunos',
      add: 'Adicionar',
      save: 'Salvar',
      cancel: 'Cancelar',
      addStudentButton: 'Adicionar Aluno',
      addSuccess: 'Turma criada com sucesso',
      addError: 'Ocorreu um erro ao criar a turma',
      saveSuccess: 'Alterações salvas com sucesso',
      saveError: 'Ocorreu um erro ao salvar as alterações',
      moveTooltip: 'Mover Aluno',
      deleteTooltip: 'Remover Aluno',
      unit: 'Unidade',
      meetLink: 'Link da Reunião',
      dayOfWeek: 'Dia da semana',
      time: 'Horário',
      day: 'Dia',
      error: {
        unit: 'Selecione a unidade',
        course: 'Inserir o curso',
        klassDate: 'Inserir a data de início do curso',
        klassTime: 'Inserir o horario da turma',
        recurrence: 'Insira a recorrência da turma',
        recessCalendar: 'Insira o Calendario de Recessos',
        dayWeek: 'Insira o dia da semana',
        teacher: 'Insira o professor da turma',
        students: 'Insira pelo menos um aluno',
        duration: 'Insira a duração de cada aula',
        invalidTime: 'Insira um horário válido',
        klassType: 'Insira o tipo de turma',
        klassStatus: 'Insira o status da turma',
      },
      select: {
        course: 'Selecione o curso',
        day: 'Selecione um dia da semana',
        duration: 'Selecione a duração da aula',
        teacher: 'Selecione o professor',
      },

      maxCapacity: 'Capacidade máxima',
      conectaSeats: 'Assentos do conecta',
      klassType: 'Tipo da turma',
      coursePathId: 'Curso',
    },
    dataKlassTable: {
      searchNotFound: 'Busca não encontrada',
      noKlasses: 'Não há turmas cadastradas',
      infoTooltip: 'Informações Gerais',
      editTooltip: 'Editar Classe',
      noDate: 'Sem data',
      processingEnrollment: 'Matrícula em Processamento',
      teacherStudentsTooltip: 'Professor e alunos',
      classProgressTooltip: 'Progresso da turma',
      klassSheetTooltip: 'Ficha da turma',
      teacherStudentsPill: 'Professores e alunos',
      lessonsTooltip: 'Aulas marcadas',
      deleteTooltip: 'Deletar Classe',
      modalConfirm: 'Tem certeza que deseja excluir essa classe?',
      modalWarning:
        'Todo o progresso do classe será perdido e esta ação não pode ser desfeita',
      deleteSuccess: 'Classe deletada com sucesso',
      deleteFail: 'Ocorreu um erro ao tentar deletar o classe',
      teacher: 'Professor',
      students: 'Alunos',
      noStudentsToFollow: 'Sem alunos para acompanhar',
      months: {
        january: 'Janeiro',
        february: 'Fevereiro',
        march: 'Março',
        april: 'Abril',
        may: 'Maio',
        june: 'Junho',
        july: 'Julho',
        august: 'Agosto',
        september: 'Setembro',
        october: 'Outubro',
        november: 'Novembro',
        december: 'Dezembro',
      },
      scheduledLessonProgressView: {
        klasses: 'Aulas',
        everyKlass: 'todas as aulas',
        completedKlasses: 'aulas concluídas',
        scheduledKlasses: 'aulas programadas',
        noScheduledLessons: 'Nenhuma lição para exibir',
        pendingKlasses: 'com relatórios pendentes',
        antecipate: {
          start: 'Início:',
          save: 'Data de inicio alterada',
          button: 'alterar',
        },
        ascending: 'crescente',
        descending: 'decrescente',
        sort: 'Ordem',
      },
    },
    listKlasses: {
      search: 'Pesquisar',
      clearFilters: 'Limpar filtros',
      clearOrder: 'Limpar Ordens',
      clearAll: 'Limpar Ordens e Filtros',
      klass: 'Classe',
      unit: 'Unidade',
      start: 'Início',
      end: 'Término',
      status: 'Status',
      actions: 'Ações',
      activeClasses: 'Classes ativas:',
    },
  },

  klassProgressPage: {
    lessonBlocked:
      'Esta lição ainda não está liberada, ela ficará disponível em breve',
    pageTitle: 'Acompanhamento da Turma',
    notFoundTitle: 'Parece que a turma que você está buscando não existe',
    notFoundText: 'Favor tente outra turma',
    genericErrorTitle: 'Ops, tivemos um problema',
    genericErrorText: 'Contate o time de suporte ou tente novamente mais tarde',
    noStudents: 'Essa turma ainda não tem nenhum aluno, selecione outra turma',
    lesson: 'Aula',
    lessons: 'Aulas',
    students: 'Alunos',
    attendances: 'Pres.',
    noProgress: 'Sem progresso nesta aula associado à esta turma',
    absences: 'Faltas',
    aboutKlass: 'Sobre a turma',
    progressAverage: 'Média do Progresso',
    averagePP: 'Média PP',
    pleaseFillReport: 'Por favor, preencha o relatório',
    givenContent: 'Conteúdo Dado',
    lessonScheduled: 'Aula Prevista',
    lessonIsScheduled: 'Esta aula está prevista para os próximos dias',
    reportNotFilled: 'Relatório não preenchido',
    seeReport: 'ver relatório',
    missed: 'Falta',
    noActiveStudent: 'Nehum aluno ativo',
    noInactiveStudent: 'Nenhum aluno inativo',
    attended: 'Presente',
    active: 'Ativos',
    inactive: 'Inativos',
    all: 'Todos',
    transferred: 'Transferido',
    pending: 'Pendente',
    done: 'Feito',
  },

  activity: {
    errorTextTitle: 'Erro ao carregar as atividade',
    errorTextSubTitle:
      'Verifique se está na página correta ou contate o suporte',
    newActivity: 'Nova Atividade',
    textLabel: 'Texto',
    videoLabel: 'Video',
    quizLabel: 'Quiz',
    codeLabel: 'Código',
    codeEditorLabel: 'Editor de código',
    blockCodeLabel: 'Código em blocos',
    trueFalseLabel: 'V/F',
    dragAndDropOrdenation: 'Drag e Drop de ordenação',
    dragAndDropPositional: 'Drag and Drop positional',
    deleteSuccess: 'Atividade deletada com sucesso',
    deleteFail: 'Ocorreu um erro ao deletar a atividade',
    addSuccess: 'Atividade Adicionada',
    addError: 'Erro ao adicionar aula',
    modalConfirm: 'Tem certeza que deseja excluir esta Atividade?',
    modalWarning: 'Esta ação não poderá ser desfeita',
    manageActivity: {
      editActivity: 'Editar atividade',
      generateQuestion: 'Gerar Questão',

      errorTextTitle: 'Erro ao carregar as atividade',
      errorTextSubTitle:
        'Verifique se está na página correta ou contate o suporte',
      newQuestion: 'Nova Questão',
      notAllowed: 'Não permitido na edição limitada',
      save: 'Salvar',
      add: 'Adicionar',
      detailsLabel: 'Informações',
      activityTypes: {
        TEXT: 'Texto',
        VIDEO: 'Video',
        QUIZ: 'Quiz',
        TRUE_OR_FALSE: 'V/F',
        DND_ORDENATION: 'Arrastar',
        DND_POSITIONAL: 'Posicionar',
        CODE: 'Código',
        CODE_EDITOR: 'Editor de código',
        BLOCK: 'Código em Blocos',
      },
      examLabel: 'Teste',
      pdfLabel: 'PDF',
      taskLabel: 'Tarefa',
      validationError: 'Erro ao salvar, corrija os erros',
      saveSuccess: 'Atividade salva com sucesso',
      deleteSuccess: 'Atividade deletada com sucesso',
      saveError: 'Ocorreu um erro ao tentar salvar a atividade',
      activityDetails: {
        name: 'Título da Atividade',
        inBank: 'Adicionar ao Banco de Atividades',
        isPublicInBank: 'Público',
        config: '',
        noQuestionsWarning:
          'Nenhuma questão criada, clique em + Nova Questão para adicionar uma questão',
        save: 'Salvar',
        delete: 'Excluir Atividade',
        deleteError: 'Ocorreu um erro ao tentar deletar a atividade',
        deleteSuccess: 'Atividade deletada com sucesso',
        saveError: 'Ocorreu um erro ao tentar salvar a atividade',
        saveSuccess: 'Atividade salva com sucesso',
      },
      activityBlock: {
        title: {
          label: 'Título',
          required: 'O título é obrigatório',
        },
        instructions: {
          label: 'Instruções',
          placeholder: 'Insira as instruções da atividade',
          required: 'As instruções são obrigatórias',
        },
        tip: {
          label: 'Dica',
          placeholder: 'Opcional: Adicione uma dica caso o aluno se perca',
        },
        maxBlocks: 'Número máximo de blocos',
        maxBlocksAlert:
          'O número máximo de blocos não pode ser menor que o número de blocos já adicionados',
        solution: {
          label: 'Código de solução',
          required: 'A solução é obrigatória',
          validateFail: 'O código de solução não atende a saída esperada',
        },
        initial: {
          label: 'Código base',
          required: 'O código inicial é obrigatório',
        },
        expectedOutput: {
          label: 'Saída Esperada',
          required: 'A saída esperada é obrigatória',
        },
        validate: 'Validar',
        validateSuccess: 'Válido',
        validateFail: 'Inválido',
      },
      activityCode: {
        title: { label: 'Titulo', required: 'O titulo é obrigatório' },
        tip: {
          label: 'Dica',
          placeholder: 'Opcional: Adicione uma dica caso o aluno se perca',
        },
        instructions: {
          label: 'Instruções',
          placeholder: 'Insira as instruções da atividade',
          required: 'As instruções são obrigatórias',
        },
        map: {
          run: 'RODAR',
          reset: 'RESETAR',
          edit: 'Editar Mapa',
        },
        workspaceOptions: {
          title: 'Opções',
          maxBlocks: 'Limite de Blocos',
          maxBlocksAlert:
            'O limite de blocos não pode ser menor que o número de blocos já adicionados',
          coins: 'Moedas',
        },
        save: 'Salvar',
        success: 'Atividade salva com sucesso',
      },
      activityText: {
        title: 'Enunciado:',
        titleError: 'Insira o titulo do texto',
        subtitle: 'Subtítulo',
        subtitleError: 'Insira o subtítulo do texto',
        content: 'Conteúdo:',
        contentError: 'Escreva alguma coisa como conteudo da questão',
        save: 'Salvar',
        saveSuccess: 'Atividade salva com sucesso',
        saveError: 'Ocorreu um erro ao salvar a atividade',
      },
      activityVideo: {
        title: 'Enunciado:',
        titleError: 'Insira o titulo do vídeo',
        subtitle: 'Subtítulo:',
        subtitleError: 'Insira o subtítulo do vídeo',
        videoID: 'Id do Vídeo:',
        videoIDError: 'O vídeo deve ter uma ID',
        videoPlayer: 'Vídeo Player:',
        videoPlayerError: 'Selecione um dos players acima',
        exampleMessage1: 'Exemplo: insira ',
        exampleMessage2: ' da URL ',
        save: 'Salvar',
        saveSuccess: 'Atividade salva com sucesso',
        saveError: 'Ocorreu um erro ao salvar a atividade',
      },
      activityQuiz: {
        answers: {
          answers: 'Respostas:',
          noValue: 'Resposta sem conteúdo',
          cantRemove: 'O quiz deve ter no mínimo 2 alternativas',
        },
        image: 'imagem',
        text: 'texto',
        cantChangeType:
          'Não é possível alterar tipo desse quiz. Crie um novo quiz.',
        imageDescription: 'Descrição da imagem:',
        missingAlt: 'Insira a descrição das imagens',
        missingImages: 'Algumas alternativas estão sem imagem',
        title: 'Enunciado:',
        content: 'Conteúdo:',
        titleError: 'A questão precisa de um enunciado',
        answersError: 'Pelo menos duas respostas devem ser adicionadas',
        atLeastOneTrueAnswerError: 'Uma resposta deve ser marcada como correta',
        newAnswer: 'Nova Resposta',
        justificative: 'Explicação da resposta certa:',
        justificativeError: 'A questão precisa de uma justificativa',
        save: 'Salvar',
        saveSuccess: 'Atividade salva com sucesso',
        saveError: 'Ocorreu um erro ao salvar a atividade',
      },
      activityTrueFalse: {
        title: 'Enunciado:',
        content: 'Conteúdo:',
        titleError: 'A questão precisa de um enunciado',
        trueOrFalse: 'O enunciado é verdadeiro ou falso?',
        trueOrFalseError: 'Uma opção deve ser selecionada como resposta',
        true: 'Verdadeiro',
        false: 'Falso',
        justificative: 'Explicação da resposta certa:',
        justificativeError: 'A questão precisa de uma justificativa',
        save: 'Salvar',
        saveSuccess: 'Atividade salva com sucesso',
        saveError: 'Ocorreu um erro ao salvar a atividade',
      },
      challenge: {
        title: 'Desafio',
        save: 'Salvar',
        add: 'Adicionar Desafio',
        deleteSuccess: 'Desafio deletado com sucesso',
        deleteFail:
          'Houve um erro ao deletar o desafio, tente novamente mais tarde ou contate o time de suporte',
        edit: 'Editar Desafio',
        delete: 'Excluir Desafio',
        cancel: 'Cancelar',
        saveSuccess: 'Desafio salvo com sucesso',
        saveError: 'Ocorreu um erro ao salvar o desafio',
        content: 'Conteúdo',
      },
      activityDNDOrdenation: {
        title: 'Enunciado:',
        subtitle: 'Subtítulo:',
        columns: 'Colunas:',
        columnName: 'Nome da coluna:',
        text: 'Texto',
        descriptionImage: 'Descrição da imagem',
        newItem: 'Novo item',
        newColumn: 'Adicionar nova coluna',
        removeColumn: 'Remover coluna',
        save: 'Salvar',
        textTooltipRemoveColumn: 'Só pode remover se tiver mais de uma coluna',
        textTooltipRemoveItem: 'Só pode remover se tiver mais de três itens',
        nameColumnRequired: 'Nome da coluna é obrigatório',
        noAltText: 'Adicionar uma descrição para a imagem',
        noImage: 'Adicionar uma imagem',
        noContent: 'Adicione algum conteúdo no item',
        noTitle: 'A questão precisa de um enunciado',
        saveSuccess: 'Atividade salva com sucesso',
        saveOrderSuccess: 'Nova ordem salva',
        saveDeleteItemSuccess: 'Item deletado',
        saveAddItemSuccess: 'Novo item adicionado',
        saveDeleteColumnSuccess: 'Coluna deletada',
        saveAddColumnSuccess: 'Coluna adicionada',
        maxLength: 'O conteúdo não deve ultrapassar 150 caracteres',
        maxLengthAltText:
          'A descrição da imagem não deve ultrapassar 150 caracteres',
      },

      dndPositionalElementEdit: {
        saveSuccess: 'Atividade salva com sucesso',
        saveOrderSuccess: 'Nova ordem salva',
        saveDeleteSuccess: 'Item deletado',
        saveAddItemSuccess: 'Novo item adicionado',
        dndErrors: {
          onlyOneItem: 'A atividade deve conter mais itens',
        },
        dndActivityInfo: {
          title: 'Enunciado:',
          subtitle: 'Subtítulo:',
          dndErrors: {
            noTitle: 'A questão precisa de um enunciado',
          },
        },
        dndColumnEdit: {
          columnName: 'Nome da coluna:',
          descriptionImage: 'Descrição da imagem',
          newItem: 'Novo item',
          question: 'Pergunta:',
          answer: 'Resposta:',
          textTooltipRemoveItem: 'Só pode remover se tiver mais de um item',
          textTooltipReorderItem: 'Só pode reordenar se tiver mais de um item',
          dndErrors: {
            nameColumnRequired: 'Nome da coluna é obrigatório',
            noTitle: 'A questão precisa de um enunciado',
            noContent: 'Adicione algum conteúdo no item',
            noAltText: 'Adicione uma descrição para a imagem',
            maxLength: 'O conteúdo não deve ultrapassar 150 caracteres',
            maxLengthAltText:
              'A descrição da imagem não deve ultrapassar 150 caracteres',
          },
        },
        save: 'Salvar',
      },
    },

    activityBank: 'Banco de atividades',
  },
  changeLanguage: 'Mudar Idioma',
  configuration: 'Configuração',
  back: 'voltar',
  leaderboard: {
    classXp: '{{xp}}exp na turma',
    rank: {
      position_one: '{{count}}°',
      position_two: '{{count}}°',
      position_few: '{{count}}°',
      position_other: '{{count}}°',
    },
  },
  dashboard: {
    hello: 'Olá, ',
    teacherUnderConstruction: 'Nova dashboard do professor em construção',
    wellcome: {
      teacher:
        'Seja bem vindo de volta professor(a)! Pronto para continuar sua jornada? 🚀',
      admin: 'Bem vindo de volta administrador!',
      unitAdmin: 'Bem vindo de volta administrador da unidade!',
      parent:
        'Bem vindo à Ctrl+Play, pronto para transformar o futuro do seu filho?',
    },
    yourCourse: 'CURSO',
    accessClasses: 'Aulas',
    totalClasses: 'AULAS',
    totalActivities: 'ATIVIDADES',
    welcome:
      'Seja bem vindo à Ctrl + Play. Dê uma olhada sobre como nossos cursos funcionam e faça sua matrícula!',
    dashboardClass: {
      teacher: 'Professor',
      yourClass: 'Sua Turma',
      colleagues: 'Colegas',
      noTeacherText: 'Professor não selecionado',
      teacherKlassMembers:
        'Selecione uma turma para ver os avatares e acessar o perfil dos alunos',
    },
    error: 'Não foi possível carregar o progresso',
    support:
      'Parece que algo deu errado. Por favor, fale com a equipe de suporte',
    studentsPerformance: {
      title: 'Performance dos alunos',
      description: `Baseado no progresso de <bold>{{studentsCount}}</bold> alunos`,
    },
  },
  coursesView: {
    myLessons: 'Minhas Aulas',
    myCourses: 'Meus Cursos',
    myCoursesInstructions:
      'Aqui você pode acessar todas as aulas e atividades disponíveis no seu curso',
    completedCourses: 'CURSOS CONCLUÍDOS',
    nextSteps: 'Próximos Passos',
    continueText:
      'Continue a evoluir e aprender com os cursos mais avançados da Ctrl+Play.',
    student: {
      courses: 'Cursos',
      noCourseProgressesFound: 'Não foi possível encontrar um curso',
      filters: {
        ordering: 'Ordenar por',
        search: 'Pesquisar',
        async: 'Autodidata',
        all: 'Todos',
      },
      section: {
        [ProgressStatusEnum.IN_PROGRESS]: 'Em andamento',
        [ProgressStatusEnum.CONCLUDED]: 'Concluídos',
      },
    },

    admin: {
      search: 'Pesquisar',
      notFoundKlass: 'Nenhuma turma encontrada',
      filters: {
        orderByFilter: {
          '-rating': 'melhor avaliado',
          rating: 'pior avaliado',
          title: 'selecione',
          created_at: 'menos recente',
          '-created_at': 'mais recente',
          '-last_update': 'recentemente atualizado',
          '-class_count': 'mais turmas',
          class_count: 'menos turmas',
        },
        groupByFilter: {
          NONE: 'selecione',
          STATUS: 'status',
          LINE: 'linha',
        },
        all: 'todos',
        category: 'categoria',
        courses: 'Curso',
        version: 'Versão',
        groupBy: 'Agrupar por',
        orderBy: 'Ordenar por',
        search: 'Pesquisar',
      },
      groupBy: {
        all: 'Ver todos',
        back: 'Voltar',
      },
      tooltip: {
        info: 'Informações',
        view: 'Visualizar',
      },
      klassesCount_one: '{{count}} Turma',
      klassesCount_other: '{{count}} Turmas',
      versions: 'Versões',
      lastEdit: 'Editado {{dateDistance}}',
      createAt: 'Criado {{dateDistance}}',
      publishedAt: 'Publicado {{dateDistance}}',
      continueCreating: 'Continuar criando',
      continueEdit: 'Continuar editando',
      continueVersion: 'Continuar versionando',
      createEdit: 'Criar edição',
      createVersion: 'Criar versionamento',
      discardCourse: 'Descartar curso',
      errorTextTitle: 'Course não encontrado',
      errorTextSubTitle: 'Verifique se você está na página correta',
      unpublishedCourses: 'Cursos não publicados',
      betaDraftExplanation:
        'Os cursos em "Beta" abaixo estão disponíveis para alguns professores olharem, revisarem ou fazerem sugestões. Cursos em "Rascunho" ainda estão sendo editados e não são exibidos para os professores até que as edições sejam finalizadas.',
      courses: 'Cursos',
      finish: 'Finalizar',
      publish: 'Publicar Curso',
      backEditing: 'Continuar Editando',
      f2f: 'Presencial',
      online: 'Online',
      newCourse: 'Novo Curso',
      newLesson: 'Nova Aula',
      viewCourse: 'Visualizar Curso',
      newVersion: 'Nova Versão',
      editCourse: 'Editar Curso',
      enableEditing: 'Habilitar Edição',
      myLessons: 'Minhas Aulas',
      addExtraLesson: {
        add: 'Adicionar',
        newLesson: 'Título da Aula',
        cancel: 'Cancelar',
        selectLessonExplain:
          'A aula extra será adicionada antes da lição selecionada. Você não pode selecionar aulas que já passaram.',
        selectLesson: 'Selecione uma lição',
        title: 'Selecione uma lição',
        allLessonClassIsDone: 'Todas as aulas da turma estão concluídas.',
        addExtraLessonQuestion: 'Deseja adicionar uma aula extra após a aula?',
      },
    },
    progress: 'PROGRESSO',
  },
  profile: {
    title: 'Meu Perfil',
    skills: 'Habilidades',
    level: {
      levelName: 'Nível {{level}}',
      levelProgress: '{{currentXp}}/{{targetXp}} EXP',
    },
    errors: {
      username: 'Este nome de usuário já existe',
      links: 'Link inválido',
    },
    sixLinks: 'É permitido adicionar até 6 links',
    addLinks: 'Adicionar Link',
    saveProfile: 'Salvar Perfil',
    editProfile: 'Editar Perfil',
    saveSuccess: 'Alterações salvas com sucesso',
    saveError:
      'Ocorreu um erro, tente novamente mais tarde ou contate o time de suporte',
    notFoundTitle: 'Ops, nenhum usuário encontrado',
    notFoundSubtitle: 'Parece que o usuário que você está buscando não existe',
    cancel: 'Cancelar',
    create: 'Crie seu perfil',
    checklist: {
      avatar: 'Adicionar avatar',
      banner: 'Adicionar banner',
      links: 'Adicionar links sociais',
      bio: 'Adicionar bio',
    },
    bioCard: {
      title: 'Este é o seu perfil!',
      description:
        'Clique no botão de editar perfil para adicionar sua Bio, links sociais, banner, ou trocar sua foto de perfil.',
    },
    totalActivities: 'Total de Atividades',
    completedCourses: 'Cursos Concluídos',
  },
  skillPage: {
    title: 'Habilidades',
    total: 'Total',
    general: 'Geral',
    areaCard: {
      lastMonth_one: 'Último mês',
      lastMonth_other: 'Últimos {{count}} meses',
      progress: 'Progresso',
    },
    chartOf: 'Gráfico de {{name}}',
    generalChart: 'Gráfico Geral',
  },
  klassView: {
    controls: {
      extraLesson: 'Aula Extra',
      lesson: 'Aula',
    },
    fullScreen: {
      exit: 'Sair de tela cheia',
      enter: 'Tela cheia',
    },
    lessonNotFound: 'Não foi possível encontrar essa aula',
    instruction: {
      errorTextTitle: 'Vídeo não encontrado',
      errorTextSubTitle: 'O vídeo sobre a ferramenta não foi encontrado',
      vizualized: 'Visualizado',
      accessAndInstalation: 'Acesso e Instalação',
    },
    iconType: {
      access: 'Acesso a Ferramenta',
      activity: 'Atividades',
      teacherFeedback: 'Avaliação',
      studentFeedback: 'O que achou da aula?',
      attendance: 'Presença',
      challenge: 'Desafio',
      studentBook: 'Material',
      teacherBook: 'Material do Professor',
      homework: 'Tarefa de casa',
      resources: 'Recursos',
    },
    homework: {
      success: 'Resposta enviada',
      notDone: 'Não fez',
      waitingFeedback: 'Aguardando correção',
      pending: 'Pendente',
      revised: 'Corrigido',
      submit: 'Enviar',
      feedback: {
        feedback: 'Correção',
        addComment: 'Adicionar comentário para o aluno',
        editComment: 'Editar comentário para o aluno',
        cancel: 'Cancelar',
        grade: 'Nota',
        save: 'Enviar para o aluno',
        submittion: 'Envio',
        notDone: 'Não fez',
        hasChangesTooltip: 'Existem mudanças que precisam ser salvas',
        saved: 'Correção salva com sucesso',
      },
      error: {
        internalError:
          'Ocorreu um erro ao tentar carregar a lição. Entre em contato com o suporte',
      },
    },
    noHomework: 'Não há tarefa de casa nessa aula',
    challengeView: {
      errorTextSubTitle: 'Verifique se você está na página correta',
      errorTextTitle: 'Desafio não encontrado',
      title: 'Desafio',
      visualized: 'Visualizado',
    },
    initialView: 'Progresso da aula',
    noContent: 'Esta aula ainda não tem um conteúdo',
    material: 'Material',
    feedback: {
      feedbackInformation:
        'A avaliação será utilizada pelo time de pedagógico para melhorar o curso, não esqueça de pedir também para o aluno avaliar a aula',
      comment: 'Comentário (opcional)',
      content: 'Conteúdo',
      save: 'Salvar',
      saveError:
        'Ops, ocorreu um erro ao tentar salvar. Tente novamente mais tarde ou entre em contato com o time de suporte',
      alertWarning: 'Adicione uma nota antes de salvar',
      saveSuccess: 'Avaliação salva com sucesso!',
      contentAndActivity: 'Conteúdo da aula e atividades',
      teacherExplanation: 'Explicação do professor',
    },
    studentMaterial: 'Material do Aluno',
    teacherMaterial: 'Material do Professor',
    activity: 'Atividades',
    challenge: 'Desafio',
    noneMaterial: 'Nenhum material disponível no momento',
    toolAccess: 'Acesso a Ferramenta',
    goBack: 'Voltar',
    errorMessage: {
      homeworkPhrase: 'Não foi possível verificar e trazer a lição de casa',
      progressPhrase: 'Não foi possível carregar o seu progresso',
    },
    resources: 'Recursos',
  },
  manageRecessCalendars: {
    manageRecessCalendars: 'Gerenciamento do Calendário de Recesso',
    recessCalendar: 'Calendário de Recesso',
    recess: 'Recesso de',
    calendar: 'Calendário',
    edit: 'Editar',
    save: 'Salvar',
    cancel: 'Cancelar',
    dates: 'Datas',
    successMessage: 'Alterações salvas com sucesso',
    errorMessage: 'Ocorreu um erro ao salvaras informações',
  },
  calendarPage: {
    calendar: 'Calendário',
    recess: 'Recesso',
    classCalendar: 'Calendário de Aulas',
    seeMore: 'ver mais',
    nextClasses: 'Próximas Aulas',
    pastClasses: 'Aulas Passadas',
    studentCalendarPhrase: 'Aqui você pode ver a data das próximas aulas 🗓️',
    noTeacher: 'Sem professor',
  },
  calendar: {
    classCalendar: 'CALENDÁRIO DE AULAS',
    seeMore: 'ver mais',
    student: {
      seeLess: 'ver menos',
      calendar: 'Calendário',
      upcomingClasses: 'PRÓXIMAS AULAS',
    },
    admin: {
      calendar: 'Calendário de Aulas',
      selectDates: 'SELECIONE AS DATAS',
      nextClasses: 'PRÓXIMAS AULAS',
      allUnits: 'Todas as Unidades',
      filters: {
        allLessons: 'todas as aulas',
        completedLessons: 'aulas concluídas',
        plannedLessons: 'aulas programadas',
      },
      noContent: {
        allLessons: 'Nenhuma aula para essa data',
        completedLessons: 'Nenhuma aula concluída nessa data',
        plannedLessons: 'Nenhuma aula programada para essa data',
      },
      statisticCard: {
        classes: 'AULAS',
        frequency: 'PRESENÇA',
        weekFrequency: 'PRESENÇA ÚLTIMOS 7 DIAS',
        monthFrequency: 'PRESENÇA ÚLTIMOS 30 DIAS',
      },
      subtitleCard: {
        classesCompleted: 'aulas concluídas',
        studentFrequency: 'presença de alunos',
      },
      manageKlass: {
        klassLink: 'Acessar link da aula',
        noKlassLink: 'Essa aula não possui link',
        report: 'Relatório',
        delayedReport: 'Relatório Atrasado',
        noKlassTooltip: 'Tente acessar pela tabela de turmas',
        teacher: 'Professor(a)',
        edit: 'editar',
        finish: 'finalizar',
        alter: 'alterar',
        alterLessonDate: 'alterar somente essa aula',
        attendance: 'Presença:',
        progress: 'Progresso:',
        accessKlass: 'acessar aula',
        alterLink: 'alterar link',
        anticipate: 'antecipar aulas',
        postpone: 'adiar aulas',
        noStudentsInClass: 'Sem alunos, apenas o administrador pode editar',
        manageAttendance: {
          attendances: 'Presenças:',
          absences: 'Faltas:',
          reschedulings: 'Remarcações:',
        },
        updateKlassLink: {
          updateKlassLinkTitle: 'Deseja adicionar o link para as aulas online?',
          success: 'Link atualizado com sucesso',
          error: 'Erro ao atualizar o link',
          placeholder: 'Insira o link do Google Meet',
          confirm: 'Confirmar',
          cancel: 'Cancelar',
        },

        updateDateKlass: {
          insertValidTime: 'Insira um horário válido',
          updateDateKlassTitle:
            'Tem certeza que deseja alterar a data desta aula?',
          selectDate: 'Selecione a nova data para a próxima aula:',
          confirmDate: 'Deseja confirmar a alteração?',
          success: 'Data alterada com sucesso',
          error: 'Erro ao alterar a data',
          confirm: 'Confirmar',
          cancel: 'Cancelar',
          or: 'ou',
          postponeAnticipateWarning: 'Para alterar a data de várias aulas, use',
          timezoneWarning: 'As datas estão no fuso horário de {{timezone}}',
          noAvailableDatetime: 'Não há datas disponíveis',
          updateOtherLessonDate:
            'Para alterar a data/hora dessa aula altere a data/hora da aula anterior ou da próxima aula',
        },
        updateTeacher: {
          updateTeacherTitle:
            'Tem certeza que deseja substituir o professor para esta aula?',
          success: 'Substituição feita com sucesso',
          error: 'Erro ao substituir o professor',
          placeholder: 'Selecione outro professor',
          confirm: 'Confirmar',
          cancel: 'Cancelar',
        },
        anticipatePostpone: {
          warningResults:
            'Esta será a nova data para a próxima aula, e todas as aulas seguintes terão suas datas atualizadas:',
          success: 'Datas alteradas com sucesso',
          confirm: 'Confirmar',
          cancel: 'Cancelar',
          noDateMessage: 'Não existem datas para essa ação',
          postpone: {
            title: 'Tem certeza que deseja adiar esta e as próximas aulas?',
            warningMessage: 'Deseja confirmar o adiamento?',
            errorMessage: 'Erro ao adiar aula',
          },
          anticipate: {
            title: 'Tem certeza que deseja antecipar esta e as próximas aulas?',
            warningMessage: 'Deseja confirmar a antecipação?',
            errorMessage: 'Erro ao antecipar aula',
          },
        },
        doNotAnticipateTooltip: 'Não é possível essa aula',
      },
    },
  },

  manageScheduledLessonReport: {
    presentStudent: 'Aluno presente',
    notifyLateless: 'Notificar atraso',
    studentEntered: 'O aluno já entrou?',
    missingRequiredFields:
      'É necessário preencher o conteúdo dado e o resumo da aula para marcar a aula como concluída',
    commentAlert:
      '[{{datetime}}] A notificação foi encerrada porque o professor marcou que o aluno entrou na aula.',
    notifySucess: 'Os administradores foram notificados sobre o atraso',
    latelessUpdated: 'Atraso atualizado',
    reportFromAnotherTeacherMessage:
      'Esta aula está atribuída a outro professor, não é possível preencher este relatório.',
    contactAdminMessage:
      'Caso você tenha dado essa aula peça ao administrador da unidade para alterar o professor desta aula.',
    hasNoPermission: 'Você não tem permissão para realizar essa alteração',
    cantEditReport: 'Você não pode editar esse relatório',
    publisInstructions:
      'Essa aula ainda não foi publicada. Acesse a página de publicação e finalize todas as mudanças',
    goEditPage: 'Acessar página de edição',
    emptyContent: 'Insira um conteúdo para a tarefa',
    cashMessage:
      'Os alunos presentes na aula receberão Ctrl+Cash quando a aula for marcada como concluída',
    title: 'Relatório de Conclusão da Aula',
    day: 'Dia',
    placeholderLessonComment: 'Faça observações sobre {{student}} nesta aula',
    notDone: 'Não fez',
    students: 'Alunos',
    presence: 'Presença',
    challenge: 'Desafio',
    grades: 'Notas',
    hasSubmittion: 'Possui entrega',
    hasHomeworkActivity: 'Não enviar tarefa de casa',
    noStudents: 'Nenhum aluno encontrado!',

    seeHomework: 'Ver entrega do aluno',
    submittion: 'Envio',
    noSubmittion: 'O aluno não respondeu a atividade.',
    hasDoneDesmark:
      'Apenas um administrador pode desmarcar esta aula como concluída',
    addGrade: 'Adicionar Nota',
    missedPresenceStatus: 'F',
    activeStudent: 'Ativar',
    savedActiveStudent: 'Aluno ativado com sucesso',
    isLockedDesc: 'Apenas um administrador pode realizar esta ação',
    homeworkNotPublishedDesc:
      'Adicione uma tarefa aqui e seus alunos irão recebê-la',
    homeworkPublishedDesc:
      'Tarefa de casa publicada, aguarde pela resposta dos alunos',
    annotationsDesc: 'Resumo da aula - O que aprendemos hoje',
    nonMandatoryPhrase: 'Não é obrigatório preencher todos os campos.',
    notHomeworkPhrase: 'Você ainda não adicionou uma tarefa para esta aula',
    notCtrlCashPhrase:
      'Esta aula já foi marcada como concluída e você não pode alterar o Ctrl+Cash',
    pending: 'Pendente',
    published: 'Publicada',
    noReports: 'Nenhum relatório encontrado!',
    draft: 'Rascunho',
    publish: 'Publicar',
    allValues: 'Todos os valores',
    homework: 'Tarefa de Casa',
    behavior: 'Comportamento',
    addComments: 'Adicionar Comentários',
    practicalChallenge: 'Desafio Prático',
    effort: 'Esforço',
    cancel: 'Cancelar',
    save: 'Salvar',
    reset: 'Redefinir',
    hasDone: 'Concluído',
    gotoLesson: 'Ir para a aula',
    selectStudentPhrase: 'Selecione um aluno ao lado, clicando em seu nome.',
    contentGiven: 'Conteúdo Dado',
    contentGiveOptions: {
      [ContentGivenEnum.GAVE_100_PERCENT_AND_MORE]:
        'Consegui dar o conteúdo todo e coisas a mais',
      [ContentGivenEnum.GAVE_100_PERCENT]: 'Dei 100% do conteúdo',
      [ContentGivenEnum.GAVE_UP_T0_50_PERCENT]: 'Eu dei até 50% do conteúdo',
      [ContentGivenEnum.GAVE_BETWEEN_50_99_PERCENT]:
        'Eu dei entre 50-99% do conteúdo',
    },
    selectAnOptionPhrase: 'Selecione uma opção',
    classNotes: 'Anotações sobre a aula:',
    canWritePhrase:
      'Você pode anotar onde parou na aula, ou qualquer outro detalhe.',
    homeworkDisplayedPhrase: 'A tarefa de casa será exibida para os alunos',
    addHomeworkPhrase: 'Adicione uma tarefa de casa para os alunos.',
    classCompleted: 'Marcar aula como concluída',
    classNotCompleted: 'Marcar aula como não concluída',
    studentHasNoComments: 'Esse estudante não possui comentários',
    saved: 'Salvo',
    savedScheduledLesson: 'Alterações na lição salvas',
    savedHomework: 'Tarefa de casa salva',
    savedGivenContent: 'Conteúdo dado salvo',
    savedReport: 'Relatório salvo',
    selectAValidOptionPhrase: 'Selecione uma opção válida',
    requestMessages: {
      studentPresenceConfimed: 'Presença do aluno salva',
      studentPresenceRemoved: 'Presença do aluno removida',
      studentAbsenceConfirmed: 'Falta do aluno confirmada',
      genericError: 'Ocorreu um erro interno. Entre em contato com o suporte',
    },
    absences: 'Faltas',
    presences: 'Presenças',
    classCompletedButtonPhrase: 'Aula concluída',
    notPossibleToChangePhrase:
      'Não é possível alterar a aula depois de finalizada',
    annotationsPlaceholder:
      'Você pode anotar onde parou na aula, ou qualquer outro detalhe.',
    homeworkGradeMarker: {
      homeworkPreviousButton: 'Tarefas da aula anterior',
      saved: 'Salvo',
    },
    active: 'Ativos',
    inactive: 'Inativos',
    transfered: 'Transferido',
    noActiveStudentPhrase: 'Nenhum aluno ativo no dia desta aula \\ (•◡•) /',
    noReportForActiveStudent: '{{student}} não possui relatório nesta aula',
    noActiveStudentsInKlass: 'Não há alunos ativos nesta turma',
    anotherKlassTag: 'Outra turma',
    selectStudentReport:
      'Selecione um aluno na lista para acessar seu relatório nesta área',
  },
  teacherDashboard: {
    hello: 'Olá,',
    visitor: 'Visitante',
    activeKlasses: {
      title: 'Turmas ativas',
      seeAll: 'Ver todas as turmas',
      today: 'Hoje',
      empty: 'Nenhuma turma ativa com aulas nesta semana',
      legend: {
        attendances: 'Chamadas',
        exams: 'Provas',
        requiresAttention: 'Requer atenção',
      },
    },
    yourLessons: {
      today: 'Hoje',
      nextLessons: 'Próximas aulas',
      empty: 'Você não tem aulas agendadas',
    },
    absenceReports: {
      newer: 'mais recente',
      older: 'mais antigo',
      openReport: 'Abrir Relatório',
      markAsRead: 'Marcar como lido',
      absenceReports: 'Relatório de Faltas',
      noAbsenceReports: 'Nenhuma falta',
      markAllAsRead: 'Marcar todas como lidas',
      allReadSuccessfuly: 'Notificações lidas com sucesso',
      klassNotFound: 'Turma não encontrada',
      groups: {
        klass: 'turma',
        teacher: 'professor',
        all: 'geral',
      },
      totalItems_one: '{{count}} item',
      totalItems_other: '{{count}} itens',
      lastLessonsHelpMessage: {
        default: 'Presente na {{lessonName}}',
        MISSED: 'Faltou a {{lessonName}}',
      },
      seeMore: 'ver mais',
    },
    teacherKlasses: {
      yourClasses: 'Suas turmas',
      informations: 'Informações',
      progress: 'Acompanhamento',
      classes: 'Aulas',
      noStudents: 'Sem alunos',
      noClassesPhrase: 'Por enquanto não existem turmas',
      noStudentsToFollow: 'Sem alunos para acompanhar',
      sheet: 'Ficha da turma',
      studentSheet: 'Ficha do aluno',
    },
    teacherReports: {
      pendingReports: 'Relatórios Pendentes',
      noReportsPhrase: 'Não existem relatórios pendentes',
    },
    pendingHomeworks: {
      homeworkForCorrection: 'Tarefas para Correção',
      noHomeworks: 'Nenhuma tarefa de casa para correção',
      klassNotFound: 'Turma não encontrada.',
    },
  },
  adminDashboard: {
    totalStudents: 'Total de Alunos',
    totalKlasses: 'Total de Turmas',
    totalTeachers: 'Total de Professores',
    churnCount: 'Churn',
    attendanceCount: 'Presença',
    studentsPerKlass: 'Alunos por Turma',
    studentsPerTeacher: 'Alunos por Professor',
    lastUpdated: 'Última atualização',
    allUnits: 'Todas as unidades',
    pendingReports: 'Relatórios pendentes',
  },
  componentPagination: {
    next: 'Próximo',
    prev: 'Anterior',
    totalOf: {
      students: 'Total de alunos:',
      klasses: 'Total de classes:',
      teachers: 'Total de professores:',
      courses: 'Total de cursos:',
      team: 'Total de membros:',
      units: 'Total de unidades:',
      hackathons: 'Total de hackathons:',
    },
    of: 'de',
    pages: 'páginas',
    page: 'página',
  },

  editMaterialPage: {
    legalInformation: 'Informações legais',
    save: 'Salvar',
    editMaterial: 'Editar Material',
    chapterTitle: 'Título do Capítulo',
    content: 'Conteúdo',
    chapters: 'Capítulos',
    placeholderEditor:
      'H2: Para seções dentro dos capítulos, utilize "Heading 2"',
    newChapterButton: 'Novo Capítulo',
    generateChapterButton: 'Gerar Capítulo',
    generateChapterContent: 'Gerar Conteúdo',
    saveButton: 'Salvar',
    cancelButton: 'Cancelar',
    cantModify: 'Não permitido na edição limitada',
    createChapterMessage: 'Capítulo adicionado',
    saveChapterMessage: 'Capítulo salvo com sucesso',
    saveOrderMessage: 'Nova ordem salva com sucesso',
    deletedChapterMessage: 'Capítulo deletado com sucesso',
    oneChapterMessage: 'O livro deve conter pelo menos um capítulo',
    uploadMaterial: 'Upload Material',
    createMaterial: 'Criar Material',
    createSlidesMaterial: 'Material em Slides',
    deleteMaterial: 'Excluir Material',
    bookIsBeingFormatedMessage: 'Aguarde enquanto o livro está sendo formatado',
    deleted: 'Material deletado com sucesso',
    updateSuccess: 'Alterações salvas com sucesso',
    autosave: 'Salvamento automático...',
    chapterFormattingFailureMessage:
      'Ocorreu um erro ao formatar este capítulo. Verifique se existem links, imagens corrompidas ou espaços vazios.',
  },

  rewardsView: {
    coinTooltip:
      'Você ganha moedas completando atividades, materiais, desafios e muito mais! As moedas não estão relacionadas ao tempo que você passa na plataforma',
    gemTooltip:
      'Você ganha gemas ao completar 100% uma lição ou finalizar alguns projetos! As gemas não estão relacionadas ao tempo que você passa na plataforma',
    xpTooltip:
      'Você ganha xp fazendo atividades, ganhando like em projetos, ganhando presenças nas aulas e muito mais! O xp não está relacionado ao tempo que você passa na plataforma',
    xp: '{{xp}} EXP',
    level: 'Nível {{lvl}}',
    errors: {
      genericError:
        'Não foi possível carregar as recompensas. Contacte o suporte',
    },
  },

  modalRewards: {
    attentionPhrase: 'Tem certeza que deseja adicionar ou sacar Ctrl+Cash?',
    rewardsButtons: {
      addCoins: 'adicionar moedas',
      withdrawCoins: 'sacar moedas',
    },
    inputPlaceholderPhrase: 'Digite qual foi a compra do aluno',
    inputPlaceholderValue: 'Valor',
    inputPlaceholderReason: 'Digite o motivo dessa transação',
    confirmWithdrawButton: 'Sacar',
    confirmDepositButton: 'Depositar',
    cancelButton: 'Cancelar',
    select: {
      book: 'Livro',
      activity: 'Atividade',
      challenge: 'Desafio',
      homework: 'Lição de casa',
      videoAccess: 'Acesso ao vídeo',
      programAccess: 'Acesso ao programa',
      lesson: 'Lição',
      minorProject: 'Projeto menor',
      majorProject: 'Projeto maior',
      behavior: 'Comportamento',
      effort: 'Esforço',
      presence: 'Presença',
      selectTheReason: 'Selecione o motivo da recompensa',
    },
    messages: {
      transactionSuccess: 'Transação feita com sucesso',
      transactionWarning: 'Preencha todos os campos',
      genericError: 'Ocorreu um erro ao buscar as recompensas',
    },
  },

  modalHomework: {
    addHomeworkAssessment: 'Adicione avaliação da tarefa de casa',
    notDone: 'Não fez',
    confirmButton: 'Confirmar',
    cancelButton: 'Cancelar',
  },

  modalEmptyActivity: {
    attentionPhrase: 'Atenção! Este curso possui atividade(s) vazias',
    deletePhrase:
      'Deseja excluir a(s) atividade(s) antes de publicá-lo ou continuar editando?',
    confirmButton: 'Deletar e Publicar',
    cancelButton: 'Cancelar',
    lesson: 'Aula',
    extraLesson: 'Aula Extra',
  },

  userForm: {
    firstName: 'Nome',
    lastName: 'Sobrenome',
    email: 'Email',
    dateOfBirth: 'Nascimento',
    username: 'Usuário',
    state: 'Estado',
    city: 'Cidade',
    school: 'Escola',
    unit: 'Unidade',
    phoneNumber: 'Telefone',
    klass: 'Turma',
    userType: 'Papel',
    status: 'Status',
    cancel: 'Cancelar',
    save: 'Salvar',
    saved: 'Salvo',
    userStatus: userStatus,
    errors: {
      required: 'Esse campo é requerido',
    },
  },
  projects: {
    page: {
      title: 'Projetos',
      newProject: 'Novo projeto',
      addProjectTitle: 'Criar projeto',
      updateProjectTitle: 'Editar projeto',
      ranking: 'Ranking hackathon',
      noProjectsFound: 'Nenhum projeto encontrado',
      addProject: 'Adicionar Projeto',
      all: 'Todos',
      highlights: 'Mais curtidos',
      viewed: 'Mais vistos',
      hackathon: 'Hackathon',
      recents: 'Novos',
      me: 'Meus projetos',
      filters: {
        allProjects: 'Todos',
        myProjects: 'Meus projetos',
        searchPlaceholder: 'Pesquisar projetos',
        mySchool: 'Minha escola',
        myClass: 'Minha turma',
        myCourse: 'Meu curso',
        period: {
          allTime: 'de todos os tempos',
          lastMonth: 'do último mês',
          lastWeek: 'da última semana',
        },
      },
    },
    carousel: {
      seeAll: 'Ver todos',
      back: 'Voltar',
    },
    error: {
      notHaveCourseFlag: 'O projeto precisa ter uma flag de curso',
    },
    card: {
      views: 'Visualizações',
      likes: 'Curtidas',
      mentor: 'Orientador',
      position: {
        rank_ordinal_one: '{{count}}º lugar',
        rank_ordinal_two: '{{count}}º lugar',
        rank_ordinal_few: '{{count}}º lugar',
        rank_ordinal_other: '{{count}}º lugar',
      },
      equip: 'Equipe',
      unit: 'Escola de ',
      actions: {
        delete: 'Excluir',
        edit: 'Editar',
        none: 'nenhuma',
        vote: 'Votar',
        rank: 'Posição',
        finalist: 'Finalista',
        unvote: 'Desvotar',
      },
    },
    form: {
      cancel: 'Cancelar',
      description: 'Descrição',
      projectUrl: 'Link do projeto',
      publishProject: 'Publicar projeto',
      selectTool: 'Selecionar ferramenta',
      title: 'Título',
      publishNewProject: 'Publicar novo projeto',
      projectUrlPlaceholder: 'Insira o link do seu projeto',
      titlePlaceholder: 'Insira o título do projeto',
      updateProject: 'Atualizar projeto',
      imageUploadFail: 'Ocorreu um erro ao fazer o upload de imagem:',
      isStudentProject: 'Está publicando projeto de um aluno?',
      studentSelectPlaceholder: 'Selecionar aluno',
      invalidLink: 'Link inválido',
      projectUrlRequired: 'Link do projeto é obrigatório',
      titleRequired: 'Título é obrigatório',
      toolRequired: 'Ferramenta é obrigatória',
      imageRequired: 'É obrigatório adicionar uma capa ao projeto',
      successAddedMessage: 'Projeto adicionado com sucesso',
      successUpdatedMessage: 'Projeto atualizado com sucesso',
    },
    fields: {
      document: 'Documento',
      imageOrVideo: 'Vídeo/Imagem',
      units: { label: 'Unidade', placeholder: 'Selecione a unidade' },

      publish: 'Criar projeto',
      update: 'Salvar projeto',
      title: { label: 'Título do projeto', placeholder: 'Insira o título' },
      projectUrl: {
        placeholder: 'Link do projeto',
        label: 'Importar',
        noMetadata:
          'Não conseguimos importar as informações do projeto, favor adicionar título e imagens manualmente',
      },
      video: {
        label: 'Vídeo',
        placeholder: 'Arraste ou clique para adicionar um vídeo',
        filename: 'Vídeo do projeto',
      },
      description: { label: 'Descrição', placeholder: 'Insira a descrição' },
      users: { label: 'Alunos', placeholder: 'Insira os alunos' },
      teacherMentor: {
        label: 'Orientador',
        placeholder: 'Insira o orientador',
      },
      tool: { label: 'Ferramenta', placeholder: 'Insira a ferramenta' },
      banner: {
        label: 'Banner',
        placeholder: 'Arraste ou clique para adicionar um banner',
        swapText: 'trocar banner',
      },
      documentation: {
        label: 'Documentação',
        placeholder: 'Arraste ou clique para adicionar um arquivo',
        filename: 'Documentação do projeto',
      },
      course: { label: 'Curso', placeholder: 'Insira o curso' },
      equipName: {
        label: 'Nome da equipe',
        placeholder: 'Insira o nome da equipe',
        maxLength: 'O nome da equipe deve ter no máximo {{max}} caracteres',
      },
      importYourProject: 'Antes de tudo, importe seu projeto',
      required: 'Campo obrigatório',
      isHackathon_false: 'É Hackathon?',
      isHackathon_true: 'É Hackathon? Não esqueça de adicionar:',
    },
    view: {
      classification: 'Classificação',
      classificationPhrase: 'Este projeto recebeu alguma classificação?',
      description: 'Descrição',
      projects: 'Projetos',
      checkInfo: 'Confira se você está na rota correta',
      projectNotFound: 'Projeto não encontrado',
      editProject: 'Editar',
      deleteProject: 'Excluir',
      views_one: '{{count}} visualização',
      views_other: '{{count}} visualizações',
      likes_one: '{{count}} curtida',
      likes_other: '{{count}} curtidas',
    },
  },

  inventory: {
    backpack: 'Mochila',
    save: 'Salvar',
    alterFail: 'Houve um problema ao salvar o avatar',
    alterSuccess: 'Alterações salvas com sucesso',
    removeItem: 'Remover item',
    goToShop: 'Ir à loja',
    purchaseSuccess: 'Compra realizada com sucesso',
    purchaseError: 'Ocorreu um erro ao tentar comprar o item',
    inventory: 'Inventário',
    shop: 'Loja',
    notEnoughCash: 'Sem fundos o suficiente',
    confirmPurchase: 'Tem certeza de que deseja comprar o item abaixo?',
    equipItem: 'Equipar Item',
    confirm: 'Confirmar',
    cancel: 'Cancelar',
    noItems: 'Nenhum item disponível',
    itemCategory: {
      base: 'Base',
      top: 'Cima',
      bottom: 'Baixo',
      accessories: 'Acessórios',
      equipment: 'Equipamento',
    },
    inventoryItems: {
      hair: 'Cabelo',
      base: 'Corpo',
      hat: 'Chapéu',
      head: 'Cabeça',
      face: 'Rosto',
      topFaceAcessory: 'Olhos',
      bottomFaceAcessory: 'Boca',
      neckAcessory: 'Pescoço',
      shirt: 'Camisa',
      coat: 'Casaco',
      pants: 'Calça',
      shoe: 'Sapato',
      hand: 'Mão',
      leftHandAcessory: 'Mão Esquerda',
      rightHandAcessory: 'Mão Direita',
    },
  },
  klassProgress: {
    myLessons: 'Minhas Aulas',
    lessonProgressList: {
      didNotFindLesson:
        'Não foi possível encontrar o progresso da lição. Fale com o suporte.',
      generalError:
        'Ocorreu um erro ao tentar carregar o progresso da aula. Fale com o suporte.',
      noLessons: 'Parece que não existem lições. Fale com o suporte.',
    },
  },

  nextLessonView: {
    noNextLesson: 'Parece que você está sem uma próxima aula agendada.',
    noTeacher: 'Professor não selecionado',
  },

  editActivity: {
    title: 'Enunciado:',
    subtitle: 'Subtítulo:',
    fields: {
      errors: {
        noTitle: 'A questão precisa de um enunciado',
      },
    },
  },

  klassInput: {
    placeholder: 'Selecione uma turma',
    warnings: {
      noResults: 'Sem resultados',
      noStudentsToFollow: 'Sem alunos para acompanhar',
    },
    errors: {
      internalError: 'Ocorreu um erro interno. Entre em contato com o suporte',
    },
  },

  rewardInput: {
    placeholder: 'Valor',
  },

  lessonForm: {
    name: 'Título da Aula',
    description: 'Descrição da Aula',
    message: 'Mensagem Motivacional',
    toolUrl: 'Link de acesso ao programa',
    links: 'Links',
    instructionsUrl: 'Instalar/acessar o programa',
    toolLinkMessage: 'Exemplo: https://scratch.mit.edu/',
    videoInstallMessage: 'Exemplo: https://www.youtube.com/watch?v=PWGX3QfAwCA',
    additHere: 'adiciona-lá aqui',
    saved: 'Alterações salvas com sucesso',
    tools: 'Ferramentas',
    topics: 'Conteúdo',
    toolOption: 'Opção de ferramenta',
    inBank: 'Adicionar ao Banco de Aulas',
    isPublicInBank: 'Público',
    normal: 'Normal',
    free: 'Livre',
    freeExplain:
      'Quando o conteúdo da aula é fora do currículo, aumentando a quantidade de aulas total do curso. Recomendado para eventos ou feriados.',
    normalExplain:
      'Quando precisa substituir uma ferramenta ou passar mais tempo para terminar um projeto. Você pode desativar outra aula para manter o total de 18 aulas.',
    addAuxiliaryLinks: 'Adicionar links',
    warnings: {
      required: 'Esse campo é requerido',
      validateLink: 'Digite um link válido',
      mainTool: 'Selecione uma ferramenta principal',
    },
    toolPhrase: 'Caso a ferramenta não esteja na lista, pode',
    addToolTitle: 'Adicionar Ferramenta',
  },

  enrollmentStatus: {
    ACTIVE: 'Ativo',
    AWAITING_CLASS: 'Aguardando turma',
    AWAITING_PAYMENT: 'Aguardando pagamento',
    AWAITING_PAYMENT_admin: 'Aguardando pagamento',
    AWAITING_PAYMENT_teacher: 'Matrícula em processamento',
    COURSE_TRANSFERRED: 'Transferido de curso',
    CLASS_TRANSFERRED: 'Transferido de turma',
    CONCLUDED: 'Formado',
    CANCELED: 'Cancelado',
    TRANSFERRED_TO_CLASS: 'Transferido para essa turma',
    TRANSFERRED_FROM_CLASS: 'Transferido para outra turma',
    CANCELED_IN_FUTURE: 'Cancelado em aulas futuras',
    TRANSFERED_IN_FUTURE: 'Transferido em aulas futuras',
  },

  tagInput: {
    tool: 'Ferramentas',
    mainToolBanner: 'Definir como capa',
    editTag: 'Editar tag',
    banner: 'Capa',
  },

  tagForm: {
    name: 'Nome',
    image: 'Imagem',
    link: 'Link de acesso',
    color: 'Cor',
    clear: 'Limpar',
    confirm: 'Confirmar',
    warnings: {
      required: 'Esse campo é requerido',
      validateLink: 'Digite um link válido',
    },
    editSaved: 'Alterações salvas com sucesso',
    addSaved: 'Tag criada com sucesso',
  },

  colorInput: {
    color: 'Cor',
    clear: 'Limpar',
    confirm: 'Confirmar',
    warnings: {
      validateHexadecimalCode: 'Digite uma cor válida',
    },
  },

  listLessons: {
    successUpdateOrder: 'Ordem de aulas atualizada',
    warnings: {
      noCourse:
        'Não foi posssível carregar o curso. Tente novamente mais tarde ou entre em contato com o time de suporte',
    },
  },

  coursesTab: {
    warnings: {
      noCoursesPath: 'Nenhum curso encontrado',
    },
  },

  klassStatus,

  periods: {
    [KlassPeriodEnum.MORNING]: 'Manhã',
    [KlassPeriodEnum.AFTERNOON]: 'Tarde',
    [KlassPeriodEnum.EVENING]: 'Noite',
  },

  userTypes: {
    [UserTypeEnum.STUDENT]: 'Estudante',
    [UserTypeEnum.TEACHER]: 'Professor',
    [UserTypeEnum.SUPER_ADMIN]: 'Super Administrador',
    [UserTypeEnum.UNIT_ADMIN]: 'Administrador da Unidade',
    [UserTypeEnum.PARENT]: 'Parente',
    [UserTypeEnum.CONTENT_CREATOR]: 'Conteudista',
  },

  courseType,

  userStatus: {
    REGISTRED: 'Registrado',
    ACTIVE_ENROLLMENT: 'Matrícula Ativa',
    INACTIVE: 'Inativo',
    WAITING_ENROLLMENT: 'Aguardando Matrícula',
  },
  ckeditor: {
    placeholder: 'Escreva aqui...',
    iframeBlocked: 'Iframe de "{{host}}" não é permitido e foi removido.',
  },

  audioPlayer: {
    play: 'Reproduzir',
    pause: 'Pausar',
    progress: 'Progresso do áudio',
  },

  pedagogicalSheet: { ...sheet, title: 'Ficha Pedagógica' },

  scrollControl: {
    upButtonTooltip: 'Ir ao topo',
    downButtonTooltip: 'Ir ap final',
  },

  commentAssessmentForm,

  occurrenceAssessmentForm,

  studentAttentionRating: {
    1: 'Demonstra dificuldade significativa de atenção, com pouca resposta mesmo após estímulos e redirecionamentos.',
    2: 'Apresenta dificuldade frequente para manter o foco, exigindo intervenções constantes.',
    3: 'Mantém atenção em atividades curtas ou mais estimulantes, mas dispersa-se com facilidade em tarefas mais longas.',
    4: 'Mantém atenção na maior parte das atividades, com poucas dispersões.',
    5: 'Mantém atenção de forma consistente durante toda a aula, mesmo em atividades mais complexas.',
  },

  studentLearningRating: {
    1: 'Demonstra pouca assimilação dos conteúdos, mesmo com intervenções, necessitando apoio individualizado.',
    2: 'Apresenta dificuldades frequentes e demanda constante reforço e adaptações.',
    3: 'Assimila conteúdos com ajuda de exemplos e retomadas pontuais.',
    4: 'Assimila bem os conteúdos, com autonomia parcial e bom rendimento.',
    5: 'Assimila com facilidade, aplica os conteúdos com autonomia e criatividade.',
  },

  studentInterestRating: {
    1: 'Demonstra pouco interesse pelas atividades e raramente participa de forma espontânea.',
    2: 'Participa apenas quando estimulado, com pouca iniciativa própria.',
    3: 'Demonstra interesse pontual, participando com mais envolvimento em temas específicos.',
    4: 'Participa ativamente da maioria das propostas, com boa disposição.',
    5: 'Demonstra entusiasmo e iniciativa frequente, buscando aprender além do proposto.',
  },

  studentSocializationRating: {
    1: 'Demonstra dificuldade acentuada de interação, podendo isolar-se ou gerar conflitos.',
    2: 'Relaciona-se com poucos colegas e necessita mediação frequente.',
    3: 'Interage de forma funcional, mas com algumas dificuldades em atividades em grupo.',
    4: 'Relaciona-se bem com os colegas, colaborando na maioria das interações.',
    5: 'Interage com empatia, coopera com o grupo e contribui positivamente para o clima da turma.',
  },

  studentOrganizationRating: {
    1: 'Demonstra grande dificuldade em manter materiais e tarefas organizados, afetando a aprendizagem.',
    2: 'Precisa de lembretes e acompanhamento frequente para manter organização mínima.',
    3: 'Apresenta organização básica, mas com lapsos ocasionais.',
    4: 'Mantém seus materiais e atividades organizados com autonomia parcial.',
    5: 'É altamente organizado(a), com responsabilidade e autonomia na gestão de suas tarefas.',
  },

  klassSheet: { ...sheet, title: 'Ficha da Turma' },

  assessmentHeader: sheet,

  behaviorAssessmentForm,

  presenceStatus,

  klassEnergyRating: {
    1: 'Turma quieta',
    2: 'Turma com boa autorregulação',
    3: 'Turma com necessidade de estratégias de autorregulação',
  },

  klassSpeedRating: {
    1: 'Turma com necessidade de mais tempo para consolidação dos conteúdos',
    2: 'Turma com ritmo compatível com os objetivos propostos',
    3: 'Turma com autonomia para avançar no conteúdo',
  },

  klassMaturityRating: {
    1: 'Turma infantil',
    2: 'Turma normal para a idade',
    3: 'Turma madura',
  },

  courseFormats,

  businessModels,

  courseLineCategory,

  courseLineSubCategory,

  buttonInfiniteList: {
    loadMore: 'Carregar mais',
  },

  consentModal: {
    message:
      'Usamos cookies e local storage para melhorar sua experiência de navegação.',
    accept: 'Aceitar',
  },
  topics: {
    pageTitle: 'Tópicos',
    topbar: {
      new: 'Criar novo',
      newArea: 'Nova área',
      newTag: 'Nova tag',
      save: 'Salvar',
      searchPlaceholder: 'Pesquisar por áreas',
    },
    tree: {
      createNew: 'Crie uma área',
    },
    workspace: {
      selectArea: 'Selecione uma área',
    },
    moveTo: {
      cancel: 'Cancelar',
      move: 'Mover',
      placeholder: 'Pesquisar área',
      currentArea: 'área atual',
      label: 'Mover para',
      transformRoot: 'transformar em tópico pai',
    },
    input: {
      searchForTopics: 'Pesquise por tópicos',
      label: 'Tópicos',
    },
    tooltip: {
      move: 'Mover área',
      renaming: 'Renomear',
      delete: 'Deletar',

      area: {
        title: 'Área',
        desc: 'Áreas podem conter tags ou outras áreas',
      },
      tag: {
        title: 'Tag',
        desc: 'Tags são marcadores de conteúdo que adicionam dificuldade e nível em uma área',
      },
    },
    tags: {
      description: {
        label: 'Descrição',
        required: 'A descrição é obrigatória',
      },
      difficulty: {
        label: 'Dificuldade',
        values: {
          ADVANCED: 'Avançado',
          INTERMEDIATE: 'Intermediário',
          BEGINNER: 'Iniciante',
        },
      },
      level: {
        label: 'Nível',
        values: { 1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V' },
      },
      add: 'Adicionar',
      cancel: 'Cancelar',
      addDifficulty: 'Adicionar dificuldade',
      addContent: 'Adicionar conteúdo',
    },
  },
  prompts: {
    pageTitle: 'Gerenciamento de AI',
    sidebarTitle: 'Prompts',
    workspace: {
      selectPrompt: 'Selecione um prompt',
    },
    form: {
      instructionLabel: 'Instruções',
      roleLabel: 'Papel',
      filesLabel: 'Anexar arquivos de contexto',
      filesInfo:
        'Os arquivos ficarão disponíveis para a IA acessar e melhorar a base de conhecimento tanto do prompt pai quanto dos prompts pertencentes à ele',
      instructionInfo:
        'As instruções são todas as informações que a IA precisa ter para criar o que você quer da melhor forma. Considere -a como um estagiário novo no trabalho e todas as instruções que você passaria para ele. Quanto mais detalhes, exemplos, etc, melhor será o resultado',
      roleInfo:
        'O papel é como o cargo que a IA deverá exercer especialmente para esse prompt. Pense, que tipo de pessoa ou profissão estaria fazendo esse trabalho? Quanto mais específico você for, mais sucesso terá. Por exemplo "Atue como um professor especializado em criar atividades educativas e altamente engajantes de robótica e programação"',
      save: 'Salvar',
      cancel: 'Cancelar',
      success: 'Alterações salvas com sucesso',
    },
    history: {
      title: 'Histórico de versões',
      latest: 'atual',
      version: 'v{{version}}',
      noHistory: 'Nenhum histórico disponível',
      loadingHistory: 'Carregando histórico...',
    },
    comparator: {
      currentVersion: 'Versão atual',
      selectedVersion: 'Versão {{version}}',
      restore: 'Restaurar',
      restoreSuccess: 'Versão restaurada com sucesso',
      restoreError: 'Erro ao restaurar versão',
    },
    categories: {
      [PromptCategoryEnum.QUIZ]: {
        name: 'Quiz',
        description: 'Geração de questões de múltipla escolha',
      },
      [PromptCategoryEnum.CODE]: {
        name: 'Código',
        description: 'Geração de atividades de código',
      },
      [PromptCategoryEnum.TRUE_OR_FALSE]: {
        name: 'Verdadeiro ou Falso',
        description: 'Geração de questões de verdadeiro ou falso',
      },
      [PromptCategoryEnum.CODE_FIX]: {
        name: 'Correção de Código',
        description: 'Correção e análise de código',
      },
      [PromptCategoryEnum.COMPLETE_ACTIVITY]: {
        name: 'Atividade Completa',
        description: 'Geração de atividades completas',
      },
      [PromptCategoryEnum.LESSON_BLOCK]: {
        name: 'Bloco de Aula',
        description: 'Geração de blocos de conteúdo para aulas',
      },
      [PromptCategoryEnum.LESSON_MATRIX]: {
        name: 'Matriz Detalhada',
        description: 'Geração de matriz detalhada de aulas',
      },
      [PromptCategoryEnum.OBJECTIVE]: {
        name: 'Objetivos',
        description: 'Utilizado na criação de objetivos para o curso',
      },
      [PromptCategoryEnum.IMAGE_GENERATION]: {
        name: 'Gerador de Imagem',
        description: 'Geração de imagens para material em slides',
      },
      [PromptCategoryEnum.COURSE_CREATION]: {
        name: 'Criação de Curso',
        description: 'Assistente para criação de cursos',
      },
      [PromptCategoryEnum.SLIDE_MATERIAL]: {
        name: 'Material em Slides',
        description: 'Geração de material em slides',
      },
      [PromptCategoryEnum.ACTIVITY_CREATION]: {
        name: 'Criação de Atividades',
        description: 'Assistente para criação de atividades',
      },
      [PromptCategoryEnum.LESSON_CONTENT]: {
        name: 'Conteúdo da Aula',
        description: 'Geração do conteúdo dos capítulos da aula',
      },
    },
  },
  wellcome: {
    hello: 'Olá, {{firstName}} 👋',
    student:
      'Bem vindo de volta, tripulante! Pronto para continuar sua jornada? 🚀',
  },
  warning: {
    addAndEditClass:
      'Atenção! Não é possível adicionar ou editar turmas pelo Portal, somente pela Loja',
  },

  weekday,

  klassType: {
    REGULAR: 'Turma Regular',
    CONECTA: 'Turma Conecta',
    SCHOOL: 'Turma na Escola',
  },

  hideComponent: {
    options: {
      hiddenSection: 'Ocultar seção',
    },
  },

  hiddenComponents: {
    title: 'Seções ocultas',
    activate: 'Ativar',
  },

  modalHiddenComponents: {
    header: {
      title: 'Você ocultou uma seção da sua dashboard!',
      paragraph:
        'Não se preocupe, se quiser ver ele novamente, é só ativá-lo na lista de seções ocultas.',
    },
    footer: {
      paragraph: 'Você consegue encontrar essa lista no final da sua dashboard',
      button: 'Entendi!',
    },
  },
  contacts: {
    noContacts: 'O usuário não possui telefones de contato',
    student: 'Aluno',
    sendMessage: 'Enviar mensagem',
  },
  alerts: {
    title: 'Notificações',
    page: {
      inbox: 'Caixa de Entrada',
      filters: 'Filtros',
      alert:
        'Você está no modo visualização. Você não tem permissão para fazer edições.',
    },
    detail: {
      delay: {
        error: 'Não foi possivel buscar os detalhes da notificação',
      },
    },
    list: {
      empty: 'Nenhuma notificação',
      totalOf: 'Total de notificações: {{count}}',
    },
    buttons: {
      contact: 'Contatos',
      copyLink: 'Copiar link do meet',
      comment: {
        add: 'Adicionar comentário',
        edit: 'Editar comentário',
      },
      fullScreen: 'Tela cheia',
      copyMessage: 'Copiar mensagem',
    },
    inputs: {
      commentPlaceholder: 'Digite seu comentário',
    },
    filters: {
      status: {
        all: 'todos',
        yesterday: 'ontem',
        today: 'hoje',
        chooseDate: 'escolher data',
      },
      newest: 'mais recentes',
      oldest: 'mais antigas',
      type: 'Tipo',
      unit: 'Unidade',
      klass: 'Turma',
      onlyPendings: 'Apenas pendentes',
      teacher: 'Professor',
    },
    verbs: {
      [AlertTypeEnum.LATENESS]: 'ainda não entrou na aula',
      ABSENCE_one: 'faltou em uma aula',
      ABSENCE_other: 'faltou em {{count}} aulas seguidas!',
      [AlertTypeEnum.HOMEWORK_NOT_DONE]: 'não fez a tarefa de casa',
      [AlertTypeEnum.OCCURRENCE]: 'recebeu uma ocorrência',
    },
    types: {
      [AlertTypeEnum.LATENESS]: 'atraso',
      [AlertTypeEnum.ABSENCE]: 'falta',
      [AlertTypeEnum.HOMEWORK_NOT_DONE]: 'tarefa de casa',
      [AlertTypeEnum.OCCURRENCE]: 'ocorrência',
    },
    messages: {
      latelessBody:
        'Notamos que o (a) {{studentName}} ainda não entrou na {{lessonName}} de hoje que começou às {{lessonDate}}.',
      absenceBody:
        'Notamos que o (a) {{studentName}} faltou na {{lessonName}} do dia {{lessonDate}}.',
      homeworkNotDoneBody:
        'Notamos que o (a) {{studentName}} não fez a tarefa de casa da aula {{lessonName}}.',
      occurrenceBody: 'Recebemos uma notificação sobre o(a) {{studentName}}',
      link: 'Você pode acessar a aula através desse link: {{link}}.',
      simpleBody:
        'Notamos que o (a) {{studentName}} não entrou na aula de hoje.',
      presentation: 'Olá, tudo bem? Aqui é o (a) {{sender}}.',
      footer: 'Obrigado pela atenção!',
      question: 'Pode nos informar o motivo?',
    },
  },
  hiddenComponent: {
    todayLessons: 'Aulas de hoje',
    teachingWeek: 'Turmas',
    yourLessons: 'Suas aulas',
  },

  klassesFilters: {
    courseFormats: 'Formato',
    courseLineId: 'Cursos',
    dayOfWeek: 'Dia',
    otherFilters: 'Outros filtros',
  },

  weekdayKlassCell: {
    termination: 'Término',
  },

  nextLessonKlass: {
    warning: 'Sem aulas ativas.',
  },

  klassPopup: {
    seeMore: 'Ver mais',
    copyNames: 'Copiar todos os nomes',
    copyEmails: 'Copiar todos os e-mails',
  },

  useKlassOption: {
    info: 'Informações',
    lessons: 'Lista de aulas',
    progress: 'Progresso',
    sheet: 'Ficha da turma',
    members: 'Membros da turma',
  },

  updates: {
    chatbot: {
      title: 'Agora você pode testar nosso novo assistente IA!',
      description:
        'O seu curso está disponível para testar um chatbot com inteligência artificial para te ajudar a estudar o material, tirar dúvidas, entre outros! Você pode acessar o chatbot diretamente no seu material clicando no botão roxo.',
    },
    hackathon: {
      title: 'Começou o hackathon!',
      description:
        'Comece a participar do hackathon e tenha a possibilidade de ganhar prêmios incríveis!',
      button: 'Ver hackathon',
    },
    terms: {
      title: 'TERMOS DE USO E SERVIÇOS',
      description:
        'Por favor, leia cuidadosamente os nossos termos de uso antes de prosseguir.',
    },
    changeKlassStartDate: {
      title: 'Data de início da turma',
      description:
        'Agora os administradores podem mudar a data de inicio da turma por aqui',
    },
    notifications: {
      description:
        'Agora você pode receber notificações administrativas sobre os alunos e as turmas e rapidamente entrar em contato com os responsáveis. Por enquanto, temos notificações de faltas e atrasos dos alunos, com mais melhorias prestes à serem adicionadas!',
      title: 'Notificações',
    },
    notifyLatelessButton: {
      description:
        'Agora você pode notificar a secretaria diretamente que o aluno ainda não chegou',
      title: 'Atraso: Relatório de Conclusão da Aula',
      secondDescriptionState:
        'Se o aluno entrar você pode encerrar a notificação para evitar que os responsáveis sejam cobrados',
      lastDescriptionState:
        'Comentários que a secretaria fizer também ficam disponíveis para você ler',
    },
    newDashProf: {
      description:
        'Agora você pode ver a lista das aulas que você tem pra hoje e também o relatórios que estão pendentes no mesmo local',
      title: 'Fizemos alterações para simplificar sua dashboard!',
    },
    todayLessons: {
      description:
        'Agora você pode visualizar quais aulas estão programadas para o dia de hoje. Suas aulas de hoje podem ser filtradas por passadas, em andamento e futuras',
      title: 'Você pode ver as aulas de hoje!',
    },
    notificationsPageTeacher: {
      description:
        'Para acompanhar as faltas dos alunos, você pode abrir a aba de "Notificações" e ver faltas, atrasos, tarefas de casa, entre outros',
      title: 'Aba de notificações',
    },

    vacationNotice: {
      title: 'Agora você pode criar avisos de férias para os alunos!',
      description: 'Você pode criar avisos na última ação da tabela de alunos',
      secondDescriptionState:
        'Quando um aviso é criado, uma tag é exibida nas aulas ao qual aquele aluno estará ausente, na página de progresso da turma e relatório da aula',
    },
    gotIt: 'Entendi',
  },

  timeframeFilter: {
    ALL: 'Todas',
    ONGOING: 'Em andamento',
    PAST: 'Passadas',
    UPCOMING: 'Futuras',
  },

  relationships: {
    FATHER: 'Pai',
    MOTHER: 'Mãe',
    BROTHER: 'Irmão',
    SISTER: 'Irmã',
    UNCLE: 'Tio',
    AUNT: 'Tia',
    GRANDFATHER: 'Avô',
    GRANDMOTHER: 'Avó',
    OTHER: 'Responsável',
  },

  filters: {
    ...courseFormats,
    ...courseType,
    ...weekday,
    ...klassStatus,
    startSoon: 'Início próximo',
    endSoon: 'Término próximo',
    hasActiveLessons: 'Possui aulas ativas',
  },

  vacationNotice: {
    title: 'Aviso de férias',
  },

  klassGroupButton: {
    tooltip: 'A turma não possui um link para o grupo',
    textButton: 'Grupo da turma',
  },
  editorJs: {
    ui: {
      blockTunes: {
        toggler: {
          'Click to tune': 'Clique para ajustar',
          'or drag to move': 'ou arraste para mover',
        },
      },
      toolbar: {
        toolbox: {
          Add: 'Adicionar',
        },
      },
      popover: {
        'Convert to': 'Converter para',
        Filter: 'Filtrar',
        'Nothing found': 'Nada encontrado',
      },
    },

    toolNames: {
      Text: 'Parágrafo',
      Heading: 'Título',
      List: 'Lista',
      Warning: 'Aviso',
      'Nested list': 'Lista Encadeada',
      'Unordered List': 'Lista Não Ordenada',
      'Ordered List': 'Lista Ordenada',
      Quote: 'Citação',
      Code: 'Código',
      Image: 'Imagem',
      Underline: 'Sublinhado',
      Delimiter: 'Delimitador',
      'Raw HTML': 'HTML Bruto',
      Table: 'Tabela',
      Link: 'Link',
      Marker: 'Marcador',
      Bold: 'Negrito',
      Italic: 'Itálico',
      InlineCode: 'Código em Linha',
      Código: 'Código',
    },

    tools: {
      convertTo: {
        'Convert to': 'Converter para',
      },
      warning: {
        Title: 'Título',
        Message: 'Mensagem',
      },

      link: {
        'Add a link': 'Adicionar um link',
      },
      linkTool: {
        Link: 'Link',
        "Couldn't fetch the link data": 'Não foi possível obter os dados',
        "Couldn't get this link data, try the other one":
          'Não foi possível obter os dados dessa link, tente outra',
        'Wrong response format from the server': 'Problemas no servidor',
      },

      stub: {
        'The block can not be displayed correctly.':
          'O bloco não pode ser exibido corretamente.',
      },
      image: {
        Caption: 'Legenda',
        'Select an Image': 'Selecione um arquivo',
        'With border': 'Com borda',
        'Stretch image': 'Esticar',
        'With background': 'Com fundo',
      },
      activityBlock: {
        Reset: 'Redefinir',
        Edit: 'Editar',
        'Create activity': 'Criar atividade',
        'Select activity': 'Atividade do banco',
      },
      code: {
        Reset: 'Resetar',
        Run: 'Rodar',
      },
      imageGenerator: {
        Prompt: 'Prompt',
        'Type your prompt': 'Digite seu prompt',
        'Generate image': 'Gerar imagem',
        'Enter your prompt and generate the image':
          'Digite seu prompt e gere a imagem',
      },
      table: {
        'Add column to left': 'Adicionar coluna à esquerda',
        'Add column to right': 'Adicionar coluna à direita',
        'Delete column': 'Excluir coluna',
        'Add row above': 'Adicionar linha acima',
        'Add row below': 'Adicionar linha abaixo',
        'Delete row': 'Excluir linha',
        'With headings': 'Com cabeçalho',
        'Without headings': 'Sem cabeçalho',
        Stretch: 'Preencher',
      },
      header: {
        'Heading 1': 'Titulo 1',
        'Heading 2': 'Titulo 2',
        'Heading 3': 'Titulo 3',
        'Heading 4': 'Titulo 4',
        'Heading 5': 'Titulo 5',
        'Heading 6': 'Titulo 6',
      },
      nestedList: {
        Ordered: 'Ordenado',
        Unordered: 'Não Ordenado',
      },
    },

    blockTunes: {
      imageTune: {
        Crop: 'Recortar',
        Apply: 'Aplicar',
        Resize: 'Redimensionar',
      },
      delete: {
        Delete: 'Excluir',
        'Click to delete': 'Confirmar',
      },
      moveUp: {
        'Move up': 'Mover para cima',
      },
      moveDown: {
        'Move down': 'Mover para baixo',
      },
    },
    placeholders: {
      header: 'Digite um título',
      paragraph: 'Digite um texto',
      rawHtml: 'Insira o código HTML',
    },
    label: {
      imageGenerator: 'Digite o prompt abaixo',
    },
  },

  codeEditor: {
    run: 'Rodar',
    reset: 'Resetar',
    notStarted: 'Compilador não iniciado',
    validateCode: 'Validar código',
  },

  section: {
    notDeleteUniqueSection: 'O capitulo deve ter ao menos uma seção',
    title: 'Nova seção',
    form: {
      save: 'salvo',
      deleteSection: 'Excluir seção',
      startsHidden: 'Iniciar oculto',
      startsVisible: 'Iniciar visivel',
      title: {
        required: 'O titulo é obrigatório',
      },
    },
    quizActivityForm: {
      createTitle: 'Crie uma atividade de quiz',
      editTitle: 'Edite a atividade de quiz',
      question: 'Enunciado',
      content: 'Conteúdo',
      answers: 'Respostas',
      newAnswer: 'Nova resposta',
      explain: 'Explicação da resposta correta',
      name: 'Nome',
      minAnswer: 'É necessário ter no mínimo 2 respostas',
      onlyOneCorrectAnswer: 'Apenas uma resposta pode ser marcada como correta',
      atLeastOneCorrectAnswer: 'É necessário marcar uma resposta como correta',
    },
  },
  book: {
    form: {
      title: 'Material em slides',
      view: 'Visualizar',
      edit: 'Editar',
      delete: 'Excluir',
      cantDeleteBook: 'Não foi possível deletar o livro',
    },
  },
  chapter: {
    title: 'Capítulo',
    notDeleteUniqueChapter: 'O livro deve ter ao menos um capitulo',
    list: {
      title: 'Conteúdo',
      add: 'adicionar',
    },
    layout: {
      layout1: 'Layout 1',
      layout2: 'Layout 2',
      layout3: 'Layout 3',
      custom: 'Personalizado',
      finish: 'Finalizar',
    },
    form: {
      finishLayout: 'Finalizar layout',
      editLayout: 'Editar layout',
      addSection: 'Adicionar seção',
      deleteSubchapter: 'Excluir subcapitulo',
      title: {
        required: 'O titulo é obrigatório',
      },
    },
    successDeleting: 'Capítulo excluído com sucesso',
    successUpdated: 'Capítulo atualizado com sucesso',
  },
  subchapter: {
    title: 'Subcapítulo',
    notDeleteUniqueSubchapter: 'O capitulo deve ter ao menos um subcapítulo',
  },

  codeEditorLanguage: {
    JAVASCRIPT: 'JavaScript',
    PYTHON: 'Python',
  },

  codeEditorForm: {
    initialCode: 'Código base (opcional)',
    expectedOutput: 'Resultado esperado',
    addTestCase: 'Adicionar',
    removeTestCase: 'Remover',
    input: 'Valor(es) a ser(em) inserido(s)',
    testCase: 'Teste {{count}}',
    testCases: 'Casos de teste',
    solutionCode: 'Código de solução',
    validate: 'Validar',
    valid: 'Válido',
    invalid: 'Inválido',
    invalidCode:
      'Os testes falharam! Confira os casos de testes e o código de solução',
    validating: 'Aguarde, validando os testes',
    testCasesInfo: {
      tooltip: 'Informações sobre casos de teste',
      inputExplain:
        'Os dados que serão fornecidos ao código do estudante para processamento.',
      singleInput: '• Para um valor: coloque apenas o valor (ex: 5)',
      multipleInput:
        '• Para múltiplos valores: coloque cada valor em uma linha separada',
      expectedOutputExplain:
        'O resultado correto que o código deve produzir quando recebe a entrada. Usado para verificar se a solução do estudante está funcionando corretamente.',
      howToReadInput: {
        title: 'Como ler entrada no código ({{language}}):',
        explain:
          'Para que os casos de teste funcionem, o código deve usar {{inputMethod}} para ler os dados.',
        example: {
          singleInput: 'Exemplo - Um valor:',
          multipleInput: 'Exemplo - Múltiplos valores:',
        },
      },
    },
  },

  resourcePreview: {
    changeResource: 'Trocar {{resource}}',
  },

  modalEditResource: {
    title: 'Alerar {{resource}} do recurso',
  },

  resourceEditor: {
    isMandatory: 'É obrigatório?',
    onlyTeacher: 'Somente professores?',
  },

  resourceActivities: {
    activityAlreadyIncluded: 'Atividade já inclusa no recurso',
  },
  hackathon: {
    votingSoon: 'A etapa de votações se inicia em breve',
    votingClosed: 'A etapa de votações foi encerrada',
    banner: {
      tematic: 'Temática',
      winnersPhrase: 'Os 3 primeiros colocados ganharão prêmios incríveis!',
      liveStream: 'Acesse a transmissão ao vivo no dia da final',
      liveStreamLink: 'clicando aqui',
      recordLiveStream: 'Assista a final do Hackathon {{edition}}',
    },
    form: {
      hackathon: {
        successCreatedMessage: 'Hackathon criado com sucesso',
        successUpdatedMessage: 'Hackathon editado com sucesso',
        required: 'Campo obrigatório',
        edition: {
          label: 'Edição (ano)',
          placeholder: 'Insira a edição',
          numberValidate: 'Esse campo deve ser um número',
        },
        theme: {
          label: 'Tema',
          placeholder: 'Insira o tema',
        },
        content: {
          label: 'Conteúdo',
          placeholder: 'Insira o conteúdo',
        },
        banner: {
          label: 'Banner',
        },
      },
      hackathonStages: {
        successCreatedMessage: 'Estágio do hackathon criado com sucesso',
        successUpdatedMessage: 'Estágio do hackathon editado com sucesso',
        type: {
          label: 'Tipo',
          placeholder: 'Selecione o tipo',
        },
      },
    },
    building: 'Em contrução',
    steps: 'Etapas:',
    shunfle: 'Embaralhar',
    noHackathon: 'Parece que não existe um hackathon acontecendo',
    countdown: {
      finishAt: 'finaliza em:',
      startAt: 'começa em:',
    },
    stages: {
      [StageTypeEnum.SUBMISSION]: 'Submissão',
      [StageTypeEnum.FINAL_VOTING]: 'Final',
      [StageTypeEnum.INITIAL_VOTING]: 'Semifinal',
    },
  },

  dayOfWeek: weekday,

  studentType,

  codeValidation: {
    passedValues: 'Valores passados',
    expect: 'Esperava',
    returned: 'Retornou',
    test: 'Teste',
    passed: 'Passou',
    failed: 'Falhou',
    notStdin: 'Atenção! Seu código não possui nenhum input',
    runTests: 'Rodar testes',
    beforeDeliveringActivity:
      'Antes de entregar a atividade, você pode conferir se o seu código atende aos requisitos da tarefa',
  },

  feedbackAuthor: {
    showAuthor: 'Ver autor',
    hideAuthor: 'Ocultar autor',
  },
  updateNotification: {
    message: 'Nova versão disponível. Deseja atualizar?',
    update: 'Atualizar',
  },

  courseStudio: {
    name: 'Nome',
    type: 'Tipo',
    format: 'Formato',
    abbreviation: 'Sigla',
    duration: 'Duração',
    courseLine: 'Linha do Curso',
    courseModule: 'Módulo',
    lessonsQuantity: 'Número de aulas',
    required: 'O campo {{field}} é requerido',
    allowFirstStudioStepMessage:
      'Por hora, esse passo do estúdio está liberado somente para criar a primeira versão do curso',
    courseInformation: 'Informações do Curso',
    fillInTheCourseInformation: 'Preencha as informações do curso',
    courseObjectives: 'Objetivos do Curso',
    shouldBeAbleTo: 'Ao final do curso, os alunos devem conseguir:',
    addAnObjective: 'Adicione um objetivo',
    curriculumMatrix: 'Matriz Curricular',
    block: 'Bloco {{index}}',
    lesson: 'Aula',
    addLesson: 'Adicionar aula',
    addBlock: 'Adicionar bloco',
    dragOrAddLesson: 'Arraste ou adicione uma aula para o bloco',
    addObjective: 'Adicionar objetivo',
    revertActions: 'Reverter ações',
    maxLessonsReached: 'Você atingiu o limite de aulas definido',
    maxBlocksReached: 'Você atingiu o limite de blocos definido',
    lessonsQuantityMismatch:
      'O número de aulas ({{actual}}) não corresponde ao valor definido ({{expected}})',
    directLessonsWithBlocksMismatch:
      'Não é possível ter aulas fora de um bloco, por favor, coloque todas as aulas em algum bloco',
    matrixDetails: 'Matriz Detalhada',
    lessonDetails: 'Detalhes das Aulas',
    noDescription: 'Nenhuma descrição fornecida',
    descriptionPlaceholder: 'Digite a descrição da aula...',
    endLessonTheStudentShould: 'Ao final da aula o aluno será capaz de',
    chapters: 'Capítulos',
    addChapter: 'Adicionar capítulo',
    addSubChapter: 'Adicionar subcapítulo',
    chapterPlaceholder: 'Capítulo da aula',
    subchapterPlaceholder: 'Subcapítulo da aula',
    noChapters: 'Sem capítulos',
    noSubChapters: 'Sem subcapítulos',
    notAllowedLimitedEditing: 'Não permitido na edição limitada',
    notAllowedIfNotAtLeastInLimitedEdition:
      'Não permitido se não estiver pelo menos em edição limitada',
    actions: {
      generateMatrix: {
        locked: 'Crie e salve os objetivos primeiro',
        name: 'Gerar matriz',
      },
      generateObjectives: {
        locked: 'Crie e salve o curso primeiro',
        name: 'Gerar objetivos',
      },
      generateLessonDetail: {
        locked: 'Crie e salve o curso primeiro',
        name: 'Gerar detalhamento',
      },
    },
  },

  courseDuration: {
    [CourseDurationMinuteEnum.ONE_HOUR]: '1h',
    [CourseDurationMinuteEnum.ONE_HOUR_AND_A_HALF]: '1h30',
    [CourseDurationMinuteEnum.TWO_HOURS]: '2h',
    [CourseDurationMinuteEnum.TWO_HOURS_AND_A_HALF]: '2h30',
    [CourseDurationMinuteEnum.THREE_HOURS]: '3h',
  },

  studentDashboard: {
    progress: 'Progresso',
    stats: {
      lessons: 'Aulas',
      activities: 'Tarefas',
      challenges: 'Desafios',
    },
    description: 'de {{total}} {{type}}',
    assisted: 'Assistidas',
    concluded: 'Concluídas',
    completed: 'Completos',
  },

  invalidLessonsModal: {
    title: 'Existem aulas inválidas nessa turma',
    description_one:
      'A aula abaixo teve um problema no momento da criação e deve ser removida para que a turma possa ser editada. Entre em contato com o suporte.',
    description_other:
      'As aulas abaixo tiveram um problema no momento da criação e devem ser removidas para que a turma possa ser editada. Entre em contato com o suporte.',
    confirm: 'Continuar',
    cancel: 'Cancelar',
    lessonWithoutTitleInThePosition: 'Aula sem título na posição {{position}}',
  },

  invalidLesson: {
    title: 'Aula inválida',
    warning:
      'Essa aula não pode ser editada e bloqueia a edição das aulas da turma',
  },

  lessonProgressCollection: {
    title: 'Suas Aulas',
  },

  courseBox: {
    actions: {
      view: 'Visualizar',
      putInBeta: 'Colocar em beta',
      discard: 'Descartar',
      legalInformation: 'Informações legais',
      publish: 'Publicar',
    },
    form: {
      format: 'Formato',
      duration: 'Duração',
      abbreviation: 'Sigla',
      objective: 'Objetivo',
      tool: 'Ferramenta',
      units: {
        label: 'Unidades',
        placeholder: 'Selecione a unidade',
      },
      inBank: 'Adicionar ao Banco de Aulas',
      isPublicInBank: 'Público',
    },
  },
  nextLessonDisplay: {
    title: 'Próxima aula',
    countdown: {
      days: 'dias',
      hrs: 'hrs',
      min: 'min',
      sec: 'seg',
    },
    classroom: 'Sala de Aula',
    at: 'às',
  },
  extraCoursesPortal: {
    extraCourses: 'Cursos Extras',
    myCourses: 'Meus Cursos',
    inProgress: 'Em Andamento',
    completed: 'Concluídos',
    progress: 'Progresso',
    continue: 'Continuar',
    review: 'Revisar',
    statusInProgress: 'Em andamento',
    statusCompleted: 'Concluído',
    noExtraCourses: 'Você ainda não possui cursos extras disponíveis',
    errorLoadingCourses:
      'Erro ao carregar cursos extras. Por favor, tente novamente.',
  },

  courseCard: {
    progressStats: {
      totalLessons_one: '{{count}} Aula',
      totalLessons_other: '{{count}} Aulas',
      remainingLessons: 'Restam {{remaining}} aulas',
      lessonProgress: 'Progresso das aulas',
    },
  },

  courseLineForm: {
    fileTree: {
      courses: 'Cursos',
      newCourse: 'Novo Curso',
    },
    layout: {
      back: 'voltar',
    },
    form: {
      courseName: 'Nome do Curso',
      courseNamePlaceholder: 'Insira o nome do curso',
      courseNameRequired: 'Nome do curso é obrigatório',
      courseCover: 'Capa do Curso',
      courseCoverAlt: 'Capa do curso',
      category: 'Categoria',
      categoryRequired: 'Categoria é obrigatória',
      subcategory: 'Subcategoria',
      subcategoryPlaceholder: 'Selecione a subcategoria',
      subcategoryRequired: 'Subcategoria é obrigatória',
      save: 'Salvar',
    },
    workspace: {
      course: 'Curso',
      newCourse: 'Novo Curso',
      tabs: {
        information: 'Informações',
        modules: 'Módulos',
        evaluations: 'Avaliações',
      },
    },
    evaluationsTab: {
      title: 'Avaliações',
      infoAlertTitle: 'Configure as avaliações',
      infoAlertDescription:
        'As avaliações criadas aqui têm relação com o conteúdo pedagógico desse curso. Você poderá modificar as informações de avaliação posteriormente.',
      addBlock: 'Adicionar Bloco de avaliação',
      addAssessment: 'Adicionar avaliação',
      blockName: 'Nome do Bloco de Avaliação',
      blockNamePlaceholder: 'Insira o nome do bloco',
      blockNameRequired: 'Nome do bloco é obrigatório',
      cancel: 'Cancelar',
      save: 'Salvar',
      createBlockSuccess: 'Bloco de avaliação criado com sucesso',
      deleteBlockSuccess: 'Bloco de avaliação excluído com sucesso',
      updateBlockSuccess: 'Bloco de avaliação atualizado com sucesso',
      reorderAssessmentsSuccess: 'Ordem das avaliações atualizada com sucesso',
      assessmentName: 'Nome da avaliação',
      assessmentNamePlaceholder: 'Insira o nome da avaliação',
      assessmentNameRequired: 'Nome da avaliação é obrigatório',
      assessmentDescription: 'Descrição da avaliação (opcional)',
      assessmentDescriptionPlaceholder: 'Insira a descrição',
      assessmentMaxGrade: 'Peso dessa avaliação',
      assessmentMaxGradePlaceholder: '0 a 100',
      assessmentMaxGradeMax: 'Max: 100%',
      assessmentMaxGradeRequired: 'Peso é obrigatório',
      assessmentMaxGradePercentage: 'O valor é uma porcentagem (entre 0 e 100)',
      pedagogicalContentSection: 'Conteúdos pedagógicos',
      selectUnitsPlaceholder: 'Selecione as unidades',
      noUnitsAvailable: 'Não há unidades',
      pedagogicalContentMissingTitle: 'Conteúdo Pedagógico faltando!',
      pedagogicalContentMissingDescription:
        'Este curso ainda não possui conteúdo pedagógico cadastrado. Você pode configurar a avaliação, mas ela ficará em rascunho até o conteúdo ser cadastrado.',
      goToPedagogicalContent: 'Ir para Cont.Pedag.',
      assessmentItemSection: 'Item de avaliação',
      addAssessmentItem: 'Adicionar',
      addAssessmentItemDisabledTooltip:
        'Para adicionar itens à avaliação, é necessário primeiro salvar a avaliação.',
      newAssessmentItemFormTitle: 'Novo Item de Avaliação',
      assessmentItemFormTitle: 'Editar Item de Avaliação',
      assessmentItemName: 'Nome do item',
      assessmentItemNamePlaceholder: 'Insira o nome do item',
      assessmentItemNameRequired: 'Nome do item é obrigatório',
      assessmentItemDescription: 'Descrição (opcional)',
      assessmentItemDescriptionPlaceholder: 'Insira a descrição',
      assessmentItemType: 'Tipo',
      assessmentItemTypeRequired: 'Tipo é obrigatório',
      assessmentItemTypeManual: 'Regular',
      assessmentItemTypeDigitalActivity: 'Conteúdo Digital',
      assessmentItemTypeTest: 'Test',
      assessmentItemTypeChangeBlockedByCriteria:
        'O tipo Test não pode ter critérios',
      assessmentItemMaxGrade: 'Pontuação Máxima',
      assessmentItemMaxGradePlaceholder: 'Insira uma porcentagem',
      assessmentItemMaxGradeRequired: 'Pontuação é obrigatória',
      assessmentItemMaxGradeRange: 'Pontuação deve estar entre 0 e 100',
      assessmentItemMaxGradeMaxBadge: 'Máx: {{max}}',
      assessmentItemMaxGradeExceedsAssessment:
        'A soma das notas dos itens excede a nota máxima da avaliação que deve ser {{assessmentMaxGrade}}',
      assessmentItemColumnName: 'Nome',
      assessmentItemColumnType: 'Tipo',
      assessmentItemColumnMaxGrade: 'Pontuação Máx.',
      assessmentItemTotal: 'Total',
      criteriaSection: 'Critérios',
      addCriteria: 'Adicionar critério',
      criteriaName: 'Nome do critério',
      criteriaNamePlaceholder: 'Insira o nome do critério',
      criteriaNameRequired: 'Nome do critério é obrigatório',
      criteriaDescription: 'Descrição do critério (opcional)',
      criteriaDescriptionPlaceholder: 'Insira a descrição',
      criteriaMaxGrade: 'Pontuação máxima do critério',
      criteriaMaxGradePlaceholder: 'Insira a pontuação',
      criteriaMaxGradeRequired: 'Pontuação é obrigatória',
      criteriaMaxGradeRange: 'Pontuação deve estar entre 0 e {{max}}',
      criteriaMaxGradeZero: 'A pontuação deve ser 0',
      criteriaMaxGradeMaxBadge: 'Máx: {{max}}',
      criteriaActivityType: 'Tipo de atividade',
      criteriaActivityTypePlaceholder: 'Selecione um tipo',
      criteriaActivityTypeRequired: 'Tipo de atividade é obrigatório',
      criteriaSumMismatch:
        'A soma das notas dos critérios não pode passar de {{expected}}, mas está dando {{actual}}',
      criteriaSaveFailed_one: 'O critério de ordem {{orders}} falhou ao salvar',
      criteriaSaveFailed_other:
        'Os critérios de ordem {{orders}} falharam ao salvar',
      deleteCriteria: 'Excluir critério',
      createAssessmentItemSuccess: 'Item criado com sucesso',
      updateAssessmentItemSuccess: 'Item atualizado com sucesso',
      deleteAssessmentItemSuccess: 'Item excluído com sucesso',
      deleteAssessmentItem: 'Excluir Item de Avaliação',
      confirmDeleteAssessmentItem: 'Tem certeza que deseja excluir este item?',
      editAssessment: 'Editar avaliação',
      createAssessmentSuccess: 'Avaliação criada com sucesso',
      updateAssessmentSuccess: 'Avaliação atualizada com sucesso',
      deleteAssessmentSuccess: 'Avaliação excluída com sucesso',
      applyTemplateModalTitle: 'Conteúdos pedagógicos',
      applyTemplateModalDescription:
        'Selecione um módulo e adicione os cursos onde este bloco será aplicado.',
      selectModuleLabel: 'Módulo',
      selectModulePlaceholder: 'Selecione um módulo',
      appliedCoursePathsLabel: 'Cursos vinculados',
      addCoursePath: 'Adicionar',
      removeCoursePath: 'Remover',
      applyTemplateSyncSuccess: 'Aplicações atualizadas com sucesso',
      emptyCoursePathsForModule: 'Nenhum curso disponível neste módulo',
      clearSelectedUnits: 'Limpar unidades selecionadas',
      coursePath: {
        templateSelectLabel: 'Template de Avaliação',
        templateSelectPlaceholder: 'Sem avaliação',
        replaceConfirmTitle: 'Substituir avaliação',
        replaceConfirmDescription:
          'Já existe uma avaliação aplicada a este curso. Deseja substituí-la pelo novo template?',
        applyTemplateSuccess: 'Template aplicado com sucesso',
        noEvaluationsCreated:
          'Não há avaliações criadas no curso {{courseLineName}}',
        editCourseLine: 'Editar curso',
      },
    },
    courseWorkspace: {
      title: 'Conteúdo Pedagógico',
      subtitle: 'Workspace do Curso #{{courseId}}',
      newCourse: 'Novo Conteúdo Pedagógico',
      draft: 'Rascunho',
      published: 'Publicado',
      publish: 'Publicar',
      publishSuccess: 'Conteúdo pedagógico publicado para beta ',
      tabs: {
        information: 'Informações',
        units: 'Unidades',
        evaluations: 'Avaliações',
        exams: 'Provas',
        lessonPlans: 'Plano de aulas',
      },
      unitsTab: {
        title: 'Unidades',
        addUnit: 'Adicionar Unidade',
        addLesson: 'Adicionar Aula',
        emptyState: 'Nenhuma unidade cadastrada',
        reorderSuccess: 'Ordem atualizada com sucesso',
        createSuccess: 'Unidade criada com sucesso',
        deleteSuccess: 'Unidade removida com sucesso',
        unitName: 'Nome da Unidade',
        unitNamePlaceholder: 'Insira o nome da unidade',
        unitNameRequired: 'Nome da unidade é obrigatório',
        dndPlaceholder: 'Crie ou arraste lições para esta unidade',
        cancel: 'Cancelar',
        save: 'Salvar',
        addQuestions: 'Questões',
      },
      validation: {
        noPublishedLessons: {
          title: 'Sem aulas publicadas',
          description:
            'Por favor, crie e publique aulas para continuar com a publicação do curso',
          close: 'Fechar',
        },
        emptyActivities: {
          title: 'Atividades sem conteúdo',
          description:
            'As seguintes atividades não possuem conteúdo e precisam ser preenchidas antes da publicação:',
          item: 'Atividade {{name}} — Aulas: {{lessons}}',
          close: 'Fechar',
        },
      },
    },
    moduleWorkspace: {
      title: 'Módulo',
      subtitle: 'Workspace do Módulo #{{moduleId}}',
    },
    tree: {
      newModule: 'Novo Módulo',
      newPedContent: 'Novo Cont. Pedag.',
      newUnit: 'Nova Unidade',
      newLesson: 'Nova Lição',
    },
    pedContentTab: {
      title: 'Conteúdo Pedagógico',
      addPedContent: 'Adicionar Conteúdo Pedagógico',
      emptyState: 'Nenhum conteúdo pedagógico cadastrado',
      deleteSuccess: 'Conteúdo pedagógico excluído com sucesso',
      createSuccess: 'Conteúdo pedagógico criado com sucesso',
    },
    courseForm: {
      form: {
        name: 'Nome do Conteúdo Pedagógico',
        namePlaceholder: 'Insira o nome do conteúdo pedagógico',
        nameRequired: 'Nome é obrigatório',
        abbreviation: 'Abreviação',
        abbreviationPlaceholder: 'Insira a abreviação',
        abbreviationRequired: 'Abreviação é obrigatória',
        format: 'Formato',
        formatRequired: 'Selecione pelo menos um formato',
        businessModel: 'Modelo de negócio',
        businessModelRequired: 'Selecione pelo menos um modelo de negócio',
        save: 'Salvar',
        cancel: 'Cancelar',
      },
      alerts: {
        createSuccess: 'Conteúdo pedagógico criado com sucesso',
        updateSuccess: 'Conteúdo pedagógico atualizado com sucesso',
      },
    },
    modulesTab: {
      title: 'Módulos',
      addModule: 'Adicionar Módulo',
      saveOrder: 'Salvar Ordem',
      addPedContent: 'Conteúdo Pedagógico',
      emptyState: 'Nenhum módulo cadastrado',
      deleteConfirm: 'Deseja excluir o módulo "{{name}}"?',
      deleteSuccess: 'Módulo excluído com sucesso',
      createSuccess: 'Módulo criado com sucesso',
      reorderSuccess: 'Ordem dos módulos atualizada com sucesso',
    },
    moduleForm: {
      form: {
        moduleName: 'Nome do Módulo',
        moduleNamePlaceholder: 'Insira o nome do módulo',
        moduleNameRequired: 'Nome do módulo é obrigatório',
        moduleCover: 'Capa do Módulo',
        save: 'Salvar',
        cancel: 'Cancelar',
      },
      workspace: {
        title: 'Módulo',
        newModule: 'Novo Módulo',
        tabs: {
          information: 'Informações',
          contents: 'Conteúdos',
        },
      },
      alerts: {
        createSuccess: 'Módulo criado com sucesso',
        updateSuccess: 'Módulo atualizado com sucesso',
      },
    },
    unitForm: {
      form: {
        unitName: 'Nome da Unidade',
        unitNamePlaceholder: 'Insira o nome da unidade',
        unitNameRequired: 'Nome da unidade é obrigatório',
        save: 'Salvar',
      },
      workspace: {
        title: 'Unidades',
        newUnit: 'Nova Unidade',
        tabs: {
          information: 'Informações',
          lessons: 'Lições',
          flashcards: 'Flashcards',
        },
        flashcardsError: {
          title: 'Erro ao carregar flashcards',
          description:
            'Um erro ocorreu ao carregar os flashcards. Por favor, tente novamente.',
          retryBtn: 'Tentar novamente',
        },
      },
      alerts: {
        createSuccess: 'Unidade criada com sucesso',
        updateSuccess: 'Unidade atualizada com sucesso',
      },
    },
    lessonForm: {
      form: {
        lessonName: 'Nome da Lição',
        lessonNamePlaceholder: 'Insira o nome da lição',
        lessonNameRequired: 'Nome da lição é obrigatório',
        lessonDescription: 'Descrição da Lição',
        lessonDescriptionPlaceholder: 'Insira a descrição da lição',
        lessonObjectives: 'Objetivos da Lição',
        lessonObjectivesPlaceholder: 'Insira os objetivos da lição',
        save: 'Salvar',
        cancel: 'Cancelar',
      },
      workspace: {
        title: 'Lições',
        newLesson: 'Nova Lição',
        tabs: {
          information: 'Informações',
          questions: 'Questões',
          resources: 'Recursos',
          materials: 'Materiais',
          challenge: 'Desafio',
          materialsSubTabs: {
            student: 'Aluno',
            teacher: 'Professor',
            noCoursePathFallback: 'Falha ao carregar o conteúdo pedagógico',
          },
        },
      },
      alerts: {
        createSuccess: 'Lição criada com sucesso',
        updateSuccess: 'Lição atualizada com sucesso',
      },
    },
    lessonsTab: {
      title: 'Lições',
      addLesson: 'Adicionar Lição',
      createSuccess: 'Lição criada com sucesso',
      deleteSuccess: 'Lição excluída com sucesso',
      reorderSuccess: 'Ordem atualizada com sucesso',
      resourcesShortcut: {
        title: 'Recursos',
        openLabel: 'Abrir Recursos da unidade',
      },
    },
    examsTab: {
      createExam: 'Criar Prova',
      createSuccess: 'Prova criada com sucesso',
      updateSuccess: 'Prova atualizada com sucesso',
      deleteSuccess: 'Prova excluída com sucesso',
      reorderSuccess: 'Ordem das provas atualizada com sucesso',
    },
    examWorkspace: {
      title: 'Provas',
      newExam: 'Nova prova',
      tabs: {
        information: 'Informações',
        questions: 'Questões da prova',
        settings: 'Configurações',
      },
      settings: {
        defaultTimeLabel: 'Tempo máximo da prova',
        defaultTimePlaceholder: '-- min',
        defaultTimeRequired: 'Tempo máximo é obrigatório',
        defaultTimeMin: 'Tempo máximo deve ser maior que zero',
        suggestions: 'Sugestões',
        minutes_one: '{{count}} min',
        minutes_other: '{{count}} min',
        save: 'Salvar',
        areasTitle: 'Ordem dos blocos da prova',
        areasInfo:
          'Arraste e ordene os blocos abaixo para definir a ordem que o aluno irá realizar as atividades da prova.',
        noAreasFallback:
          'Não existem áreas criadas para essa categoria de curso',
        showLabel: 'Exibir?',
        attachSuccess: 'Bloco adicionado à prova',
        detachSuccess: 'Bloco removido da prova',
        reorderSuccess: 'Ordem dos blocos atualizada',
        detachConfirmTitle: 'Remover área da prova?',
        detachConfirmDescription_one:
          'Esta área tem 1 atividade vinculada que será removida. Deseja continuar?',
        detachConfirmDescription_other:
          'Esta área tem {{count}} atividades vinculadas que serão removidas. Deseja continuar?',
        detachConfirmLabel: 'Remover',
      },
      questions: {
        dndPlaceholder:
          'Arraste e solte as questões aqui para adicioná-las à prova',
        createBlock: 'Criar Bloco de Questões',
        createQuestion: 'Criar questão',
        blockName: 'Nome do Bloco de Questões',
        blockNamePlaceholder: 'Ex: Bloco 1',
        blockNameRequired: 'Nome do bloco é obrigatório',
        area: 'Habilidade',
        areaPlaceholder: 'Selecione a habilidade',
        areaRequired: 'Habilidade é obrigatória',
        hasTime: 'Possui tempo?',
        timeLimitLabel: 'Tempo máximo para este bloco',
        timeLimitPlaceholder: '-- min',
        timeLimitMax: 'Max: {{count}}',
        timeLimitRequired: 'Informe o tempo máximo',
        timeLimitMaxError: 'Tempo máximo permitido é {{count}} minutos',
        randomize: 'Randomizar?',
        randomizeInfo:
          "Ao selecionar a opção 'Randomizar', as questões desse bloco serão sorteadas para cada aluno, garantindo que as provas não sejam repetidas.",
        shuffleEnabled: 'Randomização ativa',
        createSuccess: 'Bloco criado com sucesso',
        updateSuccess: 'Bloco atualizado',
        deleteSuccess: 'Bloco removido',
        noAreasFallback:
          "Configure as áreas da prova em 'Configurações' primeiro",
        goToSettings: 'Ir para Configurações',
        selectFilterToViewBlocks:
          'Selecione um filtro acima pra visualizar os blocos',
        allAreas: 'Todos',
        save: 'Salvar',
        cancel: 'Cancelar',
        pointsCount_one: '{{count}} ponto',
        pointsCount_other: '{{count}} pontos',
        reorderQuestionsSuccess: 'Ordem das questões atualizada com sucesso',
      },
    },
    examForm: {
      form: {
        name: 'Nome da prova',
        namePlaceholder: 'Insira o nome da prova',
        nameRequired: 'Nome da prova é obrigatório',
        description: 'Descrição da prova',
        descriptionPlaceholder: 'Descreva brevemente esta prova',
        type: 'Tipo de prova',
        typeRequired: 'Tipo da prova é obrigatório',
        evaluation: 'Bloco de Avaliação',
        evaluationPlaceholder: 'Selecione o bloco',
        evaluationRequired: 'Bloco de avaliação é obrigatório',
        assessment: 'Avaliação',
        assessmentPlaceholder: 'Selecione a avaliação',
        assessmentRequired: 'Avaliação é obrigatória',
        assessmentItem: 'Item de Avaliação',
        assessmentItemPlaceholder: 'Selecione o item',
        assessmentItemRequired: 'Item de avaliação é obrigatório',
        assessmentItemAlreadyInUse: 'Este item já está em uso por outra prova',
        simulatedNoAssessmentInfo:
          'As provas do tipo simulado não são avaliadas com notas e não estão conectadas ao sistema de avaliação do curso. Elas servem apenas como uma ferramenta para ajudar os alunos a se prepararem melhor.',
        save: 'Salvar',
        cancel: 'Cancelar',
      },
      alerts: {
        createSuccess: 'Prova criada com sucesso',
        updateSuccess: 'Prova atualizada com sucesso',
      },
    },
    resourcesTab: {
      createResource: 'Adicionar recurso',
      deleteResource: 'Excluir recurso',
      viewResource: 'Visualizar recurso',
      downloadResource: 'Baixar recurso',
      createSuccess: 'Recurso criado com sucesso',
      updateSuccess: 'Recurso atualizado com sucesso',
      deleteSuccess: 'Recurso excluído com sucesso',
      reorderSuccess: 'Ordem dos recursos atualizada com sucesso',
      mandatoryBadge: 'Obrigatório',
      onlyTeacherBadge: 'Somente professor',
    },
    lessonPlansTab: {
      addLessonPlan: 'Adicionar Aula',
      addLesson: 'Adicionar Item',
      name: 'Nome da aula',
      namePlaceholder: 'Insira o nome da aula',
      nameRequired: 'Nome é obrigatório',
      cancel: 'Cancelar',
      save: 'Salvar',
      createSuccess: 'Aula criada com sucesso',
      updateSuccess: 'Aula atualizada com sucesso',
      deleteSuccess: 'Aula excluída com sucesso',
      itemName: 'Nome do item',
      itemNamePlaceholder: 'Insira o nome do item',
      contentType: 'Tipo de conteúdo',
      contentTypeRegular: 'Regular',
      contentTypeEvaluation: 'Avaliação',
      time: 'Tempo',
      timePlaceholder: '-- min',
      timeRequired: 'Tempo é obrigatório',
      assessment: 'Avaliação',
      assessmentPlaceholder: 'Selecione a avaliação',
      assessmentRequired: 'Avaliação é obrigatória',
      assessmentItem: 'Item de Avaliação',
      assessmentItemPlaceholder: 'Selecione o item de avaliação',
      assessmentItemRequired: 'Item de avaliação é obrigatório',
      evaluationBadge: 'Avaliação',
      minutes: '{{time}} min',
      totalLessons: 'Total de aulas: {{count}}',
      totalCourseTime: 'Total do curso: {{time}}',
      blockTotalTime: 'Total: {{time}}',
      itemCreateSuccess: 'Item criado com sucesso',
      itemUpdateSuccess: 'Item atualizado com sucesso',
      itemDeleteSuccess: 'Item excluído com sucesso',
      reorderSuccess: 'Ordem atualizada com sucesso',
      itemDndPlaceholder: 'Arraste itens para este bloco',
    },
    resourceForm: {
      form: {
        title: 'Título',
        titlePlaceholder: 'Insira o título do recurso',
        titleRequired: 'Título é obrigatório',
        link: 'Link',
        linkPlaceholder: 'Cole o link do recurso',
        linkRequired: 'Link é obrigatório',
        linkInvalid: 'Link inválido',
        file: 'Arquivo',
        fileRequired: 'Arquivo é obrigatório',
        currentFile: 'Arquivo atual',
        typeLink: 'Link',
        typeFile: 'Mídia',
        isMandatory: 'Obrigatório',
        allowDownload: 'Permitir download',
        onlyTeacher: 'Exclusivo professor',
        save: 'Salvar',
        cancel: 'Cancelar',
        uploadingFile: 'Carregando arquivo, aguarde...',
      },
    },
    questionsTab: {
      addBlock: 'Criar Bloco de Questões',
      addFlashCardBlock: 'Criar Bloco de Flashcards',
      createFlashcardQuestion: 'Criar Flashcard',
      addQuestion: 'Criar questão',
      createBlockSuccess: 'Bloco de questões criado com sucesso',
      deleteBlockSuccess: 'Bloco de questões excluído com sucesso',
      updateBlockSuccess: 'Bloco de questões atualizado com sucesso',
      dndPlaceholder:
        'Arraste e solte as questões aqui para adicioná-las ao bloco',
      blockName: 'Nome do bloco',
      blockNamePlaceholder: 'Insira o nome do bloco',
      blockNameRequired: 'Nome do bloco é obrigatório',
      blockType: 'Tipo do bloco',
      questionBank: 'Banco de questões',
      createQuestionPlaceholder: 'Criar questão',
      active: 'Ativo',
      inactive: 'Inativo',
      cancel: 'Cancelar',
      save: 'Salvar',
      reorderQuestionsSuccess: 'Ordem das questões atualizada com sucesso',
      deleteQuestionSuccess: 'Questão excluída com sucesso',
      createQuestionModal: {
        title: 'Criar atividade',
        educationalParameter: 'Parâmetro Educacional',
        typology: 'Tipologia',
        selectPlaceholder: 'Selecione',
        save: 'Salvar Bloco',
        back: 'voltar',
        questionCounter: 'Questão {{current}} de {{total}}',
        saveSuccess: 'Questões salvas com sucesso',
        incompleteBlockTitle: 'Há questões incompletas neste bloco!',
        incompleteBlockDescription:
          'Para poder salvar o bloco de questões, finalize o preenchimento dos itens:',
        incompleteBlockFix: 'Corrigir',
        saveGradeError: 'Falha ao salvar pontuação da questão',
        newQuestion: 'Nova questão',
        selectQuestionPlaceholder: 'Selecione uma questão ao lado',
        blockLabel: 'Bloco',
        previewPlaceholder: 'Preview será implementado em breve',
        previewTitle: 'Preview da questão',
        questionForm: {
          title: 'Enunciado',
          titleRequired: 'O enunciado é obrigatório',
          titleDescription: 'Adicione a explicação dessa questão',
          context: 'Contexto',
          contextDescription: 'Adicione a explicação dessa questão',
          contextPlaceholder: 'Escreva o contexto da questão aqui...',
          contextRequired: 'O conteúdo é obrigatório',
          formTitle: 'Instruções da questão',
          contentTab: 'Conteúdo',
          feedbackTab: 'Feedback',
          save: 'Salvar',
          cancel: 'Cancelar',
          saveSuccess: 'Questão salva com sucesso',
          updateSuccess: 'Questão atualizada com sucesso',
          editText: 'Editar texto',
          editMedia: 'Editar mídia',
          markCorrect: 'Marcar como correta',
          markIncorrect: 'Marcar como incorreta',
          delete: 'Excluir',
          altTextPlaceholder: 'Texto alternativo (acessibilidade)',
          points: 'Pontuação',
          pointsPlaceholder: '0 a 10',
          pointsRange: 'A pontuação deve estar entre 0 e 10',
          pointsRequired: 'A pontuação é obrigatória',
          pointsSectionTitle: 'Atribua uma nota para essa questão',
        },
        answersCommon: {
          answersTitle: 'Campos de resposta',
          addOption: 'Adicionar opção',
          newOption: 'Nova opção',
          correctIn: 'Correta em:',
          validAtBlanks: 'Correta nas lacunas:',
          optionContentRequired: 'Cada opção precisa ter conteúdo',
          atLeastOneCorrectOption:
            'É necessário marcar ao menos uma opção como correta',
          minOptionsRequired_one: 'É necessário adicionar ao menos uma opção',
          minOptionsRequired_other:
            'É necessário adicionar ao menos {{count}} opções',
        },
        quizForm: {
          titlePlaceholder: 'Pergunta do Quiz',
          optionPlaceholder: 'Texto da alternativa',
          mediaPlaceholder: 'Selecione uma imagem',
        },
        trueOrFalseForm: {
          titlePlaceholder: 'Título da questão de Verdadeiro ou Falso',
          whatIsTheAnswer: 'Qual a resposta?',
          true: 'Verdadeiro',
          false: 'Falso',
        },
        flashcardForm: {
          titlePlaceholder: 'Título da questão de flashcard',
          frontContent: 'Frente',
          backContent: 'Verso (resposta)',
          contextDescription:
            'Adicione a resposta deste flashcard (texto, imagem, áudio, etc.)',
          showFrontOnBack: 'Visível ao virar o card',
          labelTitle: 'Nome do flashcard',
          titleRequired: 'O nome do flashcard é obrigatório',
          frontContentRequired: 'O conteúdo da frente (pergunta) é obrigatório',
          backContentRequired: 'O conteúdo do verso (resposta) é obrigatório',
          frontContentPlaceholder: 'Escreva a pergunta do flashcard aqui...',
          backContentPlaceholder: 'Escreva a resposta do flashcard aqui...',
        },
        orderingForm: {
          titlePlaceholder: 'Coloque a palavra na ordem correta',
          orientation: 'Orientação',
          horizontal: 'Horizontal',
          vertical: 'Vertical',
          positionLabel: 'Posição {{number}}',
        },
        combinationForm: {
          titlePlaceholder: 'Organize as respostas de acordo com as imagens',
          mainColumnTitle: 'Coluna principal',
          newItem: 'Novo item',
          itemLabel: 'Item {{number}}',
          tabText: 'Texto',
          tabMedia: 'Mídia',
          everyQuestionNeedsAnswer_one:
            'O item {{items}} precisa de pelo menos uma resposta válida',
          everyQuestionNeedsAnswer_other:
            'Os itens {{items}} precisam de pelo menos uma resposta válida',
          notEnoughDistinctAnswers:
            'Não há respostas suficientes para todos os itens. Cada item precisa de uma resposta distinta',
        },
        writingForm: {
          titlePlaceholder: 'Título da tarefa de Escrita',
        },
        multimediaForm: {
          titlePlaceholder: 'Título da tarefa de Texto ou Vídeo',
        },
        fillInTheBlankForm: {
          titlePlaceholder: 'Complete a frase',
          questionLabel: 'Questão',
          questionHint:
            'Para adicionar uma lacuna, digite ___ (3 underlines). Ex: Eu quero ___ pizza.',
          questionPlaceholder:
            'Digite o enunciado usando ___ para marcar as lacunas',
          questionRequired: 'O enunciado da questão é obrigatório',
          atLeastOneBlankRequired:
            'Adicione ao menos uma lacuna (___) no enunciado da questão',
          everyBlankNeedsAnswer_one:
            'A lacuna {{blanks}} precisa de pelo menos uma opção válida',
          everyBlankNeedsAnswer_other:
            'As lacunas {{blanks}} precisam de pelo menos uma opção válida',
          notEnoughDistinctOptions:
            'Não há opções suficientes para preencher todas as lacunas. Cada lacuna precisa de uma opção distinta',
          responseFieldsTitle: 'Campos de resposta',
          blankBadge: 'Lacuna {{number}}',
          blankLabel: 'Lacuna {{number}}',
          previewLabel: 'Pré-visualização do enunciado',
        },
        feedbacks: {
          title: 'Feedback da questão',
          types: {
            POSITIVE: 'Positivo',
            NEUTRAL: 'Neutro',
            NEGATIVE: 'Negativo',
          },
          descriptions: {
            POSITIVE:
              'Esta mensagem é exibida quando o aluno acerta uma questão.',
            NEUTRAL:
              'Esta mensagem é exibida quando o feedback não contempla a resposta.',
            NEGATIVE:
              'Esta mensagem é exibida quando o aluno erra uma questão.',
          },
          titlePlaceholders: {
            POSITIVE: 'Ex: Isso mesmo!',
            NEUTRAL: 'Ex: Quase lá!',
            NEGATIVE: 'Ex: Tente novamente.',
          },
          messagePlaceholders: {
            POSITIVE: 'Ex: Muito bem! Você acertou a questão.',
            NEUTRAL: 'Ex: Sua resposta está parcialmente correta.',
            NEGATIVE:
              'Ex: Não foi dessa vez. Revise o conteúdo e tente de novo.',
          },
        },
      },
    },
    alerts: {
      createSuccess: 'Linha de curso criada com sucesso',
      updateSuccess: 'Linha de curso atualizada com sucesso',
    },
  },
  questionView: {
    errorFetchingActivities: 'Erro ao buscar questão.',
    comingSoonBadge: 'Em breve',
    unsupportedType: 'Preview das respostas em breve',
    studentAnswer: 'Resposta do aluno',
    correct: 'Correta',
    writingCorrection: {
      correct: 'Correta',
      incorrect: 'Incorreta',
      partial: 'Parcial',
      adjustGrade: 'Ajustar nota',
      generalFeedbackPlaceholder: 'Escreva o feedback geral para o aluno...',
      useAICorrection: 'Usar resposta da IA',
      sendAnswer: 'Enviar resposta',
      correctionSuccess: 'Correção salva com sucesso',
      yourCorrection: 'Sua correção',
    },
    aiCorrection: {
      title: 'Correção da IA',
      manualCorrection: 'Correção manual',
      acceptAnswer: 'Aceitar resposta',
      alertTitle: 'Atenção',
      alertMsg:
        'A resposta da IA será enviada automaticamente ao aluno após 3 dias.',
    },
    fillInTheBlank: {
      blankLabel: 'Lacuna {{number}}',
    },
    ordering: {
      sequenceLabel: 'Sequência da resposta',
    },
    combination: {
      slotLabel: 'Resposta para {{cue}}',
    },
    typeAnswer: 'Digite sua resposta aqui',
    noAnswer: 'Sem resposta',
    trueOrFalse: {
      true: 'Verdadeiro',
      false: 'Falso',
      missed: 'Não respondida',
    },
    flashcard: {
      front: 'Frente',
      back: 'Verso',
      flipCard: 'Virar card',
      true: 'Acertei',
      false: 'Errei',
    },
    dnd: {
      usageInstructions:
        'Pressione espaço para pegar o item, use as setas para movê-lo e espaço novamente para soltar. Pressione esc para cancelar.',
      lifted: 'Você pegou {{item}}.',
      movedToPosition: '{{item}} movido para a posição {{position}}.',
      movedToBank: '{{item}} movido para as opções disponíveis.',
      notOverZone: '{{item}} não está sobre uma área válida.',
      dropped: '{{item}} colocado na posição {{position}}.',
      returnedToBank: '{{item}} devolvido às opções disponíveis.',
      cancelled: 'Movimento cancelado. {{item}} voltou ao lugar de origem.',
      bankLabel: 'Opções disponíveis',
      slotLabel: 'Área de resposta',
      dropZoneRoleDescription: 'zona de soltar',
      itemFallback: 'o item',
    },
  },
  klasses: {
    title: 'Turmas',
    summary: 'Resumo',
    back: 'voltar',
    breadcrumb: {
      home: 'Home',
      klasses: 'Turmas',
    },
    columns: {
      performance: 'Performance da turma',
      klass: 'Turma',
      unit: 'Unidade',
      startDate: 'Início',
      endDate: 'Término',
      status: 'Status',
      details: 'Detalhes',
    },
    summaryBadges: {
      klasses: 'Turmas: {{value}}',
      students: 'Alunos: {{value}}',
      studentsPerKlass: 'Alunos por Turma (média): {{value}}',
    },
    legend: {
      performance: 'Performance da turma',
      endingSoon: 'Término próximo',
    },
    filters: {
      searchPlaceholder: 'Turma, curso, professor, dia...',
    },
    openDetails: 'Ver detalhes da turma {{klass}}',
    performanceValue: '{{value}}%',
    noDate: 'Sem data',
    empty: 'Nenhuma turma encontrada',
  },
  klassDetail: {
    title: 'Detalhes da turma',
    eyebrow: 'Turmas',
    back: 'voltar',
    breadcrumb: {
      home: 'Home',
      klasses: 'Turmas',
    },
    switchKlass: {
      label: 'Trocar turma',
      placeholder: 'Buscar turma...',
    },
    tags: {
      students: 'Alunos: {{value}}',
    },
    tabs: {
      students: 'Alunos',
      exams: 'Provas',
      activities: 'Atividades',
      lessons: 'Aulas',
      pedagogicalSheet: 'Ficha Pedagógica',
    },
    students: {
      columns: {
        performance: 'Performance do aluno',
        student: 'Aluno',
        email: 'E-mail',
        unit: 'Unidade',
        details: 'Detalhes',
      },
      legend: {
        performance: 'Performance do aluno',
      },
      filters: {
        searchPlaceholder: 'Nome, e-mail...',
      },
      openDetails: 'Ver detalhes do aluno {{student}}',
      noEmail: 'Sem e-mail',
      empty: 'Nenhum aluno encontrado',
    },
    exams: {
      columns: {
        exam: 'Prova',
        student: 'Aluno',
        status: 'Status',
        deliveredAt: 'Entregue em',
        grade: 'Nota',
        actions: 'Ações',
      },
      status: {
        PENDING: 'A Corrigir',
        CORRECTED: 'Corrigida',
      },
      filters: {
        searchPlaceholder: 'Prova, aluno...',
        exam: 'Prova',
        allExams: 'Todas',
        status: 'Status',
        allStatuses: 'Todas',
        periodDelivered: 'Período entregue',
      },
      legend: {
        requiresAttention: 'Requer atenção',
      },
      gradeNotAvailable: 'N/A',
      empty: 'Nenhuma prova encontrada',
    },
  },
  pendencies: {
    dashboardTitle: 'Minhas Pendências',
    title: 'Pendências',
    back: 'voltar',
    allKlasses: 'Todas as turmas',
    tabs: {
      attendance: 'Chamadas',
      exams: 'Provas',
      activities: 'Atividades',
    },
    columns: {
      lesson: 'Aula',
      unit: 'Unidade',
      klass: 'Turma',
      date: 'Data',
      actions: 'Ações',
    },
    correction: {
      delivered: 'Entregue: ',
      corrected: 'corrigidas',
      pending: 'pendentes',
      save: 'Salvar',
      cancel: 'Cancelar',
    },
    unitName: 'Unidade {{order}}',
    outstanding: 'Pendentes',
    viewReport: 'Ver relatório',
    empty: 'Nenhuma pendência encontrada',
    viewAll: 'Ver todas',
    filters: {
      all: 'Todos',
      period: 'Período',
      searchPlaceholder: 'Lição, ordem, tipo...',
    },
    exams: {
      columns: {
        exam: 'Prova',
        klass: 'Turma',
        student: 'Aluno',
        deliveredAt: 'Entregue em',
        actions: 'Ações',
      },
      correct: 'Corrigir',
      filters: {
        exam: 'Prova',
        periodDelivered: 'Periodo entregue',
        searchPlaceholder: 'Prova, aluno, turma...',
      },
    },
    activities: {
      columns: {
        activity: 'Atividade',
        lesson: 'Lição',
        unit: 'Unidade',
        klass: 'Turma',
        student: 'Aluno',
        sentAt: 'Enviada em:',
        actions: 'Ações',
      },
      correct: 'Corrigir',
      filters: {
        student: 'Aluno',
        periodSend: 'Período enviado',
        searchPlaceholder: 'Atividade, aluno...',
      },
    },
  },
  examScheduling: {
    title: 'Agendamento',
    headerLabel: 'Provas',
    back: 'voltar',
    availableExams: {
      title: 'Provas disponíveis',
      subtitle: 'Selecione uma prova para realizar o agendamento.',
    },
    filters: {
      searchPlaceholder: 'Prova, turma...',
      all: 'Todas',
      status: {
        label: 'Status',
        notScheduled: 'Não agendadas',
        scheduled: 'Agendadas',
      },
    },
    card: {
      notScheduled: 'Não agendada',
      scheduled: 'Agendada',
      expectedFor: 'Prevista para: {{date}}',
      scheduledFor: 'Agendada para: {{date}}',
    },
    empty: 'Nenhuma prova encontrada',
    wizardStub: {
      title: 'Data e Horário',
      description: 'Página em construção.',
    },
  },
  breadcrumb: {
    home: 'Home',
    klasses: 'Turmas',
    exams: 'Provas',
    activities: 'Atividades',
    scheduling: 'Agendamento',
  },
  filterBar: {
    search: 'Buscar',
    searchPlaceholder: 'Buscar...',
    moreFilters: 'Mais filtros',
    lessFilters: 'Menos filtros',
    clearAll: 'Limpar todos',
    removeFilter: 'Remover filtro {{label}}',
  },
  searchSelect: {
    placeholder: 'Selecione',
    emptyMessage: 'Nenhum resultado',
  },
};

export default pt_BR;
